import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  Bell,
  BookOpen,
  Box,
  Boxes,
  Briefcase,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Component,
  CreditCard,
  Database,
  ExternalLink,
  FileChartColumn,
  FileStack,
  FileText,
  GalleryHorizontalEnd,
  GitBranch,
  GitCommitHorizontal,
  HeartPulse,
  Home,
  Image,
  Inbox,
  Layers,
  Lightbulb,
  MapPin,
  Maximize2,
  MessageCircle,
  MoreVertical,
  Network,
  Package,
  Pause,
  Play,
  PanelsTopLeft,
  Search,
  Server,
  Share,
  ShieldAlert,
  ShieldCheck,
  SignalHigh,
  SignalLow,
  SignalMedium,
  Sparkles,
  SquareArrowOutUpRight,
  SquareStack,
  Star,
  ShoppingCart,
  Tags,
  Truck,
  UserPlus,
  Users,
  Waypoints,
  Workflow,
  Wrench,
  X
} from "lucide-react"
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { AgentActivityFeed } from "./AgentActivityFeed"
import { csrfToken } from "../lib/rails"
import { replayPreparationState, useReplayVersion } from "../lib/useReplayVersion"
import { scheduleReplayAudioStart } from "../lib/replayAudio"
import { conceptReplayPositionForAudio, conceptReplayCaptionEntriesFor, conceptReplayDurationLabel, conceptReplayEventsFor, conceptReplayFacetEventKeys, conceptReplayFrameAt, conceptReplayFramesFor, conceptReplayCapBeatKeys, conceptReplaySpokenKeysAt, conceptReplayNarratedFramesFor, conceptReplayNarrationMentions, conceptReplayNarrationSubjectsAt, conceptReplaySeries, conceptReplayPlaybackFor, replayTimelineMonthTicks } from "../lib/conceptReplay"

// Start fetching Three.js and the atlas scene with the main application
// rather than waiting for React effects to run after the first commit.
const conceptStarfieldScenePromise = import("./ConceptStarfieldScene")
import { inboxIssueSeverityClassName, isOpenInboxIssue } from "../lib/inboxIssueStatus"
import { orderedInboxIssues } from "./Inbox"
import { STAR_FAMILY_SWATCHES, STAR_LUMINANCE_FLOOR, STAR_LUMINANCE_UNKNOWN, starColorFor, starGlowColorFor, starLuminanceForDaysSince } from "../lib/starPalette"
import { useDismissiblePopover } from "../lib/useDismissiblePopover"
import { ConceptConstellation } from "./ConceptConstellation"
import { DetailShareButton } from "./DetailShareButton"
import { ContextActionDetailPage, diagramsByActionKey, personaRelatedIssues } from "./ContextOverview"
import { LoadingOrb } from "./LoadingOrb"

const CONCEPT_ITEM_ERROR_COPY = "This commit could not be translated."
const CONCEPT_REPLAY_FRESH_DAYS = 7
const CONCEPT_REPLAY_COOLING_DAYS = 28
const DEFAULT_CONCEPT_ATLAS_NODE_TYPE_VISIBILITY = Object.freeze({
  concept: true,
  persona: true,
  action: true
})
const CONCEPT_REPLAY_SPEED_OPTIONS = [
  { value: 0.5, label: "0.5x" },
  { value: 1, label: "1x" },
  { value: 1.5, label: "1.5x" },
  { value: 2, label: "2x" }
]
const CONCEPT_ATLAS_HOVER_CARD_ESTIMATED_HEIGHT = 360
const CONCEPT_ATLAS_HOVER_CARD_ESTIMATED_WIDTH = 340
const CONCEPT_ATLAS_HOVER_CARD_GAP = 46
const CONCEPT_ZOOM_PANEL_FRACTION = 1 / 3
// Seed for the mobile CSS: under (max-width: 900px) .concept-zoom-panel is a
// bottom sheet at top: 44dvh over the 100dvh stage. The live sheet is
// measured once mounted (conceptZoomSheetInsetFraction), so this only frames
// the first flight before that measurement lands.
const CONCEPT_ZOOM_SHEET_FRACTION = 0.56
const CONCEPT_DESKTOP_VIEWPORT_QUERY = "(min-width: 901px)"

// Fraction of the stage the zoom sheet covers, from the sheet's layout
// position (offsetTop is transform-immune, so the slide-up entrance never
// skews it). Null when the sheet or stage cannot be measured yet.
export function conceptZoomSheetInsetFraction(panelOffsetTop, stageHeight) {
  if (!stageHeight || !Number.isFinite(panelOffsetTop)) return null
  return Math.min(1, Math.max(0, 1 - panelOffsetTop / stageHeight))
}

// A measurement only counts when it moves the *effective* inset — the prior
// measurement, or the seed while none has landed. offsetTop rounds to integer
// pixels, so the first measurement sits within ~1px/stageHeight of the seed;
// committing it anyway would refit the camera on every single zoom-open.
export const CONCEPT_ZOOM_SHEET_INSET_EPSILON = 0.005

export function nextMeasuredSheetInset(current, next, seed) {
  const effective = current ?? seed
  if (Number.isFinite(effective) && Math.abs(effective - next) < CONCEPT_ZOOM_SHEET_INSET_EPSILON) {
    return current
  }
  return next
}
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect

function useDesktopViewport() {
  const [desktop, setDesktop] = useState(() => (
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? true
      : window.matchMedia(CONCEPT_DESKTOP_VIEWPORT_QUERY).matches
  ))

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined

    const media = window.matchMedia(CONCEPT_DESKTOP_VIEWPORT_QUERY)
    const onChange = () => setDesktop(media.matches)
    onChange()
    media.addEventListener("change", onChange)
    return () => media.removeEventListener("change", onChange)
  }, [])

  return desktop
}

export function conceptAtlasHoverPlacement({ y = 0, height = 0, cardHeight = CONCEPT_ATLAS_HOVER_CARD_ESTIMATED_HEIGHT, gap = CONCEPT_ATLAS_HOVER_CARD_GAP } = {}) {
  const safeY = Math.max(0, Number(y) || 0)
  const safeHeight = Math.max(0, Number(height) || 0)
  const spaceAbove = Math.max(0, safeY - gap)
  const spaceBelow = Math.max(0, safeHeight - safeY - gap)

  if (spaceBelow >= cardHeight) return "below"
  if (spaceAbove >= cardHeight) return "above"
  return spaceBelow >= spaceAbove ? "below" : "above"
}

// Parks the hover card fully outside the hovered constellation's screen-space
// bounds (the outermost star coordinates): below or above when there is
// vertical room, otherwise to the side with the most space. The returned
// x/y is the anchor point the CSS classes hang the card off of.
export function conceptAtlasHoverCardFrame({ x = 0, y = 0, width = 0, height = 0, bounds = null, cardWidth = CONCEPT_ATLAS_HOVER_CARD_ESTIMATED_WIDTH, cardHeight = CONCEPT_ATLAS_HOVER_CARD_ESTIMATED_HEIGHT, gap = CONCEPT_ATLAS_HOVER_CARD_GAP } = {}) {
  if (!bounds) {
    return { placement: conceptAtlasHoverPlacement({ y, height, cardHeight, gap }), x, y }
  }

  const safeWidth = Math.max(0, Number(width) || 0)
  const safeHeight = Math.max(0, Number(height) || 0)
  const centerX = (bounds.minX + bounds.maxX) / 2
  const centerY = (bounds.minY + bounds.maxY) / 2
  const options = [
    { placement: "below", fits: safeHeight - bounds.maxY - gap >= cardHeight, space: safeHeight - bounds.maxY, x: centerX, y: bounds.maxY },
    { placement: "above", fits: bounds.minY - gap >= cardHeight, space: bounds.minY, x: centerX, y: bounds.minY },
    { placement: "right", fits: safeWidth - bounds.maxX - gap >= cardWidth, space: safeWidth - bounds.maxX, x: bounds.maxX, y: centerY },
    { placement: "left", fits: bounds.minX - gap >= cardWidth, space: bounds.minX, x: bounds.minX, y: centerY }
  ]

  return options.find((option) => option.fits)
    || options.reduce((best, option) => (option.space > best.space ? option : best))
}

// Overview hover surfaces the parent identity only: hovering an unfocused
// facet routes to the parent concept card instead of rendering a facet label.
export function conceptAtlasSceneHover(nextHoverInfo, { focusedConceptKey = null } = {}) {
  if (nextHoverInfo?.root !== false || focusedConceptKey) return nextHoverInfo
  return { ...nextHoverInfo, key: `root:${nextHoverInfo.conceptKey}`, root: true }
}

const CONCEPT_ICON_RULES = [
  { Icon: GitCommitHorizontal, terms: ["change timeline", "commit narrative", "commit narratives", "commit", "commits", "timeline", "history"] },
  { Icon: GitBranch, terms: ["repository", "repositories", "repo", "repos", "source code", "codebase"] },
  { Icon: BookOpen, terms: ["context overview", "context", "overview", "summary", "glossary"] },
  { Icon: Inbox, terms: ["business risk inbox", "inbox issue", "inbox issues", "inbox", "issue", "issues", "triage"] },
  { Icon: Server, terms: ["infrastructure component", "infrastructure", "component", "provider", "providers", "deployment", "deployments", "host", "hosting"] },
  { Icon: Network, terms: ["architecture map", "architecture", "relationship", "relationships", "graph"] },
  { Icon: Layers, terms: ["stack map", "stack", "layer", "layers"] },
  { Icon: PanelsTopLeft, terms: ["workspace", "workspaces"] },
  { Icon: GalleryHorizontalEnd, terms: ["storyboard", "storyboards", "frame", "frames", "visual", "visuals"] },
  { Icon: Component, terms: ["concept", "concepts", "domain object", "object", "objects"] },
  { Icon: Waypoints, terms: ["action", "actions", "journey", "journeys"] },
  { Icon: Workflow, terms: ["workflow diagram", "workflow", "workflows", "flow", "flows"] },
  { Icon: ShieldAlert, terms: ["business risk", "risk", "risks", "incident", "incidents", "blocked"] },
  { Icon: UserPlus, terms: ["invitation", "invitations", "invite", "invites", "join", "joins"] },
  { Icon: Home, terms: ["home", "house", "property", "properties"] },
  { Icon: Briefcase, terms: ["job", "jobs", "work order", "dispatch", "field", "crew", "technician", "technicians"] },
  { Icon: Calendar, terms: ["appointment", "appointments", "schedule", "calendar", "booking", "reservation"] },
  { Icon: CreditCard, terms: ["billing", "invoice", "invoices", "payment", "payments", "subscription", "renewal", "checkout"] },
  { Icon: MessageCircle, terms: ["chat", "message", "messages", "email", "conversation", "comment", "comments"] },
  { Icon: Users, terms: ["customer", "customers", "homeowner", "homeowners", "user", "users", "member", "members", "team", "teams", "person", "people"], generic: true },
  { Icon: MapPin, terms: ["address", "location", "map", "route", "routes", "territory"] },
  { Icon: Truck, terms: ["delivery", "deliveries", "fleet", "vehicle", "vehicles", "truck"] },
  { Icon: Wrench, terms: ["service", "repair", "maintenance", "installation", "technician"] },
  { Icon: FileChartColumn, terms: ["report", "reports", "readout", "readouts"] },
  { Icon: FileText, terms: ["record", "records", "document", "documents", "file", "files", "note", "notes"] },
  { Icon: ShieldCheck, terms: ["repository access", "auth", "security", "permission", "permissions", "role", "roles", "access"] },
  { Icon: BarChart3, terms: ["analytics", "metric", "metrics", "dashboard", "insight", "reporting"] },
  { Icon: Package, terms: ["inventory", "product", "products", "catalog", "package", "packages"] },
  { Icon: ShoppingCart, terms: ["cart", "order", "orders", "purchase", "shop", "store"] },
  { Icon: ClipboardList, terms: ["task", "tasks", "checklist", "plan", "planning", "workflow", "workflows"] },
  { Icon: Database, terms: ["database", "data", "schema", "table", "tables"] },
  { Icon: Bell, terms: ["alert", "alerts", "notification", "notifications", "reminder", "reminders"] },
  { Icon: Search, terms: ["search", "lookup", "filter", "discovery"] },
  { Icon: HeartPulse, terms: ["health", "status", "monitoring", "incident", "incidents"] },
  { Icon: Tags, terms: ["tag", "tags", "category", "categories", "label", "labels"] }
]

const CONCEPT_ICON_FALLBACKS = [
  Lightbulb,
  Sparkles,
  Box,
  Boxes,
  FileStack,
  SquareStack,
  Component,
  BookOpen,
  GitBranch,
  Network,
  Server,
  ClipboardList,
  Tags,
  Search,
  Package,
  Image,
  BarChart3,
  MessageCircle,
  ShieldCheck,
  Database
]

const activeEssentialConceptSelectionStatuses = new Set(["pending", "generating"])

function conceptActive(summary) {
  if (summary?.status === "queued" || summary?.status === "running") return true
  if (activeEssentialConceptSelectionStatuses.has(summary?.essentialConceptSelection?.status)) return true
  if (activeEssentialConceptSelectionStatuses.has(summary?.essentialConceptSelection?.refreshAttempt?.status)) return true

  return conceptTranslationPending(summary) && Boolean(summary?.generationActive)
}

// A refresh can transiently report zero concepts while a regeneration is still
// selecting essentials. Blanking the persistent sky for that window would read
// as the whole atlas resetting, so the last non-empty list holds until the
// server either delivers concepts or settles into a non-active state.
export function conceptItemsAfterRefresh(currentItems = [], nextItems = [], nextSummary = null) {
  if (nextItems.length > 0) return nextItems
  if (currentItems.length > 0 && conceptActive(nextSummary)) return currentItems

  return nextItems
}

function conceptRemainingCount(summary) {
  if (summary?.dueCount != null || summary?.inFlightCount != null) {
    return Number(summary?.dueCount || 0) + Number(summary?.inFlightCount || 0)
  }

  return Number(summary?.pendingCount || 0) + Number(summary?.generatingCount || 0)
}

function conceptTranslationPending(summary) {
  return conceptRemainingCount(summary) > 0
}

function conceptCommitReadingActive(summary) {
  return summary?.status === "queued" || summary?.status === "running" || conceptTranslationPending(summary)
}

export function conceptReadingPaused(summary) {
  return conceptTranslationPending(summary) && !conceptActive(summary)
}

export function ConceptReadingAction({ summary = null }) {
  if (!summary) return null

  const paused = conceptReadingPaused(summary)
  const busy = !paused
  const totalCount = Number(summary.totalCount) || 0
  const processedCount = Math.min(
    totalCount,
    (Number(summary.readyCount) || 0) + (Number(summary.failedCount) || 0)
  )
  const progressVisible = totalCount > 0 && conceptTranslationPending(summary)
  const timestamp = summary.latestGeneratedAtInWords
  const label = paused ? "Reading paused" : conceptCommitReadingActive(summary) ? "Reading commits" : "Reading concepts"

  return (
    <div className="repository-update-action context-refresh-control concept-reading-action">
      <button
        type="button"
        className={`repository-update-button context-refresh-button ${busy ? "is-busy" : ""}`.trim()}
        disabled
      >
        {busy ? <LoadingOrb state="searching" /> : <BookOpen aria-hidden="true" />}
        <span>{label}</span>
      </button>
      {progressVisible ? (
        <span className="repository-update-status context-refresh-timestamp">
          {processedCount}/{totalCount} commits processed
        </span>
      ) : timestamp ? (
        <span className="repository-update-status context-refresh-timestamp">
          Last synced {timestamp}
        </span>
      ) : null}
    </div>
  )
}

const activeVisualStatuses = new Set(["planning", "generating"])
const activeStoryArcStatuses = new Set(["pending", "generating"])
const activeConstellationStatuses = new Set(["pending", "generating"])
const activeJourneyStatuses = new Set(["pending", "generating"])
const activeContextTimelineStatuses = new Set(["pending", "generating"])

function replayTranscriptActive(replayTranscript = null) {
  return generatedRecordActive(replayTranscript, activeStoryArcStatuses) || replayTranscript?.narration?.status === "generating"
}

function visualsActive(concepts = []) {
  return concepts.some((concept) => activeVisualStatuses.has(conceptVisualFor(concept)?.status))
}

function storyArcsActive(concepts = []) {
  return concepts.some((concept) => generatedRecordActive(concept?.storyArc, activeStoryArcStatuses))
}

function constellationsActive(concepts = []) {
  return concepts.some((concept) => generatedRecordActive(concept?.constellation, activeConstellationStatuses))
}

function journeysActive(concepts = []) {
  return concepts.some((concept) => generatedRecordActive(concept?.journey, activeJourneyStatuses))
}

function contextTimelineActive(contextTimeline = null) {
  return generatedRecordActive(contextTimeline, activeContextTimelineStatuses)
}

function contextConstellationsActive(contextConstellations = null) {
  const personas = Array.isArray(contextConstellations?.personas) ? contextConstellations.personas : []
  const actions = Array.isArray(contextConstellations?.actions) ? contextConstellations.actions : []

  return [...personas, ...actions].some((record) => generatedRecordActive(record?.constellation, activeConstellationStatuses))
}

function generatedRecordActive(record, activeStatuses) {
  return activeStatuses.has(record?.status) || activeStatuses.has(record?.refreshAttempt?.status)
}

export function conceptRefreshFailure(summary, concepts = [], contextTimeline = null, contextConstellations = null, replayTranscript = null) {
  const records = [
    { label: "Essential concept selection", record: summary?.essentialConceptSelection },
    { label: "Replay transcript", record: replayTranscript },
    { label: "Context timeline", record: contextTimeline },
    ...concepts.flatMap((concept) => {
      const title = conceptDisplayTitle(concept)
      return [
        { label: `${title} story`, record: concept?.storyArc },
        { label: `${title} constellation`, record: concept?.constellation },
        { label: `${title} journey`, record: concept?.journey }
      ]
    }),
    ...(contextConstellations?.personas || []).map((persona) => ({
      label: `${persona?.name || persona?.key || "Persona"} constellation`,
      record: persona?.constellation
    })),
    ...(contextConstellations?.actions || []).map((action) => ({
      label: `${action?.label || action?.key || "Action"} constellation`,
      record: action?.constellation
    }))
  ]
  const failure = records.find(({ record }) => record?.refreshAttempt?.status === "failed")
  if (!failure) return null

  const error = failure.record.refreshAttempt?.error
  return error ? `${failure.label} refresh failed: ${error}` : `${failure.label} refresh failed.`
}

function conceptProcessingLines(summary) {
  return conceptCommitReadingActive(summary) ? commitProcessingLines(summary) : conceptSelectionLines(summary)
}

function commitProcessingLines(summary) {
  const totalCount = Number(summary?.totalCount) || 0
  const readyCount = Number(summary?.readyCount) || 0
  const failedCount = Number(summary?.failedCount) || 0
  const processedCount = totalCount > 0 ? Math.min(totalCount, readyCount + failedCount) : readyCount + failedCount
  const readingCount = Number(summary?.readingCount) || 0
  const remainingCount = conceptRemainingCount(summary)
  const lines = [
    {
      active: true,
      key: "reading-commits",
      text: "Reading commits",
      tone: "status"
    }
  ]

  if (totalCount > 0) {
    lines.push({
      key: "commits-processed",
      text: `${processedCount}/${totalCount} commits processed.`,
      tone: "reasoning"
    })
  }

  if (readingCount > 0) {
    lines.push({
      key: "commits-reading",
      text: `${readingCount} ${readingCount === 1 ? "commit" : "commits"} currently being translated.`,
      tone: "reasoning"
    })
  }

  if (remainingCount > 0) {
    lines.push({
      key: "commits-remaining",
      text: `${remainingCount} ${remainingCount === 1 ? "commit" : "commits"} waiting for concept translation.`,
      tone: "status"
    })
  }

  if (summary?.latestGeneratedAtInWords) {
    lines.push({
      key: "latest-commit-generated",
      text: `Latest commit translated ${summary.latestGeneratedAtInWords}.`,
      tone: "status"
    })
  }

  return lines.length > 1 ? lines : [
    ...lines,
    {
      key: "commits-waiting",
      text: "Waiting for commit translation progress.",
      tone: "status"
    }
  ]
}

function conceptSelectionLines(summary) {
  const selectionStatus = summary?.essentialConceptSelection?.status
  const readyCount = Number(summary?.readyCount) || 0
  const totalCount = Number(summary?.totalCount) || 0
  const lines = [
    {
      active: true,
      key: "reading-concepts",
      text: "Reading concepts",
      tone: "status"
    }
  ]

  if (selectionStatus) {
    lines.push({
      key: "concept-selection-status",
      text: `Concept selection is ${selectionStatus}.`,
      tone: "reasoning"
    })
  }

  if (readyCount > 0 || totalCount > 0) {
    lines.push({
      key: "concept-source-count",
      text: `${readyCount}/${totalCount || readyCount} translated commits available for concept selection.`,
      tone: "status"
    })
  }

  if (summary?.latestGeneratedAtInWords) {
    lines.push({
      key: "latest-concept-source",
      text: `Latest source translated ${summary.latestGeneratedAtInWords}.`,
      tone: "status"
    })
  }

  return lines.length > 1 ? lines : [
    ...lines,
    {
      key: "concept-selection-active",
      text: "Selecting product concepts from repository history.",
      tone: "reasoning"
    }
  ]
}

export function conceptAsyncActive(summary, concepts = [], contextTimeline = null, contextConstellations = null, replayTranscript = null) {
  return conceptActive(summary) ||
    replayTranscriptActive(replayTranscript) ||
    storyArcsActive(concepts) ||
    constellationsActive(concepts) ||
    journeysActive(concepts) ||
    visualsActive(concepts) ||
    contextTimelineActive(contextTimeline) ||
    contextConstellationsActive(contextConstellations)
}

export function conceptItemsSignature(concepts = []) {
  const items = Array.isArray(concepts) ? concepts : []

  return JSON.stringify(items.map((concept) => {
    const visual = conceptVisualFor(concept)

    return {
      key: conceptItemKey(concept),
      title: concept?.title || null,
      displayTitle: concept?.displayTitle || null,
      summary: concept?.summary || null,
      commitCount: concept?.commitCount || 0,
      rootCommit: conceptRootCommitSignature(concept?.rootCommit || concept?.root_commit),
      commits: Array.isArray(concept?.commits)
        ? concept.commits.map((commit) => [
          commitItemKey(commit),
          commit?.sha,
          commit?.shortSha,
          commit?.status,
          commit?.committedAt,
          commit?.title,
          commit?.commitSubject,
          commit?.additions,
          commit?.deletions
        ])
        : [],
      contributionDays: Array.isArray(concept?.contributionDays)
        ? concept.contributionDays.map((day) => [
          day.date,
          day.count,
          day.codeVolume,
          Array.isArray(day.commits)
            ? day.commits.map((commit) => [commitItemKey(commit), commit?.sha, commit?.committedAt, commit?.status])
            : []
        ])
        : [],
      constellationStatus: concept?.constellation?.status || null,
      constellationGeneratedAt: concept?.constellation?.generatedAt || null,
      constellationFacetKeys: Array.isArray(concept?.constellation?.facets)
        ? concept.constellation.facets.map((facet) => [facet.key, facet.label, facet.parentKey, facet.kind, facet.firstSeenDate, facet.commitShas])
        : [],
      constellationEdges: Array.isArray(concept?.constellation?.conceptEdges)
        ? concept.constellation.conceptEdges.map((edge) => [edge.targetKey, edge.kind, edge.direction, edge.strength, edge.label, edge.commitShas])
        : [],
      constellationRelatedConcepts: Array.isArray(concept?.constellation?.relatedConcepts)
        ? concept.constellation.relatedConcepts.map((edge) => [edge.targetKey, edge.relationSignal, edge.sharedTerms, edge.sharedCommitShas])
        : [],
      nextMoves: Array.isArray(concept?.nextMoves)
        ? concept.nextMoves.map((move) => [
          move?.key,
          move?.title,
          move?.rationale,
          move?.kind,
          move?.anticipatedSize,
          move?.recommendationReason,
          move?.inboxIssue?.key,
          move?.inboxIssue?.detailPath
        ])
        : [],
      storyArcStatus: concept?.storyArc?.status || null,
      storyArcGeneratedAt: concept?.storyArc?.generatedAt || null,
      visualStatus: visual?.status || null,
      visualFrameCount: visual?.frames?.length || 0,
      visualFirstFrameUrl: visual?.image?.url || visual?.frames?.[0]?.url || null
    }
  }))
}

function conceptSummarySignature(summary) {
  return JSON.stringify(summary || null)
}

function pluralize(word, count) {
  return count === 1 ? word : `${word}s`
}

function commitItemKey(item) {
  return item?.id || item?.sha
}

export function appendCommitItems(currentItems = [], nextItems = []) {
  const currentKeys = new Set(currentItems.map(commitItemKey).filter(Boolean))
  const uniqueNextItems = nextItems.filter((item) => {
    const key = commitItemKey(item)
    return key ? !currentKeys.has(key) : true
  })

  return [...currentItems, ...uniqueNextItems]
}

export function mergeCommitItems(currentItems = [], refreshedItems = []) {
  const refreshedKeys = new Set(refreshedItems.map(commitItemKey).filter(Boolean))
  const olderItems = currentItems.filter((item) => {
    const key = commitItemKey(item)
    return key ? !refreshedKeys.has(key) : true
  })

  return [...refreshedItems, ...olderItems]
}

export function conceptItemKey(item) {
  return item?.key || item?.title
}

export function mergeConceptItems(currentItems = [], refreshedItems = []) {
  const refreshedKeys = new Set(refreshedItems.map(conceptItemKey).filter(Boolean))
  const staleItems = currentItems.filter((item) => {
    const key = conceptItemKey(item)
    return key ? !refreshedKeys.has(key) : true
  })

  return [...refreshedItems, ...staleItems]
}

export function liveUsageByStarfieldKey(payload = {}) {
  return (Array.isArray(payload?.nodes) ? payload.nodes : []).reduce((usage, node) => {
    const conceptKey = node?.conceptKey?.toString().trim()
    if (!conceptKey) return usage

    const facetKey = node?.facetKey?.toString().trim()
    const intensity = Math.max(0, Math.min(1, Number(node?.intensity) || 0))
    if (intensity <= 0) return usage

    const key = facetKey ? `satellite:${conceptKey}:${facetKey}` : `root:${conceptKey}`
    usage[key] = {
      intensity,
      errorRate: Math.max(0, Math.min(1, Number(node?.errorRate) || 0)),
      lastSeenAt: node?.lastSeenAt || null
    }
    return usage
  }, {})
}

export function RepositoryConcepts({
  motionPaused = false,
  onSceneReady = null,
  onReplaySessionChange = null,
  onReplayOpen = null,
  onReplayClose = null,
  onShareReplay = null,
  replayPath = null,
  preloadReplay = false,
  replayRequested = false,
  replayAutoplay = false,
  replayActive = false,
  concept,
  contextActionListPath = null,
  contextDiagrams = null,
  contextEmphasisTarget = null,
  contextFrameActive = false,
  contextOverview = null,
  contextPersonaListPath = null,
  contextStoryboards = null,
  emphasizedIssueKey = null,
  issues = [],
  onConceptChange,
  onConceptClose,
  onConceptNavigate,
  onIssueClose = null,
  onOpenContextAction = null,
  onOpenContextPersona = null,
  onSummaryChange,
  sectionActive = true,
  selectedIssueKey = null,
  stackFrameActive = false,
  visualPaths = null
}) {
  const initialConcept = concept || {}
  const incomingItems = initialConcept?.concepts?.items || []
  const incomingSummary = initialConcept?.concepts?.summary || initialConcept?.summary || null
  const replayTranscript = initialConcept?.concepts?.replayTranscript || null
  const contextTimeline = initialConcept?.contextTimeline || null
  const contextConstellations = initialConcept?.contextConstellations || null
  const incomingItemsSignature = conceptItemsSignature(incomingItems)
  const incomingSummarySignature = conceptSummarySignature(incomingSummary)
  const incomingContextConstellationsSignature = contextConstellationsSignature(contextConstellations)
  const [items, setItems] = useState(incomingItems)
  const [summary, setSummary] = useState(incomingSummary)
  const [loading, setLoading] = useState(false)
  const [visualBusy, setVisualBusy] = useState(false)
  const [replayAvailable, setReplayAvailable] = useState(false)
  const [error, setError] = useState(null)
  const [visualError, setVisualError] = useState(null)
  const [liveUsageByKey, setLiveUsageByKey] = useState({})
  const pollRef = useRef(null)
  const hasLoadedRef = useRef(false)

  const conceptPath = initialConcept?.path
  const active = useMemo(
    () => conceptAsyncActive(summary, items, contextTimeline, contextConstellations, replayTranscript),
    [contextConstellations, contextTimeline, items, replayTranscript, summary]
  )
  const paused = conceptReadingPaused(summary)
  const showProcessingPanel = items.length === 0 && conceptActive(summary)
  const showSelectionFailure = !showProcessingPanel &&
    items.length === 0 &&
    summary?.essentialConceptSelection?.status === "failed"
  const showInitialSkeleton = loading && items.length === 0 && !showProcessingPanel && !showSelectionFailure
  const showEmptyState = !loading && !showProcessingPanel && !showSelectionFailure && items.length === 0
  const conceptContentVisible = !showProcessingPanel && !showSelectionFailure && !showInitialSkeleton && !showEmptyState
  const refreshFailure = conceptRefreshFailure(summary, items, contextTimeline, contextConstellations, replayTranscript)
  const canGenerateVisuals = Boolean(visualPaths?.create && conceptContentVisible && items.length > 0)
  const visualControlsVisible = conceptContentVisible && initialConcept?.visualControlsVisible === true
  const conceptViewControlsVisible = conceptContentVisible && items.length > 0
  const selectedConceptKey = initialConcept?.selectedConceptKey || null
  const selectedConceptView = initialConcept?.selectedConceptView || null
  const selectedAtlas = conceptAtlasSelectionFor(initialConcept?.selectedAtlas)
  // Persona/action sheets reuse the concept zoom presentation; the sky focuses
  // the matching atlas star via its typed item key.
  const zoomFocusKey = selectedConceptKey ||
    (selectedAtlas ? conceptAtlasTypedKey(selectedAtlas.type, conceptAtlasStableKey(selectedAtlas.key)) : null)
  const contributionTimelineDomain = useMemo(() => contributionDomain(summary), [summary])
  const conceptFocus = useMemo(() => conceptFocusContext(items, contributionTimelineDomain), [items, contributionTimelineDomain])
  const atlasItems = useMemo(
    () => conceptAtlasItemsFor(items, contributionTimelineDomain, conceptFocus, contextOverview, contextConstellations, {
      actionStoryboards: contextStoryboards || [],
      conceptListPath: initialConcept?.listPath || null,
      conceptDetailBasePath: initialConcept?.detailBasePath || null
    }),
    [items, contributionTimelineDomain, conceptFocus, contextOverview, incomingContextConstellationsSignature, contextStoryboards, initialConcept?.listPath, initialConcept?.detailBasePath] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const attentionByKey = useMemo(
    () => conceptAtlasAttentionByKey(items, issues, atlasItems),
    [items, issues, atlasItems]
  )
  const highlightedIssue = useMemo(() => {
    if (!emphasizedIssueKey) return null
    const issueList = Array.isArray(issues) ? issues : []
    return issueList.find((issue) => String(issue?.key || issue?.id || "") === String(emphasizedIssueKey)) || null
  }, [issues, emphasizedIssueKey])
  const issueHighlightKeys = useMemo(
    () => conceptAtlasIssueHighlightKeys(items, highlightedIssue, atlasItems),
    [items, highlightedIssue, atlasItems]
  )
  const contextEmphasisKeys = useMemo(
    () => (contextFrameActive ? conceptAtlasEmphasisKeysFor(atlasItems, contextEmphasisTarget) : null),
    [atlasItems, contextEmphasisTarget, contextFrameActive]
  )
  // The sky has a single emphasis channel. While the Context frame is open it
  // belongs to the frame's relationship emphasis; issue selection lives in
  // other sections and cannot be active at the same time.
  const skyHighlightKeys = contextFrameActive ? contextEmphasisKeys : issueHighlightKeys
  const conceptPanelControlsVisible = !replayRequested && !replayActive && ((conceptViewControlsVisible && replayAvailable && replayPath) || visualControlsVisible)
  // The Context and Stack frames share one "a dialog is covering the sky"
  // posture: controls hide, interactivity parks, zoom releases.
  const frameOverlayActive = contextFrameActive || stackFrameActive
  // Zoom is a Concepts-section presentation: a backdropped sky un-focuses
  // (animated flight home) instead of holding a stale zoom inset behind
  // another section's content, and a workspace frame suppresses it the same
  // way while it overlays the sky. Re-entering the section re-focuses.
  const conceptZoomActive = Boolean(zoomFocusKey) && sectionActive && !frameOverlayActive
  const desktopViewport = useDesktopViewport()
  const zoomPanelInset = conceptZoomActive && desktopViewport ? CONCEPT_ZOOM_PANEL_FRACTION : 0
  const zoomSheetInset = conceptZoomActive && !desktopViewport ? CONCEPT_ZOOM_SHEET_FRACTION : 0
  // An open inbox detail sheet reserves the same band the concept zoom does
  // (right third on desktop, bottom sheet on mobile), and the sky frames the
  // issue's highlighted constellations inside the remaining visible area so
  // nothing clips under the sheet or the container edges.
  const issueFramingActive = Boolean(selectedIssueKey && issueHighlightKeys) && sectionActive && !frameOverlayActive && !conceptZoomActive
  const issuePanelInset = issueFramingActive && desktopViewport ? CONCEPT_ZOOM_PANEL_FRACTION : 0
  const issueSheetInset = issueFramingActive && !desktopViewport ? CONCEPT_ZOOM_SHEET_FRACTION : 0
  const issueFrameKeys = useMemo(
    () => (issueFramingActive ? Array.from(issueHighlightKeys) : null),
    [issueFramingActive, issueHighlightKeys]
  )
  const selectedConcept = useMemo(() => (
    selectedConceptKey
      ? items.find((item) => conceptItemKey(item) === selectedConceptKey) || null
      : null
  ), [items, selectedConceptKey])
  const selectedAtlasSubject = useMemo(() => {
    if (!selectedAtlas) return null

    const contextIndex = conceptAtlasContextIndex(contextOverview, contextConstellations, {
      actionStoryboards: contextStoryboards || []
    })
    const contextKey = conceptAtlasStableKey(selectedAtlas.key)

    if (selectedAtlas.type === "persona") {
      const persona = contextIndex.personasByKey.get(contextKey)
      const atlasItem = atlasItems.find((item) => (
        item.atlasType === "persona" && item.contextKey === persona?.contextKey
      )) || null
      return persona ? { type: "persona", persona, atlasItem, routeKey: persona.contextRouteKey } : null
    }

    const contextAction = contextIndex.actionsByKey.get(contextKey)
    if (!contextAction) return null

    return {
      type: "action",
      action: contextAction,
      diagrams: diagramsByActionKey(contextDiagrams || []).get(contextAction.contextRouteKey) || [],
      routeKey: contextAction.contextRouteKey,
      storyboard: contextAtlasActionStoryboard(contextAction, contextIndex.storyboardsByActionKey) || null
    }
  }, [selectedAtlas?.type, selectedAtlas?.key, contextOverview, incomingContextConstellationsSignature, contextStoryboards, contextDiagrams, atlasItems]) // eslint-disable-line react-hooks/exhaustive-deps
  const atlasSheetDetail = selectedAtlasSubject ? (
    selectedAtlasSubject.type === "persona" ? (
      selectedAtlasSubject.atlasItem ? (
        <ConceptDetail
          className="persona-concept-detail"
          concept={selectedAtlasSubject.atlasItem}
          concepts={items}
          domain={contributionTimelineDomain}
          focusContext={conceptFocus}
          fullViewHref={conceptAtlasContextDetailPath(contextPersonaListPath, "personas", selectedAtlasSubject.routeKey)}
          fullViewLabel={`Open full ${selectedAtlasSubject.atlasItem.title} persona page`}
          onConceptNavigate={onConceptNavigate}
          onOpenFullView={(event) => onOpenContextPersona?.(selectedAtlasSubject.routeKey, event)}
          openIssues={orderedInboxIssues(
            personaRelatedIssues(selectedAtlasSubject.persona, issues).filter(isOpenInboxIssue)
          )}
        />
      ) : null
    ) : (
      <ContextActionDetailPage
        action={selectedAtlasSubject.action}
        concept={initialConcept}
        diagrams={selectedAtlasSubject.diagrams}
        embedded
        fullViewHref={conceptAtlasContextDetailPath(contextActionListPath, "actions", selectedAtlasSubject.routeKey)}
        onOpenFullView={(event) => onOpenContextAction?.(selectedAtlasSubject.routeKey, event)}
        storyboard={selectedAtlasSubject.storyboard}
      />
    )
  ) : null

  useEffect(() => {
    setItems((current) => conceptItemsAfterRefresh(current, incomingItems, incomingSummary))
  }, [conceptPath, incomingItemsSignature])

  useEffect(() => {
    setSummary(incomingSummary)
  }, [conceptPath, incomingSummarySignature])

  // Every entry into the Concepts section refreshes — quietly whenever the
  // sky already has concepts to show, and deferred past the section
  // transition: the response can legitimately change the atlas signature
  // (the server picks snapshot vs live per request) and a scene rebuild
  // mid-transition reads as the whole page reloading. A cold empty entry
  // fetches immediately so its skeleton is not delayed.
  useEffect(() => {
    if (!conceptPath || !sectionActive) return undefined

    const quiet = hasLoadedRef.current || items.length > 0
    const delay = quiet ? 400 : 0
    const timer = window.setTimeout(() => {
      hasLoadedRef.current = true
      loadConcepts({ replace: true, quiet })
    }, delay)

    return () => {
      window.clearTimeout(timer)
      stopPolling()
    }
  }, [conceptPath, sectionActive]) // eslint-disable-line react-hooks/exhaustive-deps

  // Selecting a concept (including SPA navigation to its permalink) refreshes
  // with the concept key so the server can sync that concept's journey.
  useEffect(() => {
    if (!conceptPath || !selectedConceptKey || !sectionActive) return

    loadConcepts({ replace: true, quiet: true })
  }, [conceptPath, selectedConceptKey])

  useEffect(() => {
    // A parked sky never polls; each section entry refreshes on arrival.
    const pollInterval = sectionActive ? (active ? 4000 : (paused ? 30000 : null)) : null

    if (!conceptPath || !pollInterval) {
      stopPolling()
      return undefined
    }

    stopPolling()
    pollRef.current = window.setInterval(() => loadConcepts({ replace: true, quiet: true }), pollInterval)

    return () => stopPolling()
  }, [conceptPath, active, paused, sectionActive, selectedConceptKey])

  useEffect(() => {
    const activityPath = initialConcept?.activityPath
    if (!activityPath || !sectionActive || typeof window === "undefined") return undefined

    let cancelled = false
    let activityRequest = null
    async function loadActivity() {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return

      activityRequest?.abort()
      const request = new AbortController()
      activityRequest = request
      try {
        const response = await fetch(activityPath, {
          cache: "no-store",
          headers: { Accept: "application/json" },
          signal: request.signal
        })
        if (!response.ok) {
          if (!cancelled && activityRequest === request) setLiveUsageByKey({})
          return
        }
        const payload = await response.json()
        if (!cancelled && activityRequest === request) setLiveUsageByKey(liveUsageByStarfieldKey(payload))
      } catch (error) {
        if (error?.name !== "AbortError" && !cancelled && activityRequest === request) setLiveUsageByKey({})
      } finally {
        if (activityRequest === request) activityRequest = null
      }
    }

    loadActivity()
    const interval = window.setInterval(loadActivity, 15_000)
    const handleVisibility = () => {
      if (document.visibilityState === "visible") loadActivity()
    }
    document.addEventListener("visibilitychange", handleVisibility)

    return () => {
      cancelled = true
      activityRequest?.abort()
      window.clearInterval(interval)
      document.removeEventListener("visibilitychange", handleVisibility)
    }
  }, [initialConcept?.activityPath, sectionActive])

  function stopPolling() {
    if (!pollRef.current) return

    window.clearInterval(pollRef.current)
    pollRef.current = null
  }

  function loadConcepts({ replace = false, quiet = false } = {}) {
    if (!conceptPath) return Promise.resolve()

    if (!quiet) setLoading(true)
    setError(null)

    const requestPath = selectedConceptKey
      ? `${conceptPath}${conceptPath.includes("?") ? "&" : "?"}concept_key=${encodeURIComponent(selectedConceptKey)}`
      : conceptPath

    return fetch(requestPath, {
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-store"
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load concepts.")
        return response.json()
      })
      .then((payload) => {
        const nextConcepts = conceptsFromPayload(payload)
        const nextItems = nextConcepts?.items || []
        const nextSummary = nextConcepts?.summary || null
        const resolvedItems = replace ? conceptItemsAfterRefresh(items, nextItems, nextSummary) : nextItems
        setSummary(nextSummary)
        if (nextConcepts) {
          // The parent stores this payload and feeds it back as props, so a
          // kept-items decision must round-trip too or the prop-sync effect
          // would wipe what this refresh preserved.
          onConceptChange?.({
            ...initialConcept,
            path: conceptPath,
            concepts: { ...nextConcepts, items: resolvedItems },
            contextTimeline: payload?.contextTimeline || initialConcept?.contextTimeline || null,
            contextConstellations: payload?.contextConstellations || initialConcept?.contextConstellations || null,
            summary: nextSummary
          })
        }
        onSummaryChange?.(nextSummary)
        setItems((current) => replace ? conceptItemsAfterRefresh(current, nextItems, nextSummary) : mergeConceptItems(current, nextItems))
      })
      .catch(() => setError("Could not load concepts."))
      .finally(() => setLoading(false))
  }

  function generateVisual() {
    if (!canGenerateVisuals || visualBusy) return

    setVisualBusy(true)
    setVisualError(null)

    fetch(visualPaths.create, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken()
      },
      body: "{}"
    })
      .then((response) => {
        if (!response.ok) {
          return response.json()
            .catch(() => ({}))
            .then((payload) => {
              throw new Error(payload.error || "Could not generate concept visual.")
            })
        }

        return response.json()
      })
      .then(() => loadConcepts({ replace: true, quiet: true }))
      .catch((generationError) => setVisualError(generationError.message || "Could not generate concept visual."))
      .finally(() => setVisualBusy(false))
  }

  if (!conceptPath) {
    return (
      <section className={`insight-panel concept-panel slide-up slide-up-delayed ${sectionActive ? "" : "is-backdrop"}`.trim()}>
        <div className="concept-empty-state">
          <p className="stage-card-title">Concept is unavailable</p>
          <p>Repository concepts are not available for this repository yet.</p>
        </div>
      </section>
    )
  }

  const atlasVisible = conceptContentVisible && atlasItems.length > 0
  const zoomOverlayActive = conceptZoomActive && atlasVisible

  return (
    <section className={`insight-panel concept-panel slide-up slide-up-delayed ${sectionActive ? "" : "is-backdrop"}`.trim()} id="concept">
      {!zoomOverlayActive && !frameOverlayActive && conceptPanelControlsVisible ? (
        <div className="concept-panel-topbar">
          {visualControlsVisible ? (
            <div className="concept-panel-actions" aria-label="Concept controls">
              <button
                type="button"
                className={`context-storyboard-button concept-storyboard-button ${visualBusy ? "is-busy" : ""}`.trim()}
                disabled={!canGenerateVisuals || visualBusy}
                onClick={generateVisual}
              >
                {visualBusy ? <LoadingOrb state="shaping" theme="light" /> : <Sparkles aria-hidden="true" />}
                <span>{visualBusy ? "Generating" : "Generate concept visual"}</span>
              </button>
            </div>
          ) : null}
          {conceptViewControlsVisible && replayAvailable && replayPath ? (
            <a
              href={replayPath}
              className="concept-panel-replay-trigger"
              aria-label="Play concept history"
              title="Play concept history"
              onClick={onReplayOpen}
            >
              <Play aria-hidden="true" strokeWidth={1.8} />
            </a>
          ) : null}
        </div>
      ) : null}

      {!zoomOverlayActive && (error || visualError) ? (
        <div className="concept-error" role="status">
          <AlertCircle aria-hidden="true" />
          <span>{error || visualError}</span>
        </div>
      ) : null}

      {!zoomOverlayActive && refreshFailure ? <ConceptRefreshWarning message={refreshFailure} /> : null}

      {showProcessingPanel ? (
        <ConceptProcessingPanel summary={summary} />
      ) : showSelectionFailure ? (
        <ConceptSelectionFailure summary={summary} />
      ) : showInitialSkeleton ? (
        <ConceptSkeletonList />
      ) : null}

      {showEmptyState ? (
        <div className="concept-empty-state" aria-live="polite">
          <Image aria-hidden="true" />
          <p>No product concepts yet.</p>
        </div>
      ) : null}

      {atlasVisible ? (
        <ConceptAtlasOverview
          attentionByKey={attentionByKey}
          concepts={atlasItems}
          contextTimeline={contextTimeline}
          domain={contributionTimelineDomain}
          focusContext={conceptFocus}
          focusedConceptKey={conceptZoomActive ? zoomFocusKey : null}
          frameOverlayActive={frameOverlayActive}
          focusInsetBottom={zoomSheetInset + issueSheetInset}
          focusInsetRight={zoomPanelInset + issuePanelInset}
          frameKeys={issueFrameKeys}
          highlightKeys={skyHighlightKeys}
          items={items}
          issueDetailOpen={Boolean(selectedIssueKey)}
          liveUsageByKey={liveUsageByKey}
          motionPaused={motionPaused}
          onConceptNavigate={onConceptNavigate}
          onIssueClose={onIssueClose}
          onReplayAvailabilityChange={setReplayAvailable}
          onReplaySessionChange={onReplaySessionChange}
          onReplayClose={onReplayClose}
          onSceneReady={onSceneReady}
          onShareReplay={onShareReplay}
          onZoomClose={onConceptClose}
          replayRequested={replayRequested}
          replayAutoplay={replayAutoplay}
          preloadReplay={preloadReplay}
          replayTranscript={replayTranscript}
          interactive={sectionActive && !frameOverlayActive}
          zoom={conceptZoomActive ? {
            concept: selectedConcept,
            detail: atlasSheetDetail,
            error: error || visualError,
            kind: selectedConceptKey ? "concept" : selectedAtlas?.type || "concept",
            openIssues: conceptOpenIssues(selectedConcept, issues),
            refreshFailure,
            view: selectedConceptView
          } : null}
        />
      ) : null}
    </section>
  )
}

function conceptZoomPanelLabel(kind, view = null) {
  if (kind === "persona") return "Persona detail"
  if (kind === "action") return "User action detail"
  if (view === "commits") return "Concept commit history"

  return "Concept detail"
}

function conceptZoomCloseLabel(kind) {
  if (kind === "persona") return "Close persona detail"
  if (kind === "action") return "Close user action detail"

  return "Close concept detail"
}

function conceptZoomMissingCopy(kind) {
  if (kind === "persona") return "Persona not found."
  if (kind === "action") return "User action not found."

  return "Concept not found."
}

function ConceptRefreshWarning({ message }) {
  return (
    <div className="concept-error" role="status">
      <AlertCircle aria-hidden="true" />
      <span>{message} Published concepts remain available.</span>
    </div>
  )
}

function ConceptSelectionFailure({ summary = null }) {
  const detail = summary?.essentialConceptSelection?.error || "Concept selection could not be completed."

  return (
    <div className="concept-empty-state concept-failure-state" role="alert">
      <AlertCircle aria-hidden="true" />
      <p className="stage-card-title">Product concepts couldn't be generated</p>
      <p>{detail}</p>
    </div>
  )
}

export function ConceptProcessingPanel({ summary = null }) {
  const readingCommits = conceptCommitReadingActive(summary)
  const lines = conceptProcessingLines(summary)
  const title = readingCommits ? "Reading commits" : "Reading concepts"
  const copy = readingCommits
    ? "Repository history is still being translated before product concepts are selected."
    : "Your product concepts are being compiled from the repository history."

  return (
    <div className="focus-card stage-waiting-panel concept-processing-panel" aria-live="polite">
      <div className="section-label">Generating</div>
      <p className="stage-card-title">{title}</p>
      <LoadingOrb className="stage-waiting-orb" size={64} state={readingCommits ? "searching" : "solving"} />
      <AgentActivityFeed className="issue-thinking-stream" collapsible generating lines={lines} />
      <p className="issue-loading-copy">{copy}</p>
    </div>
  )
}

function ConceptAtlasOverview({ attentionByKey = null, concepts, contextTimeline = null, domain = null, focusContext = {}, focusedConceptKey = null, focusInsetBottom = 0, focusInsetRight = 0, frameKeys = null, frameOverlayActive = false, highlightKeys = null, interactive = true, issueDetailOpen = false, items = [], liveUsageByKey = null, motionPaused = false, nodeTypeVisibility = DEFAULT_CONCEPT_ATLAS_NODE_TYPE_VISIBILITY, onConceptNavigate = null, onIssueClose = null, onReplayAvailabilityChange = null, onReplaySessionChange = null, onReplayClose = null, onSceneReady = null, onShareReplay = null, onZoomClose = null, preloadReplay = false, replayRequested = false, replayAutoplay = false, replayTranscript = null, zoom = null }) {
  const stageRef = useRef(null)
  const sceneRef = useRef(null)
  const cameraStateRef = useRef(null)
  const anchorsRef = useRef(new Map())
  const atlasNavigationRef = useRef({ concepts: [], itemByKey: new Map(), onConceptNavigate: null })
  const focusedConceptKeyRef = useRef(focusedConceptKey)
  const frameKeysRef = useRef(frameKeys)
  const issueDetailOpenRef = useRef(issueDetailOpen)
  const onIssueCloseRef = useRef(onIssueClose)
  const onZoomCloseRef = useRef(onZoomClose)
  const focusInsetRightRef = useRef(focusInsetRight)
  const focusInsetBottomRef = useRef(focusInsetBottom)
  const zoomPanelRef = useRef(null)
  const interactiveRef = useRef(interactive)
  const atlasLabelDragRef = useRef({ conceptKey: null, dragged: false })
  const replayPlayingRef = useRef(false)
  const hoverClearTimeoutRef = useRef(null)
  const [measuredSheetInset, setMeasuredSheetInset] = useState(null)
  const [hoverInfo, setHoverInfo] = useState(null)
  const [idleHighlightKey, setIdleHighlightKey] = useState(null)
  const [replayPosition, setReplayPosition] = useState(0)
  const [replayMode, setReplayMode] = useState("idle")
  const [replayPlaying, setReplayPlaying] = useState(false)
  const [replayStarting, setReplayStarting] = useState(false)
  const [replayNeedsUserStart, setReplayNeedsUserStart] = useState(false)
  const [mobileReplayFeedback, setMobileReplayFeedback] = useState(null)
  const mobileReplayFeedbackTimerRef = useRef(null)
  const replayStartCancelRef = useRef(null)
  const replayStartFallbackTimerRef = useRef(null)
  useEffect(() => () => {
    replayStartCancelRef.current?.()
    if (replayStartFallbackTimerRef.current !== null) window.clearTimeout(replayStartFallbackTimerRef.current)
    if (mobileReplayFeedbackTimerRef.current !== null) window.clearTimeout(mobileReplayFeedbackTimerRef.current)
  }, [])
  const [replayCaptionsVisible, setReplayCaptionsVisible] = useState(false)
  const [replaySessionId, setReplaySessionId] = useState(0)
  const [replayActivationIndex, setReplayActivationIndex] = useState(null)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const [playbackDetail, setPlaybackDetail] = useState("balanced")
  const [focusReplayActive, setFocusReplayActive] = useState(false)
  const pendingPlaybackRef = useRef(null)
  const [sceneState, setSceneState] = useState("loading")
  // Bumped when the WebGL context is lost (browser eviction) so the mount
  // effect rebuilds the scene even though the data signature is unchanged.
  const [sceneEpoch, setSceneEpoch] = useState(0)
  const signature = useMemo(() => JSON.stringify(concepts.map((concept) => ({
    key: concept.key,
    title: concept.title,
    detailPath: concept.detailPath,
    color: concept.color,
    commitCount: concept.commitCount,
    rootCommit: conceptRootCommitSignature(concept.rootCommit || concept.root_commit),
    generatedAt: concept.generatedAt || null,
    replayNarrative: concept.replayNarrative?.milestones?.map((milestone) => [
      milestone.date,
      milestone.caption,
      milestone.storyRole,
      milestone.personaKey,
      milestone.workflowKey,
      milestone.targetKeys,
      milestone.evidenceCommitShas,
      milestone.facetKeys
    ]) || null,
    replayTranscript: concept.replayNarrative?.transcript?.entries?.map((entry) => [
      entry.date,
      entry.caption,
      entry.storyRole,
      entry.sequence,
      entry.conceptKeys,
      entry.personaKeys,
      entry.actionKeys,
      entry.targetKeys,
      entry.evidenceCommitShas,
      entry.facetKeys
    ]) || null,
    graphNode: concept.graphNode ? [concept.graphNode.role, concept.graphNode.clusterKey, concept.graphNode.clusterLabel] : null,
    commits: concept.commits.map((commit) => [
      commit.id,
      commit.sha,
      commit.shortSha,
      commit.status,
      commit.title,
      commit.committedAt,
      commit.additions,
      commit.deletions
    ]),
    contributionDays: concept.contributionDays.map((day) => [
      day.date,
      day.count,
      day.codeVolume,
      day.remainingCount,
      day.commits.map((commit) => [
        commit.id,
        commit.sha,
        commit.shortSha,
        commit.committedAt,
        commit.additions,
        commit.deletions
      ])
    ]),
    facets: concept.facets.map((facet) => [facet.key, facet.label, facet.parentKey, facet.color, facet.firstSeenDate, facet.commitShas]),
    relationships: concept.relationships.map((relationship) => [
      relationship.targetKey,
      relationship.kind,
      relationship.direction,
      relationship.strength,
      relationship.label,
      relationship.rationale,
      relationship.commitShas,
      relationship.evidenceFallback || false
    ])
  }))), [concepts])
  const contextTimelineSignature = useMemo(() => contextTimelineReplaySignature(contextTimeline), [contextTimeline])
  const replayTranscriptSignature = useMemo(() => replayTranscriptReplaySignature(replayTranscript), [replayTranscript])
  const audioRef = useRef(null)
  const narrationCuesRef = useRef([])
  const pendingAudioSeekRef = useRef(null)
  const [narrationError, setNarrationError] = useState(null)
  const [spokenConceptKeys, setSpokenConceptKeys] = useState([])
  const replayConcepts = useMemo(() => concepts.filter(conceptAtlasItemIsConcept), [concepts])
  // Focused replay animates one concept's history in place: while active, the
  // whole replay pipeline sees only the focused concept and no repo-wide
  // timeline or transcript, so frames, captions, and tones scope automatically.
  const focusCandidateItem = useMemo(
    () => (focusedConceptKey ? concepts.find((item) => item.key === focusedConceptKey && conceptAtlasItemIsConcept(item)) || null : null),
    [concepts, focusedConceptKey]
  )
  const focusReplayEventCount = useMemo(
    () => (focusCandidateItem ? conceptReplayFramesFor(conceptReplayEventsFor([focusCandidateItem], { contextTimeline: null })).length : 0),
    [focusCandidateItem]
  )
  const focusReplayAvailable = focusReplayEventCount > 0 && Boolean(replayTranscript?.versionsPath)
  const focusReplaySourceItem = focusReplayActive ? focusCandidateItem : null
  const replaySourceConcepts = useMemo(
    () => (focusReplaySourceItem ? [focusReplaySourceItem] : replayConcepts),
    [focusReplaySourceItem, replayConcepts]
  )
  const replaySourceTimeline = focusReplaySourceItem ? null : contextTimeline
  const preparedReplay = useReplayVersion({
    path: replayTranscript?.versionsPath,
    detail: playbackDetail,
    conceptKey: focusReplaySourceItem?.key || null,
    active: replayCaptionsVisible || preloadReplay,
    sourceSignature: replayTranscriptSignature
  })
  // The landing experience already ships with its published replay. Keep
  // using that recording while a newly requested detail is being prepared;
  // otherwise visitors see an internal queue state instead of the story.
  const publishedReplayTranscript = replayTranscript?.narration?.status === "ready" ? replayTranscript : null
  const replaySourceTranscript = preparedReplay.version || publishedReplayTranscript
  const authoredTranscriptSignature = replayTranscriptReplaySignature(replaySourceTranscript)
  const narration = replaySourceTranscript?.narration || null
  const rawReplayEvents = useMemo(
    () => conceptReplayEventsFor(replaySourceConcepts, { contextTimeline: replaySourceTimeline }),
    [contextTimelineSignature, signature, focusReplaySourceItem] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const replayFrameEvents = useMemo(() => conceptReplayFramesFor(rawReplayEvents), [rawReplayEvents])
  const replaySourceEvents = useMemo(
    () => replaySourceTranscript
      ? conceptReplayNarratedFramesFor(replayFrameEvents, replaySourceConcepts, replaySourceTimeline, replaySourceTranscript)
      : [],
    [replayFrameEvents, authoredTranscriptSignature] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const replaySourceEntries = useMemo(
    () => conceptReplayCaptionEntriesFor(replaySourceEvents, replaySourceConcepts, replaySourceTimeline, replaySourceTranscript),
    [replaySourceEvents, authoredTranscriptSignature] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const replayVersion = useMemo(
    () => conceptReplayPlaybackFor(replaySourceEvents, replaySourceEntries, narration),
    [replaySourceEvents, replaySourceEntries, narration]
  )
  const replayVersions = preparedReplay.options.map((option) => ({
    ...option,
    durationMs: option.value === preparedReplay.selectedDetail && replaySourceTranscript ? replayVersion.durationMs : option.durationMs
  }))
  const replayEvents = replayVersion.frames
  const replayCaptionEntries = replayVersion.entries
  const narrationAudioUrl = narration?.status === "ready" && replayVersion.audioCues.length > 0 ? narration.audioUrl : null
  const replayPreparing = !narrationAudioUrl || (replayStarting && replayMode === "idle")
  const replayPreparation = replayPreparationState(preparedReplay)
  const replayPreparationError = replayPreparation.error || (
    narration?.status === "ready" && !narrationAudioUrl ? "Saved narration couldn't be loaded." : null
  )
  narrationCuesRef.current = replayVersion.audioCues
  const replayNarrationItems = focusReplaySourceItem ? replaySourceConcepts : concepts
  const narrationMentions = useMemo(
    () => conceptReplayNarrationMentions(replayNarrationItems, replayVersion.narrationSegments),
    [replayNarrationItems, replayVersion.narrationSegments]
  )
  const replaySeries = useMemo(() => conceptReplaySeries(replayEvents), [replayEvents])
  const replayLastIndex = Math.max(0, replayEvents.length - 1)
  const replayDisplayIndex = replayMode === "idle" && !replayCaptionsVisible ? replayLastIndex : replayPosition
  const replaySignature = useMemo(() => JSON.stringify(replayFrameEvents.map((event) => [event.id, event.time])), [replayFrameEvents])
  const replayInspecting = replayCaptionsVisible && Boolean(focusedConceptKey) && !focusReplayActive
  const effectiveReplayMode = !narrationAudioUrl || replayInspecting ? "idle" : replayPlaying ? "playing" : replayMode
  const replayFrame = useMemo(
    () => conceptReplayFrameAt(replayEvents, replayDisplayIndex, { mode: effectiveReplayMode }),
    [effectiveReplayMode, replayDisplayIndex, replayEvents]
  )
  const replayActivationFrame = useMemo(() => {
    if (effectiveReplayMode !== "playing") return replayFrame
    if (replayActivationIndex === null) return null

    return conceptReplayFrameAt(replayEvents, replayActivationIndex, { mode: effectiveReplayMode })
  }, [effectiveReplayMode, replayActivationIndex, replayEvents, replayFrame])
  const replayHighlightFrame = useMemo(
    () => ({
      ...conceptReplayCapBeatKeys(conceptReplayFrameWithActivation(replayFrame, replayActivationFrame), narrationMentions, replayVersion.audioCues, replayFrame.index),
      narrationSubjectKeys: conceptReplayNarrationSubjectsAt(narrationMentions, replayVersion.audioCues, replayFrame.index)
    }),
    [replayActivationFrame, replayFrame, narrationMentions, replayVersion.audioCues]
  )
  const narrationColorByKey = useMemo(
    () => new Map(replayNarrationItems.map((item) => [item.key, item.color || STAR_FAMILY_SWATCHES[conceptAtlasItemType(item)] || STAR_FAMILY_SWATCHES.concept])),
    [replayNarrationItems]
  )
  const replayDynamicCounts = replayFrame.mode === "playing" || replayFrame.mode === "scrubbed"
  const replayToneByKey = useMemo(
    () => (replayDynamicCounts ? conceptReplayFocusToneColors(replaySourceConcepts, replayFrame.date, { domain, frame: replayHighlightFrame }) : null),
    [domain, replaySourceConcepts, replayDynamicCounts, replayFrame.date, replayHighlightFrame]
  )
  const replayLabelsActive = replayFrame.mode === "playing" || replayFrame.mode === "scrubbed"
  const replayActiveConceptKeys = useMemo(() => new Set(replayHighlightFrame.activeConceptKeys || []), [replayHighlightFrame])
  const replayVisibleConceptKeys = useMemo(() => new Set(replayFrame.visibleConceptKeys || []), [replayFrame])
  const replayActivePersonaKeys = useMemo(() => new Set(replayHighlightFrame.activePersonaKeys || []), [replayHighlightFrame])
  const replayVisiblePersonaKeys = useMemo(() => new Set(replayFrame.visiblePersonaKeys || []), [replayFrame])
  const replayActiveActionKeys = useMemo(() => new Set(replayHighlightFrame.activeActionKeys || []), [replayHighlightFrame])
  const replayVisibleActionKeys = useMemo(() => new Set(replayFrame.visibleActionKeys || []), [replayFrame])
  const narrationConceptKeys = useMemo(() => {
    if (!replayPlaying || !narrationAudioUrl || narrationError) return new Set()
    const spokenKeys = new Set(spokenConceptKeys)
    return new Set(replayNarrationItems.filter((item) => {
      const nodeType = conceptAtlasItemType(item)
      return spokenKeys.has(item.key) && conceptAtlasNodeTypeVisible(nodeTypeVisibility, nodeType) && conceptAtlasReplayVisible({
        item, nodeType, replayVisibleConceptKeys, replayVisiblePersonaKeys, replayVisibleActionKeys
      })
    }).map((item) => item.key))
  }, [replayPlaying, narrationAudioUrl, narrationError, spokenConceptKeys, replayNarrationItems, nodeTypeVisibility, replayVisibleConceptKeys, replayVisiblePersonaKeys, replayVisibleActionKeys])
  // Every facet that has a birth event on the replay timeline — the scene uses
  // this to tell "facet not born yet" apart from "facet has no date at all".
  const replayFacetEventKeys = useMemo(() => conceptReplayFacetEventKeys(rawReplayEvents), [rawReplayEvents])
  const itemByKey = useMemo(() => {
    const map = new Map(concepts.map((item, index) => [conceptItemKey(item), { item, index }]))
    items.forEach((item, index) => {
      const key = conceptItemKey(item)
      if (key) map.set(key, { item, index })
    })
    return map
  }, [concepts, items])
  // Facet labels need the atlas item — raw concept items carry no facet stars.
  const focusedAtlasItem = focusedConceptKey ? concepts.find((item) => item.key === focusedConceptKey) || null : null
  const hoverPausesMotion = Boolean(hoverInfo?.conceptKey)
  const atlasMotionPaused = motionPaused || hoverPausesMotion
  // The prop is the seed (matches the CSS at the current breakpoint); the
  // mounted sheet's measured coverage wins so the sky centers in whatever
  // band the sheet actually leaves visible.
  const sheetInsetBottom = focusInsetBottom > 0 ? (measuredSheetInset ?? focusInsetBottom) : 0
  atlasNavigationRef.current = { concepts, itemByKey, nodeTypeVisibility, onConceptNavigate }
  focusedConceptKeyRef.current = focusedConceptKey
  frameKeysRef.current = frameKeys
  issueDetailOpenRef.current = issueDetailOpen
  onIssueCloseRef.current = onIssueClose
  onZoomCloseRef.current = onZoomClose
  focusInsetRightRef.current = focusInsetRight
  focusInsetBottomRef.current = sheetInsetBottom
  replayPlayingRef.current = replayPlaying
  interactiveRef.current = interactive

  useBrowserLayoutEffect(() => {
    if (!(focusInsetBottom > 0)) {
      setMeasuredSheetInset(null)
      return undefined
    }

    const measure = () => {
      const next = conceptZoomSheetInsetFraction(zoomPanelRef.current?.offsetTop, stageRef.current?.clientHeight)
      if (next === null) return
      setMeasuredSheetInset((current) => nextMeasuredSheetInset(current, next, focusInsetBottom))
    }
    measure()

    // Mobile URL-bar settling fires resize bursts; one measurement per frame
    // is plenty and keeps the state (and the camera fit behind it) quiet.
    let measureFrame = null
    const scheduleMeasure = () => {
      if (typeof requestAnimationFrame !== "function") {
        measure()
        return
      }
      if (measureFrame !== null) return
      measureFrame = requestAnimationFrame(() => {
        measureFrame = null
        measure()
      })
    }
    window.addEventListener("resize", scheduleMeasure)
    return () => {
      window.removeEventListener("resize", scheduleMeasure)
      if (measureFrame !== null && typeof cancelAnimationFrame === "function") cancelAnimationFrame(measureFrame)
    }
  }, [focusInsetBottom])

  useEffect(() => {
    if (!hoverInfo?.conceptKey) return

    const hoverEntry = itemByKey.get(hoverInfo.conceptKey)
    if (!hoverEntry || !conceptAtlasNodeTypeVisible(nodeTypeVisibility, conceptAtlasItemType(hoverEntry.item))) {
      setHoverInfo(null)
    }
  }, [hoverInfo?.conceptKey, itemByKey, nodeTypeVisibility])

  useEffect(() => {
    // A parked sky can still hold a zoomed concept (leaving the section keeps
    // the key); its Escape listener must not yank navigation back to Concepts.
    if (!focusedConceptKey || !interactive) return undefined

    setHoverInfo(null)

    function handleFocusKeydown(event) {
      if (event.key !== "Escape" || event.defaultPrevented) return
      event.preventDefault()
      onZoomClose?.()
    }

    window.addEventListener("keydown", handleFocusKeydown)

    return () => window.removeEventListener("keydown", handleFocusKeydown)
  }, [focusedConceptKey, interactive, onZoomClose])

  useEffect(() => () => clearHoverClearTimeout(), [])

  useEffect(() => {
    if (pendingPlaybackRef.current) return
    closeReplayPlayer()
  }, [replaySignature]) // eslint-disable-line react-hooks/exhaustive-deps

  useBrowserLayoutEffect(() => {
    if (!replayCaptionsVisible) return
    if (!replaySourceTranscript || !narrationAudioUrl) {
      if (replayPlaying || replayStarting) {
        pendingPlaybackRef.current ||= { resume: true, date: null }
        cancelScheduledPlaybackStart()
        setReplayPlaying(false)
      }
      return
    }
    setNarrationError(null)
    const pending = pendingPlaybackRef.current
    if (!pending) {
      seekNarration(replayPosition)
      return
    }
    pendingPlaybackRef.current = null
    const index = pending.date ? replayEvents.findIndex((event) => event.date >= pending.date) : 0
    const position = index < 0 ? replayLastIndex : index
    setReplayPosition(position)
    setReplayActivationIndex(null)
    setReplayMode(pending.resume ? "idle" : "scrubbed")
    setReplaySessionId((current) => current + 1)
    seekNarration(position)
    if (pending.resume) schedulePlaybackStart()
  }, [replaySourceTranscript?.id, replaySourceTranscript?.generatedAt, Boolean(replaySourceTranscript), narrationAudioUrl, replayCaptionsVisible]) // eslint-disable-line react-hooks/exhaustive-deps

  // Inspecting a constellation pauses the repository story without replacing
  // its recording or position. A focus change still ends a concept-only replay.
  useEffect(() => {
    if (focusReplayActive) closeReplayPlayer()
    else if (focusedConceptKey && replayCaptionsVisible) pauseReplay()
  }, [focusedConceptKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Hand the shell a way to end the session (the sidebar's logo and settings
  // exits) while replay is active, null when idle. Routed through a ref so
  // the handed-out callback keeps a stable identity across renders.
  const closeReplayPlayerRef = useRef(null)
  closeReplayPlayerRef.current = closeReplayPlayer
  // Mobile empty-sky taps toggle playback; desktop taps leave the player.
  const toggleReplayRef = useRef(null)
  toggleReplayRef.current = toggleReplay
  const toggleMobileReplayRef = useRef(null)
  toggleMobileReplayRef.current = toggleMobileReplay
  const replaySessionActive = replayMode !== "idle" || replayCaptionsVisible
  // The scene's tap handler is built once at mount and reads this later.
  const replaySessionActiveRef = useRef(replaySessionActive)
  replaySessionActiveRef.current = replaySessionActive
  useEffect(() => {
    if (!onReplaySessionChange) return undefined
    onReplaySessionChange(replaySessionActive ? () => closeReplayPlayerRef.current?.() : null)
    return () => onReplaySessionChange(null)
  }, [replaySessionActive, onReplaySessionChange])

  // Escape ends the session and returns the sky to its resting view.
  useEffect(() => {
    if (!replaySessionActive || typeof window === "undefined") return undefined

    function handleReplayKeydown(event) {
      if (event.key !== "Escape" || event.defaultPrevented || focusedConceptKeyRef.current) return
      event.preventDefault()
      closeReplayPlayerRef.current?.()
    }

    window.addEventListener("keydown", handleReplayKeydown)
    return () => window.removeEventListener("keydown", handleReplayKeydown)
  }, [replaySessionActive])

  useEffect(() => {
    const audio = audioRef.current
    return () => audio?.pause()
  }, [narrationAudioUrl])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !narrationAudioUrl) return undefined
    audio.playbackRate = playbackSpeed
  }, [narrationAudioUrl, playbackSpeed])

  useEffect(() => {
    const audio = audioRef.current
    setSpokenConceptKeys([])
    if (!audio || !narrationAudioUrl) return undefined
    if (!replayPlaying) {
      if (!replayStarting) audio.pause()
      return undefined
    }
    let animationFrame = null
    function tick() {
      if (!audio.paused && !audio.seeking && audio.readyState >= 2) {
        setReplayPosition(conceptReplayPositionForAudio(narrationCuesRef.current, audio.currentTime))
        // Pure in audio time: a seek simply lands on whatever is lit there.
        const keys = conceptReplaySpokenKeysAt(narrationMentions, audio.currentTime)
        setSpokenConceptKeys((current) => (
          current.length === keys.length && current.every((key, index) => key === keys[index]) ? current : keys
        ))
      }
      animationFrame = window.requestAnimationFrame(tick)
    }
    animationFrame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(animationFrame)
  }, [narrationAudioUrl, narrationMentions, replayPlaying, replayStarting])

  // Parking the sky pauses playback in place — the rAF loop above must not
  // keep scrubbing an invisible scene; returning shows the transport paused.
  useEffect(() => {
    if (interactive || (!replayPlaying && !replayStarting && !pendingPlaybackRef.current)) return

    pendingPlaybackRef.current = null
    cancelScheduledPlaybackStart()
    setReplayPlaying(false)
    setReplayMode("scrubbed")
  }, [interactive, replayPlaying, replayStarting]) // eslint-disable-line react-hooks/exhaustive-deps

  // A workspace frame retires the replay session outright rather than pausing
  // it: a replay-restricted sky hides stars the Context frame's relationship
  // emphasis needs to light up, and the transport would peek out around the
  // dialog either way.
  useEffect(() => {
    if (frameOverlayActive) closeReplayPlayer()
  }, [frameOverlayActive]) // eslint-disable-line react-hooks/exhaustive-deps

  // An open issue sheet frames the issue's constellations in the remaining
  // sky, and a replay-restricted graph hides the very stars that framing
  // needs visible — while the transport and captions would fight the sheet
  // for the same band. Retire the session outright, like a workspace frame.
  useEffect(() => {
    if (issueDetailOpen) closeReplayPlayer()
  }, [issueDetailOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // A hover held at section-switch time would pause the backdrop's drift
  // forever — hover pauses motion, and a backdropped sky receives no
  // pointerleave to clear it.
  useEffect(() => {
    if (interactive) return

    setHoverInfo(null)
  }, [interactive])

  // Beat membership changes the moment the beat does; the scene's own eased
  // emphasis carries the transition, so no gap is needed here.
  useEffect(() => {
    setReplayActivationIndex(Number(replayFrame.index) || 0)
  }, [replayFrame.index, replayFrame.mode, replaySessionId])

  useEffect(() => {
    let cancelled = false
    const stage = stageRef.current
    setSceneState("loading")
    // Cleanup runs before the next effect, so keep the outgoing camera in a
    // separate ref before disposing the scene it belongs to.
    const previousCameraState = cameraStateRef.current

    if (!stage) return undefined

    conceptStarfieldScenePromise
      .then(({ mountConceptStarfieldScene }) => {
        if (cancelled) return
        const scene = mountConceptStarfieldScene(stage, concepts, {
          anchors: anchorsRef.current,
          onHover: handleSceneHover,
          onIdleHighlight: setIdleHighlightKey,
          onContextLost: () => setSceneEpoch((current) => current + 1),
          onSelect: focusConceptFromAtlas,
          onBackgroundTap: () => {
            const target = atlasBackgroundTapTarget({
              focusedKey: focusedConceptKeyRef.current,
              issueDetailOpen: issueDetailOpenRef.current,
              replaySessionActive: replaySessionActiveRef.current,
              now: performance.now()
            })
            if (target === "concept") {
              onZoomCloseRef.current?.()
              return
            }
            // An open inbox detail sheet closes on empty-sky taps, even when
            // the issue has no related constellation keys to frame.
            if (target === "issue") {
              onIssueCloseRef.current?.()
              return
            }
            if (target === "replay") {
              if (window.matchMedia("(max-width: 700px)").matches) {
                toggleMobileReplayRef.current?.()
              }
              else closeReplayPlayerRef.current?.()
            }
          },
          focusedKey: focusedConceptKeyRef.current,
          frameKeys: frameKeysRef.current,
          restrictToHighlightKeys: issueDetailOpenRef.current,
          focusInsetRight: focusInsetRightRef.current,
          focusInsetBottom: focusInsetBottomRef.current,
          initialCamera: previousCameraState
        })
        if (!scene) {
          setSceneState("static")
          return
        }
        sceneRef.current = scene
        scene.update({ attentionByKey, liveUsageByKey, highlightKeys, motionPaused: atlasMotionPaused, nodeTypeVisibility, replayFrame: replayHighlightFrame, replayToneByKey, narrationConceptKeys, replayFacetEventKeys, focusedKey: focusedConceptKeyRef.current, frameKeys: frameKeysRef.current, restrictToHighlightKeys: issueDetailOpenRef.current, focusInsetRight: focusInsetRightRef.current, focusInsetBottom: focusInsetBottomRef.current })
        setSceneState("ready")
        window.requestAnimationFrame(() => {
          if (!cancelled) onSceneReady?.()
        })
      })
      .catch(() => setSceneState("static"))

    return () => {
      cancelled = true
      if (sceneRef.current) cameraStateRef.current = sceneRef.current.cameraState()
      sceneRef.current?.dispose()
      sceneRef.current = null
    }
  }, [signature, sceneEpoch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Story progress (0..1) while a replay session is open; null otherwise.
  // Drives the backdrop sky's fill-in from an empty first beat.
  const replayProgress = replayCaptionsVisible && replayLastIndex > 0
    ? Math.max(0, Math.min(1, replayDisplayIndex / replayLastIndex))
    : null

  useBrowserLayoutEffect(() => {
    sceneRef.current?.update({ attentionByKey, liveUsageByKey, highlightKeys, motionPaused: atlasMotionPaused, nodeTypeVisibility, replayFrame: replayHighlightFrame, replayToneByKey, narrationConceptKeys, replayProgress, replayFacetEventKeys, focusedKey: focusedConceptKey, frameKeys, restrictToHighlightKeys: issueDetailOpen, focusInsetRight, focusInsetBottom: sheetInsetBottom })
  }, [attentionByKey, liveUsageByKey, highlightKeys, atlasMotionPaused, nodeTypeVisibility, replayHighlightFrame, replayToneByKey, narrationConceptKeys, replayProgress, replayFacetEventKeys, focusedConceptKey, frameKeys, issueDetailOpen, focusInsetRight, sheetInsetBottom, sceneState])

  // The scene hides anchors imperatively and its state dies with it; the
  // static flex fallback has no scene to unhide them, so React must.
  useBrowserLayoutEffect(() => {
    if (sceneState !== "static") return

    anchorsRef.current.forEach((element) => {
      element.hidden = false
    })
  }, [sceneState])

  function registerAtlasAnchor(key) {
    return (element) => {
      if (element) anchorsRef.current.set(key, element)
      else anchorsRef.current.delete(key)
    }
  }

  function focusConceptFromAtlas(key, event = null) {
    const navigation = atlasNavigationRef.current
    const concept = navigation.itemByKey.get(key)?.item || navigation.concepts.find((item) => item.key === key)
    if (!concept) return
    if (!conceptAtlasNodeTypeVisible(navigation.nodeTypeVisibility, conceptAtlasItemType(concept))) return

    if (replayPlayingRef.current || replayStartCancelRef.current) pauseReplay()
    navigation.onConceptNavigate?.(concept, event)
  }

  function clearHoverClearTimeout() {
    if (!hoverClearTimeoutRef.current) return

    window.clearTimeout(hoverClearTimeoutRef.current)
    hoverClearTimeoutRef.current = null
  }

  function handleSceneHover(nextHoverInfo) {
    const payload = conceptAtlasSceneHover(nextHoverInfo, { focusedConceptKey: focusedConceptKeyRef.current })
    if (payload?.conceptKey) {
      clearHoverClearTimeout()
      const cardFrame = conceptAtlasHoverCardFrame(payload)
      setHoverInfo({
        ...payload,
        placement: cardFrame.placement,
        cardX: cardFrame.x,
        cardY: cardFrame.y
      })
      return
    }

    scheduleHoverClear()
  }

  function scheduleHoverClear() {
    clearHoverClearTimeout()
    hoverClearTimeoutRef.current = window.setTimeout(() => {
      hoverClearTimeoutRef.current = null
      setHoverInfo(null)
    }, 120)
  }

  function beginAtlasLabelDrag(conceptKey, event) {
    const started = sceneRef.current?.beginNodeDrag?.(conceptKey, event.nativeEvent)
    if (!started) return

    atlasLabelDragRef.current = { conceptKey, dragged: false }
    if (Number.isFinite(event.pointerId)) event.currentTarget?.setPointerCapture?.(event.pointerId)
  }

  function moveAtlasLabelDrag(event) {
    const result = sceneRef.current?.moveNodeDrag?.(event.nativeEvent)
    if (!result?.dragged) return

    atlasLabelDragRef.current = {
      ...atlasLabelDragRef.current,
      dragged: true
    }
    event.preventDefault()
  }

  function releaseAtlasLabelPointerCapture(event) {
    if (!Number.isFinite(event?.pointerId)) return
    const target = event.currentTarget
    if (target?.hasPointerCapture?.(event.pointerId) === false) return
    target?.releasePointerCapture?.(event.pointerId)
  }

  function endAtlasLabelDrag(event) {
    const result = sceneRef.current?.endNodeDrag?.(event.nativeEvent, { selectOnClick: false })
    if (result?.dragged) {
      atlasLabelDragRef.current = {
        ...atlasLabelDragRef.current,
        dragged: true
      }
    }
    releaseAtlasLabelPointerCapture(event)
  }

  function cancelAtlasLabelDrag(event) {
    sceneRef.current?.cancelNodeDrag?.()
    atlasLabelDragRef.current = { conceptKey: null, dragged: false }
    releaseAtlasLabelPointerCapture(event)
  }

  function handleAtlasLabelClick(conceptKey, event) {
    const labelDrag = atlasLabelDragRef.current
    atlasLabelDragRef.current = { conceptKey: null, dragged: false }
    if (labelDrag.conceptKey === conceptKey && labelDrag.dragged) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    focusConceptFromAtlas(conceptKey, event)
  }

  function showLabelHover(conceptKey, event) {
    const stage = stageRef.current
    if (!stage || !conceptKey) return

    const rect = stage.getBoundingClientRect()
    const targetRect = event.currentTarget?.getBoundingClientRect?.()
    const fallbackX = targetRect ? targetRect.left + (targetRect.width / 2) : rect.left + (rect.width / 2)
    const fallbackY = targetRect ? targetRect.top + (targetRect.height / 2) : rect.top + (rect.height / 2)
    const clientX = targetRect ? fallbackX : (Number.isFinite(event.clientX) ? event.clientX : fallbackX)
    const clientY = targetRect ? fallbackY : (Number.isFinite(event.clientY) ? event.clientY : fallbackY)
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left))
    const y = Math.max(0, Math.min(rect.height, clientY - rect.top))
    clearHoverClearTimeout()
    const cardFrame = conceptAtlasHoverCardFrame({ x, y, width: rect.width, height: rect.height })
    setHoverInfo({
      conceptKey,
      key: `root:${conceptKey}`,
      root: true,
      x,
      y,
      width: rect.width,
      height: rect.height,
      placement: cardFrame.placement,
      cardX: cardFrame.x,
      cardY: cardFrame.y
    })
  }

  const replayAvailable = replayFrameEvents.length > 0 && Boolean(replayTranscript?.versionsPath)
  const replayRouteOpenedRef = useRef(false)

  useEffect(() => {
    onReplayAvailabilityChange?.(replayAvailable)
  }, [replayAvailable, onReplayAvailabilityChange])

  useEffect(() => {
    if (!replayRequested) {
      if (replayRouteOpenedRef.current) closeReplayPlayer()
      replayRouteOpenedRef.current = false
      return
    }
    if (replayRouteOpenedRef.current || !replayAvailable || !interactive || focusedConceptKey || issueDetailOpen) return

    replayRouteOpenedRef.current = true
    pendingPlaybackRef.current = { resume: replayAutoplay, date: null }
    setReplayPosition(0)
    setReplayCaptionsVisible(true)
  }, [replayRequested, replayAutoplay, replayAvailable, interactive, focusedConceptKey, issueDetailOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  function startReplay() {
    if (replayFrameEvents.length === 0) return
    if (!replaySourceTranscript || !narrationAudioUrl) {
      pendingPlaybackRef.current = { resume: true, date: null }
      setReplayCaptionsVisible(true)
      return
    }

    const atEnd = audioRef.current?.ended
    if (replayMode === "idle" || atEnd) {
      setReplayPosition(0)
      setReplayMode("idle")
      seekNarration(0)
    }
    setReplayActivationIndex(null)
    setReplayCaptionsVisible(true)
    setReplaySessionId((current) => current + 1)
    schedulePlaybackStart()
  }

  function cancelScheduledPlaybackStart() {
    replayStartCancelRef.current?.()
    replayStartCancelRef.current = null
    if (replayStartFallbackTimerRef.current !== null) window.clearTimeout(replayStartFallbackTimerRef.current)
    replayStartFallbackTimerRef.current = null
    setReplayStarting(false)
  }

  // Keep the opening in preparation until the voice can start. A loaded
  // transcript or an audio URL alone does not make a recording playable.
  function schedulePlaybackStart() {
    cancelScheduledPlaybackStart()
    setNarrationError(null)
    setReplayStarting(true)
    replayStartCancelRef.current = scheduleReplayAudioStart(audioRef.current, {
      onAutoplayBlocked: () => {
        if (replayStartFallbackTimerRef.current !== null) window.clearTimeout(replayStartFallbackTimerRef.current)
        replayStartFallbackTimerRef.current = null
        replayStartCancelRef.current = null
        setReplayStarting(false)
        setReplayNeedsUserStart(true)
      },
      onStarted: () => {
        if (replayStartFallbackTimerRef.current !== null) window.clearTimeout(replayStartFallbackTimerRef.current)
        replayStartFallbackTimerRef.current = null
        replayStartCancelRef.current = null
        setReplayStarting(false)
        setReplayNeedsUserStart(false)
        setReplayMode("playing")
        setReplayPlaying(true)
      },
      onError: handleNarrationError
    })
    // Mobile Safari occasionally leaves a delayed autoplay request pending
    // instead of rejecting it. Keep waiting, but expose a real gesture target
    // so the viewer can start immediately rather than stare at a frozen sky.
    replayStartFallbackTimerRef.current = window.setTimeout(() => {
      replayStartFallbackTimerRef.current = null
      if (replayStartCancelRef.current !== null && !replayPlayingRef.current) setReplayNeedsUserStart(true)
    }, 1200)
  }

  function toggleReplay() {
    if (replayEvents.length === 0) return

    if (replayPlaying || replayStartCancelRef.current !== null) {
      pauseReplay()
      return
    }

    startReplay()
  }

  function flashMobileReplayAction(action) {
    setMobileReplayFeedback({ action, key: performance.now() })
    if (mobileReplayFeedbackTimerRef.current !== null) window.clearTimeout(mobileReplayFeedbackTimerRef.current)
    mobileReplayFeedbackTimerRef.current = window.setTimeout(() => {
      mobileReplayFeedbackTimerRef.current = null
      setMobileReplayFeedback(null)
    }, 620)
  }

  function toggleMobileReplay() {
    const action = replayPlayingRef.current ? "pause" : "play"
    flashMobileReplayAction(action)
    toggleReplay()
  }

  function forceStartMobileReplay() {
    flashMobileReplayAction("play")
    cancelScheduledPlaybackStart()
    startReplay()
  }

  function pauseReplay() {
    if (pendingPlaybackRef.current) pendingPlaybackRef.current.resume = false
    cancelScheduledPlaybackStart()
    audioRef.current?.pause()
    setReplayPlaying(false)
    setReplayMode("scrubbed")
  }

  function toggleFocusReplay() {
    if (focusReplayActive) {
      toggleReplay()
      return
    }
    if (!focusReplayAvailable) return

    pendingPlaybackRef.current = { resume: true, date: null }
    setFocusReplayActive(true)
    setReplayCaptionsVisible(true)
  }

  function scrubReplay(nextIndex) {
    const safeIndex = Math.max(0, Math.min(replayLastIndex, Number(nextIndex) || 0))
    cancelScheduledPlaybackStart()
    setReplayPlaying(false)
    setReplayMode("scrubbed")
    setReplayActivationIndex(Math.round(safeIndex))
    setReplayPosition(safeIndex)
    seekNarration(safeIndex)
  }

  function changePlaybackDetail(value) {
    if (value === playbackDetail) return
    pendingPlaybackRef.current = {
      resume: pendingPlaybackRef.current?.resume ?? (replayPlaying || replayStarting),
      date: replayEvents[Math.round(replayDisplayIndex)]?.date || pendingPlaybackRef.current?.date || null
    }
    cancelScheduledPlaybackStart()
    setReplayPlaying(false)
    setPlaybackDetail(value)
    setReplayActivationIndex(null)
  }

  function seekNarration(index) {
    const time = replayVersion.audioCues[Math.round(index)]
    if (!Number.isFinite(time)) return
    pendingAudioSeekRef.current = time
    if (audioRef.current?.readyState >= 1) {
      if (audioRef.current.currentTime !== time) audioRef.current.currentTime = time
      pendingAudioSeekRef.current = null
    }
  }

  function retryPreparedReplay() {
    pendingPlaybackRef.current ||= {
      resume: replayPlaying || replayStarting,
      date: replayEvents[Math.round(replayDisplayIndex)]?.date || null
    }
    cancelScheduledPlaybackStart()
    setReplayPlaying(false)
    preparedReplay.retry()
  }

  function handleNarrationError() {
    cancelScheduledPlaybackStart()
    setNarrationError("Narration couldn't play. Press play to retry.")
    setReplayPlaying(false)
    setReplayNeedsUserStart(true)
    if (replayPlayingRef.current) setReplayMode("scrubbed")
  }

  function closeReplayPlayer() {
    pendingPlaybackRef.current = null
    cancelScheduledPlaybackStart()
    setFocusReplayActive(false)
    setReplayPlaying(false)
    setReplayNeedsUserStart(false)
    setReplayMode("idle")
    setReplayCaptionsVisible(false)
    setReplayActivationIndex(null)
    setReplayPosition(replayLastIndex)
    if (replayCaptionsVisible) onReplayClose?.()
  }

  function completeReplay() {
    if (!replayPlayingRef.current) return

    closeReplayPlayer()
    // Releasing replay and focus uses the scene's existing flight home.
    if (focusReplayActive) onZoomClose?.()
  }

  return (
    <section
      className={`concept-atlas-overview is-${sceneState} ${hoverPausesMotion ? "is-hover-paused" : ""}`.trim()}
      aria-label="Concept map"
    >
      <div className="concept-atlas-layout">
        <div
          className="concept-atlas-stage"
          ref={stageRef}
          onClick={sceneState === "static" && focusedConceptKey ? (event) => {
            if (event.target.closest(".concept-zoom-panel")) return
            if (event.target.closest("a") || event.target.closest("button")) return
            onZoomClose?.()
          } : undefined}
        >
          <div className="concept-atlas-label-layer">
            {concepts.map((concept) => {
              const nodeType = conceptAtlasItemType(concept)
              const nodeTypeVisible = conceptAtlasNodeTypeVisible(nodeTypeVisibility, nodeType)
              const conceptNode = nodeType === "concept"
              const narrationMentioned = narrationConceptKeys.has(concept.key)
              const replayActive = conceptAtlasReplayActive({
                item: concept,
                nodeType,
                replayActiveConceptKeys,
                replayActivePersonaKeys,
                replayActiveActionKeys
              })
              const labelVisible = conceptAtlasRootLabelVisible({
                hovered: hoverInfo?.conceptKey === concept.key,
                idle: concept.key === idleHighlightKey,
                replayActive: replayActive || narrationMentioned,
                replayLabelsActive
              }) || concept.key === focusedConceptKey || Boolean(highlightKeys?.has(concept.key))
              const commitCount = replayDynamicCounts
                ? replayFrameConceptCommitCount(replayFrame, concept.key)
                : concept.commitCount
              const detailPath = concept.detailPath
              const labelAccessible = nodeTypeVisible && labelVisible
              const labelClassName = [
                "concept-atlas-label",
                "is-root",
                `is-${nodeType}`,
                labelVisible ? "is-visible" : "",
                concept.key === idleHighlightKey ? "is-idle-highlight" : "",
                highlightKeys?.has(concept.key) ? "is-issue-highlight" : "",
                replayLabelsActive && replayActive ? "is-replay-active" : "",
                narrationMentioned ? "is-narration-mentioned" : "",
                nodeTypeVisible ? "" : "is-filtered",
                focusedConceptKey && concept.key !== focusedConceptKey ? "is-focus-dimmed" : ""
              ].filter(Boolean).join(" ")
              const commonLabelProps = {
                className: labelClassName,
                ref: registerAtlasAnchor(`root:${concept.key}`),
                "aria-hidden": !labelAccessible,
                "aria-label": detailPath
                  ? conceptAtlasDetailAriaLabel(concept, nodeType, commitCount)
                  : `${concept.title}, ${conceptNode ? `${commitCount} ${pluralize("commit", commitCount)}` : concept.metaLabel || conceptAtlasNodeTypeLabel(nodeType)}`,
                "data-concept-atlas-root": concept.key,
                "data-concept-atlas-node-type": nodeType,
                "data-facet-count": concept.facets.length,
                tabIndex: labelAccessible ? 0 : -1,
                onFocus: (event) => showLabelHover(concept.key, event),
                onMouseEnter: (event) => showLabelHover(concept.key, event),
                onMouseLeave: scheduleHoverClear,
                onPointerDown: (event) => beginAtlasLabelDrag(concept.key, event),
                onPointerMove: moveAtlasLabelDrag,
                onPointerUp: endAtlasLabelDrag,
                onPointerCancel: cancelAtlasLabelDrag,
                onDragStart: (event) => event.preventDefault(),
                draggable: false
              }

              return detailPath ? (
                <a
                  {...commonLabelProps}
                  href={detailPath}
                  key={concept.key}
                  onClick={(event) => handleAtlasLabelClick(concept.key, event)}
                >
                  <span>{concept.title}</span>
                </a>
              ) : (
                <button
                  {...commonLabelProps}
                  type="button"
                  key={concept.key}
                  onClick={(event) => handleAtlasLabelClick(concept.key, event)}
                >
                  <span>{concept.title}</span>
                </button>
              )
            })}

            {focusedAtlasItem
              // Matches the STARFIELD_MAX_SATELLITES cap in ConceptStarfieldScene —
              // labels for uncapped facets would have no star to anchor to.
              ? (focusedAtlasItem.facets || []).slice(0, 8).map((facet) => (
                <span
                  aria-hidden="true"
                  className={`concept-atlas-label is-satellite is-${conceptAtlasItemType(focusedAtlasItem)} is-visible`}
                  key={facet.key}
                  ref={registerAtlasAnchor(`satellite:${focusedAtlasItem.key}:${facet.key}`)}
                >
                  <span>{facet.label}</span>
                </span>
              ))
              : null}
          </div>

          {hoverInfo?.root === false && hoverInfo?.title && hoverInfo.conceptKey !== focusedConceptKey ? (
            <div
              className={`concept-atlas-hover-facet is-${hoverInfo.placement || "below"}`}
              style={{
                "--concept-atlas-hover-x": `${hoverInfo.x}px`,
                "--concept-atlas-hover-y": `${hoverInfo.y}px`,
                "--concept-atlas-hover-lift": `${hoverInfo.lift ?? 20}px`
              }}
            >
              <span>{hoverInfo.title}</span>
            </div>
          ) : null}

          {zoom ? (
            <aside
              className={`concept-zoom-panel is-${zoom.kind || "concept"}`}
              aria-label={conceptZoomPanelLabel(zoom.kind, zoom.view)}
              key={focusedConceptKey || "zoom"}
              ref={zoomPanelRef}
            >
              {zoom.concept && zoom.view === "commits" ? null : (
                <button
                  type="button"
                  className="detail-panel-action concept-zoom-close"
                  aria-label={conceptZoomCloseLabel(zoom.kind)}
                  onClick={() => onZoomClose?.()}
                >
                  <X aria-hidden="true" />
                </button>
              )}

              <div className="concept-zoom-panel-scroll">
                {zoom.refreshFailure || zoom.error ? (
                  <div className="concept-zoom-notices">
                    {zoom.refreshFailure ? <ConceptRefreshWarning message={zoom.refreshFailure} /> : null}
                    {zoom.error ? (
                      <div className="concept-error" role="status">
                        <AlertCircle aria-hidden="true" />
                        <span>{zoom.error}</span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {zoom.concept ? (
                  <div
                    className={`concept-zoom-view ${zoom.view === "commits" ? "is-concept-history" : "is-concept-detail"}`}
                    key={zoom.view === "commits" ? "concept-history" : "concept-detail"}
                  >
                    {zoom.view === "commits" ? (
                      <ConceptCommitPage
                        concept={zoom.concept}
                        onClose={onZoomClose}
                        onNavigate={onConceptNavigate}
                        playControl={{
                          available: focusReplayAvailable,
                          playing: focusReplayActive && replayPlaying,
                          onToggle: toggleFocusReplay
                        }}
                      />
                    ) : (
                      <ConceptDetail
                        concept={zoom.concept}
                        concepts={items}
                        domain={domain}
                        focusContext={focusContext}
                        onConceptNavigate={onConceptNavigate}
                        openIssues={zoom.openIssues || []}
                      />
                    )}
                  </div>
                ) : zoom.detail ? (
                  <div className={`concept-zoom-view ${zoom.kind === "persona" ? "is-concept-detail is-persona-detail" : "is-context-detail"}`.trim()}>{zoom.detail}</div>
                ) : (
                  <div className="concept-zoom-view is-empty">
                    <div className="concept-empty-state" aria-live="polite">
                      <Image aria-hidden="true" />
                      <p>{conceptZoomMissingCopy(zoom.kind)}</p>
                    </div>
                  </div>
                )}
              </div>
            </aside>
          ) : null}

          {replayEvents.length > 0 && replayCaptionsVisible && !replayInspecting && replayMode !== "idle" && !replayPreparing ? (
            <ConceptAtlasCaptionOverlay
              activeIndex={Math.round(replayDisplayIndex)}
              audioRef={audioRef}
              conceptColorByKey={narrationColorByKey}
              entries={replayCaptionEntries}
              narrationMentions={narrationAudioUrl ? narrationMentions : null}
              narrativeReady={replayCaptionEntries[replayFrame.index]?.ready}
              narrated={Boolean(narrationAudioUrl)}
              narrationSegments={narrationAudioUrl ? replayVersion.narrationSegments : null}
              replaySessionId={replaySessionId}
              spokenConceptKeys={spokenConceptKeys}
              zoomDocked={focusInsetRight > 0}
            />
          ) : null}

          {narrationAudioUrl ? (
            <audio
              key={narrationAudioUrl}
              ref={audioRef}
              src={narrationAudioUrl}
              preload="auto"
              onCanPlay={() => setNarrationError(null)}
              onLoadedMetadata={(event) => {
                if (pendingAudioSeekRef.current === null) return
                if (event.currentTarget.currentTime !== pendingAudioSeekRef.current) {
                  event.currentTarget.currentTime = pendingAudioSeekRef.current
                }
                pendingAudioSeekRef.current = null
              }}
              onError={handleNarrationError}
              onEnded={completeReplay}
            />
          ) : null}

          {onShareReplay && replayCaptionsVisible && !replayInspecting ? (
            <button
              type="button"
              className="concept-atlas-replay-share"
              aria-label="Share player"
              title="Copy player URL"
              onClick={onShareReplay}
            >
              <Share aria-hidden="true" />
            </button>
          ) : null}

          {mobileReplayFeedback ? (
            <span className="concept-atlas-replay-tap-feedback" key={mobileReplayFeedback.key} aria-hidden="true">
              {mobileReplayFeedback.action === "pause" ? <Pause /> : <Play />}
            </span>
          ) : null}

          {replayCaptionsVisible && replayNeedsUserStart && narrationAudioUrl && !replayInspecting ? (
            <button
              type="button"
              className="concept-atlas-replay-mobile-start"
              aria-label="Start playback"
              onClick={forceStartMobileReplay}
            >
              <Play aria-hidden="true" />
            </button>
          ) : null}

          {replayCaptionsVisible && !replayInspecting ? (
            <ConceptAtlasReplay
              events={replayEvents}
              index={replayPreparing ? 0 : replayDisplayIndex}
              playing={replayPlaying || (replayStarting && replayMode !== "idle")}
              series={replaySeries}
              onPlayToggle={toggleMobileReplay}
              onScrub={scrubReplay}
              onSpeedChange={setPlaybackSpeed}
              playbackSpeed={playbackSpeed}
              playbackDetail={preparedReplay.selectedDetail}
              preparing={replayPreparing}
              preparationError={replayPreparationError}
              preparationMessage={replayPreparation.message}
              preparationAction={replayPreparation.action}
              onRetry={retryPreparedReplay}
              versions={replayVersions}
              onDetailChange={changePlaybackDetail}
              narrationStatus={replayPreparing ? null : narrationError}
              onClose={closeReplayPlayer}
              zoomDocked={focusInsetRight > 0}
            />
          ) : null}
        </div>
      </div>
    </section>
  )
}

// Word start times for one caption line. Real timings come from the
// recording's per-word alignment; a line without them spreads its span
// across its words by length, and a line with no span at all reads as
// already spoken.
function captionWordTimings(group, segments) {
  const tokens = String(group?.caption || "").split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return null

  const segment = Array.isArray(segments) ? segments[group.startIndex] : null
  const timed = Array.isArray(segment?.words) ? segment.words : null
  if (timed && timed.length === tokens.length && timed.every((word) => Number.isFinite(Number(word?.start)))) {
    return tokens.map((text, index) => ({ text, start: Number(timed[index].start) }))
  }

  const start = Number(segment?.start)
  const end = Number(segment?.end)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return tokens.map((text) => ({ text, start: -Infinity }))
  }
  const totalLength = tokens.reduce((sum, text) => sum + text.length + 1, 0)
  let consumed = 0
  return tokens.map((text) => {
    const wordStart = start + (consumed / totalLength) * (end - start)
    consumed += text.length + 1
    return { text, start: wordStart }
  })
}

function spokenWordCountAt(words, time) {
  let count = 0
  for (const word of words) {
    if (word.start <= time + 0.03) count += 1
    else break
  }
  return count
}

// A spoken word that names a lit constellation takes on that star's colour
// for as long as the star is lit, then settles back to plain spoken white.
function ConceptSpokenCaption({ words, spokenCount, wordColors = null }) {
  return (
    <span className="concept-atlas-caption-lyric">
      {words.flatMap((word, index) => {
        const color = index < spokenCount ? wordColors?.get(index) : undefined
        return [
          index > 0 ? " " : null,
          <span
            className={`concept-atlas-caption-word ${index < spokenCount ? "is-spoken" : "is-unspoken"} ${color ? "is-concept" : ""}`.trim()}
            key={`${word.text}:${index}`}
            style={color ? { "--concept-color": color } : undefined}
          >
            {word.text}
          </span>
        ]
      })}
    </span>
  )
}

// Caption word index → star colour, for the current line's mentions whose
// constellation is lit right now.
function captionConceptWordColors(group, mentions, spokenKeys, colorByKey) {
  if (!group || !Array.isArray(mentions) || !spokenKeys?.length || !colorByKey) return null
  const spoken = new Set(spokenKeys)
  const colors = new Map()
  mentions.forEach((mention) => {
    if (mention.segmentIndex !== group.startIndex || !spoken.has(mention.conceptKey)) return
    const color = colorByKey.get(mention.conceptKey)
    if (!color) return
    for (let offset = 0; offset < mention.wordCount; offset += 1) colors.set(mention.wordIndex + offset, color)
  })
  return colors.size > 0 ? colors : null
}

function captionDisplayLines(groups, maxCharacters = 32) {
  return groups.flatMap((group) => {
    const words = String(group.caption || "").split(/\s+/).filter(Boolean)
    const lines = []
    let start = 0
    let length = 0

    words.forEach((word, index) => {
      const nextLength = length + (length > 0 ? 1 : 0) + word.length
      if (length > 0 && nextLength > maxCharacters) {
        lines.push({ group, start, end: index, text: words.slice(start, index).join(" ") })
        start = index
        length = word.length
      } else {
        length = nextLength
      }
    })
    if (start < words.length) lines.push({ group, start, end: words.length, text: words.slice(start).join(" ") })
    return lines
  })
}

function ConceptAtlasCaptionOverlay({ activeIndex = 0, audioRef = null, conceptColorByKey = null, entries = [], narrationMentions = null, narrativeReady = true, narrated = false, narrationSegments = null, replaySessionId = 0, spokenConceptKeys = [], zoomDocked = false }) {
  const transcriptRef = useRef(null)
  const [userScrolledBack, setUserScrolledBack] = useState(false)
  const [spokenWordCount, setSpokenWordCount] = useState(0)
  const safeEntries = Array.isArray(entries) ? entries.filter((entry) => entry?.caption) : []
  const safeActiveIndex = Math.max(0, Math.min(safeEntries.length - 1, Math.round(Number(activeIndex) || 0)))
  // Each authored entry or fallback caption renders once for the entire
  // replay. A fallback scorer can revisit an older caption after a newer one;
  // those frames continue the story without appending or retyping the repeat.
  const captionGroups = []
  safeEntries.forEach((entry, index) => {
    if (entry.first === false) return

    captionGroups.push({ key: entry.key, groupKey: entry.groupKey || entry.caption, caption: entry.caption, startIndex: index })
  })
  const visibleGroups = captionGroups.filter((group) => group.startIndex <= safeActiveIndex)
  const pendingClassName = narrativeReady ? "" : " is-pending"
  const currentGroup = visibleGroups.length > 0 ? visibleGroups[visibleGroups.length - 1] : null
  const currentWords = useMemo(
    () => (narrated && currentGroup ? captionWordTimings(currentGroup, narrationSegments) : null),
    [narrated, currentGroup?.key, currentGroup?.startIndex, currentGroup?.caption, narrationSegments] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const currentWordColors = useMemo(
    () => (narrated ? captionConceptWordColors(currentGroup, narrationMentions, spokenConceptKeys, conceptColorByKey) : null),
    [narrated, currentGroup?.startIndex, narrationMentions, spokenConceptKeys, conceptColorByKey] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const displayLines = captionDisplayLines(captionGroups)
  const currentGroupLines = displayLines.filter((line) => line.group.key === currentGroup?.key)
  const activeWordIndex = Math.max(0, spokenWordCount - 1)
  const activeGroupLine = currentGroupLines.find((line) => activeWordIndex < line.end) || currentGroupLines.at(-1)
  const activeLineIndex = Math.max(0, displayLines.indexOf(activeGroupLine))

  useEffect(() => {
    setUserScrolledBack(false)
  }, [replaySessionId])

  useEffect(() => {
    const transcript = transcriptRef.current
    if (!transcript || userScrolledBack) return

    if (typeof transcript.scrollTo === "function") transcript.scrollTo({ top: transcript.scrollHeight, behavior: "smooth" })
    else transcript.scrollTop = transcript.scrollHeight
  }, [safeActiveIndex, userScrolledBack, visibleGroups.length])

  // Lyrics: follow the recording's clock and light each word as the voice
  // reaches it. State only changes when the spoken count does.
  useEffect(() => {
    if (!currentWords || typeof window === "undefined") {
      setSpokenWordCount(0)
      return undefined
    }
    let frame = null
    function tick() {
      const time = audioRef?.current?.currentTime
      const count = Number.isFinite(time) ? spokenWordCountAt(currentWords, time) : 0
      setSpokenWordCount((current) => (current === count ? current : count))
      frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [currentWords, audioRef])

  function handleScroll(event) {
    const element = event.currentTarget
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight
    setUserScrolledBack(distanceFromBottom > 18)
  }

  if (safeEntries.length === 0) return null

  const activeLineWords = activeGroupLine && currentWords?.slice(activeGroupLine.start, activeGroupLine.end)
  const activeLineColors = currentWordColors && activeGroupLine
    ? new Map(Array.from(currentWordColors.entries())
      .filter(([index]) => index >= activeGroupLine.start && index < activeGroupLine.end)
      .map(([index, color]) => [index - activeGroupLine.start, color]))
    : null
  const currentContent = activeGroupLine && narrated && activeLineWords
    ? <ConceptSpokenCaption words={activeLineWords} spokenCount={Math.max(0, spokenWordCount - activeGroupLine.start)} wordColors={activeLineColors} />
    : activeGroupLine?.text

  return (
    <aside className={`concept-atlas-caption-overlay${zoomDocked ? " is-zoom" : ""}${pendingClassName}`} aria-label={narrativeReady ? "Replay transcript" : "Replay transcript not ready"}>
      <div className="concept-atlas-caption-mobile" aria-hidden="true">
        <div className="concept-atlas-caption-mobile-track" style={{ "--concept-atlas-caption-offset": `${(1 - activeLineIndex) * 32}px` }}>
          <p className="concept-atlas-replay-caption is-neighbor">{"\u00a0"}</p>
          {displayLines.map((line, index) => (
            <p
              className={`concept-atlas-replay-caption ${index === activeLineIndex ? "is-current" : "is-neighbor"}`}
              key={`${line.group.key}:${line.start}`}
            >
              {index === activeLineIndex ? currentContent : line.text}
            </p>
          ))}
          <p className="concept-atlas-replay-caption is-neighbor">{"\u00a0"}</p>
        </div>
      </div>
      <div className="concept-atlas-caption-scroll" ref={transcriptRef} onScroll={handleScroll}>
        {visibleGroups.map((group, index) => {
          const current = index === visibleGroups.length - 1
          let content = group.caption
          if (current && narrated && currentWords) {
            content = <ConceptSpokenCaption words={currentWords} spokenCount={spokenWordCount} wordColors={currentWordColors} />
          } else if (current && !narrated) {
            content = <ConceptTypedCaption key={`${replaySessionId}:${group.key}`} replaySessionId={replaySessionId} text={group.caption} />
          }

          return (
            <p
              className={`concept-atlas-replay-caption ${current ? "is-current" : "is-past"}`.trim()}
              key={group.key}
              aria-live={current ? "polite" : undefined}
            >
              {content}
            </p>
          )
        })}
      </div>
    </aside>
  )
}

function ConceptTypedCaption({ replaySessionId = 0, text }) {
  let characterIndex = 0

  return (
    <span className="concept-atlas-caption-type" key={`${replaySessionId}:${text}`}>
      {String(text || "").split(/(\s+)/).map((token, tokenIndex) => {
        if (!token) return null
        if (/^\s+$/.test(token)) {
          characterIndex += token.length
          return token
        }

        return (
          <span className="concept-atlas-caption-word" key={`${token}:${tokenIndex}`}>
            {Array.from(token).map((char, charIndex) => {
              const delay = characterIndex * 16
              characterIndex += 1

              return (
                <span
                  className="concept-atlas-caption-char"
                  key={`${char}:${charIndex}`}
                  style={{ "--caption-char-delay": `${delay}ms` }}
                >
                  {char}
                </span>
              )
            })}
          </span>
        )
      })}
    </span>
  )
}

// Story detail reads as signal strength: one bar for the shortest cut, three
// for the longest, positionally so the icons follow the slider's own order.
// Each signal occupies the bottom of its default viewBox. Center the visible
// bars so every detail level lines up with the adjacent playback speed.
const CONCEPT_REPLAY_DETAIL_ICONS = [
  { Icon: SignalLow, viewBox: "0 6 24 24" },
  { Icon: SignalMedium, viewBox: "0 4 24 24" },
  { Icon: SignalHigh, viewBox: "0 2 24 24" }
]

function conceptReplayDetailIcon(versionIndex, versionCount) {
  if (versionCount <= 1) return CONCEPT_REPLAY_DETAIL_ICONS[1]
  const scaled = Math.round((versionIndex / (versionCount - 1)) * (CONCEPT_REPLAY_DETAIL_ICONS.length - 1))
  return CONCEPT_REPLAY_DETAIL_ICONS[Math.max(0, Math.min(CONCEPT_REPLAY_DETAIL_ICONS.length - 1, scaled))]
}

function ConceptAtlasReplay({ preparing = false, preparationError = null, preparationMessage = null, preparationAction = null, onRetry = null, events, index, playbackSpeed, playbackDetail = "balanced", versions = [], onClose = null, onDetailChange = null, playing, series, onPlayToggle, onScrub, onSpeedChange, zoomDocked = false, narrationStatus = null }) {
  const [openSetting, setOpenSetting] = useState(null)
  const [controlsIdle, setControlsIdle] = useState(false)
  const idleTimerRef = useRef(null)
  const scrubberPointerActiveRef = useRef(false)
  const speedTriggerRef = useRef(null)
  const detailTriggerRef = useRef(null)
  const mobileSettingsTriggerRef = useRef(null)
  const settingsId = useId()

  // A few still seconds during playback fade the whole transport away; any
  // pointer, touch, or key activity brings it back and restarts the clock.
  // An open settings menu never goes idle underneath the user. A tap that
  // lands while the bar is hidden is reported, so the sky can read it as
  // "show me the controls" rather than "leave the player".
  useEffect(() => {
    if (!playing || openSetting || typeof window === "undefined") {
      setControlsIdle(false)
      return undefined
    }
    const mobile = window.matchMedia("(max-width: 700px)").matches
    if (mobile) setControlsIdle(true)
    function arm() {
      if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current)
      idleTimerRef.current = window.setTimeout(() => {
        idleTimerRef.current = null
        setControlsIdle(true)
      }, 3000)
    }
    function wake() {
      setControlsIdle(false)
      arm()
    }
    const events = ["pointermove", "pointerdown", "keydown", "wheel", "touchstart"]
    events.forEach((name) => window.addEventListener(name, wake, { passive: true }))
    arm()
    return () => {
      events.forEach((name) => window.removeEventListener(name, wake))
      if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [playing, openSetting])
  const settingsRef = useDismissiblePopover(Boolean(openSetting), () => setOpenSetting(null))
  const maxIndex = Math.max(0, events.length - 1)
  const safeIndex = Math.max(0, Math.min(maxIndex, Number(index) || 0))
  const playbackSpeedLabel = conceptReplaySpeedLabel(playbackSpeed)
  const selectedVersion = versions.find((version) => version.value === playbackDetail)
  const selectedVersionIndex = Math.max(0, versions.indexOf(selectedVersion))
  const durationLabel = selectedVersion?.durationMs > 0 ? conceptReplayDurationLabel(selectedVersion.durationMs, playbackSpeed) : null
  const allVersionsEqual = versions.length > 0 && versions.filter((version) => version.available).length === 1
  const cursorPercent = replayProgressPercentForIndex(safeIndex, events.length)
  const { Icon: DetailIcon, viewBox: detailIconViewBox } = conceptReplayDetailIcon(selectedVersionIndex, versions.length)
  const detailHint = allVersionsEqual
    ? "This history is short enough for one complete story."
    : "Choose how much of the story to explore."

  function chooseSpeed(nextSpeed) {
    onSpeedChange?.(nextSpeed)
    closeSettings()
  }

  function closeSettings() {
    setOpenSetting(null)
    const triggerRef = openSetting === "mobile" ? mobileSettingsTriggerRef : openSetting === "detail" ? detailTriggerRef : speedTriggerRef
    triggerRef.current?.focus()
  }

  function handleSettingsKeyDown(event) {
    if (event.key === "Escape" && openSetting) {
      event.preventDefault()
      event.stopPropagation()
      closeSettings()
    }
  }

  function handleSpeedKeyDown(event) {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return

    event.preventDefault()
    setOpenSetting("speed")
    const key = event.key
    window.requestAnimationFrame(() => {
      const options = Array.from(settingsRef.current?.querySelectorAll('[role="menuitemradio"]:not(:disabled)') || [])
      if (!options.length) return

      const current = options.indexOf(document.activeElement)
      const index = key === "Home" ? 0 : key === "End" ? options.length - 1 :
        current < 0 ? (key === "ArrowUp" ? options.length - 1 : 0) :
          (current + (key === "ArrowUp" ? -1 : 1) + options.length) % options.length
      options[index].focus()
    })
  }

  function chooseDetail(version) {
    if (version?.available && version.value !== playbackDetail) onDetailChange?.(version.value)
  }

  function scrubberIndexForPointer(event) {
    const rect = event.currentTarget.getBoundingClientRect()
    if (preparing || !rect.width || maxIndex <= 0) return null

    const progress = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    return progress * maxIndex
  }

  function updateScrubberFromPointer(event) {
    const nextIndex = scrubberIndexForPointer(event)
    if (nextIndex === null) return

    onScrub?.(nextIndex)
  }

  function handleScrubberPointerDown(event) {
    if (preparing || event.button !== 0 || maxIndex <= 0) return

    event.preventDefault()
    scrubberPointerActiveRef.current = true
    event.currentTarget.setPointerCapture?.(event.pointerId)
    updateScrubberFromPointer(event)
  }

  function handleScrubberPointerMove(event) {
    if (!scrubberPointerActiveRef.current) return

    event.preventDefault()
    updateScrubberFromPointer(event)
  }

  function handleScrubberPointerEnd(event) {
    if (!scrubberPointerActiveRef.current) return

    scrubberPointerActiveRef.current = false
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }

  return (
    <div className={`concept-atlas-replay ${zoomDocked ? "is-zoom-docked" : ""} ${playing ? "is-playing" : ""} ${controlsIdle ? "is-idle" : ""}`.replace(/\s+/g, " ").trim()} aria-label="Replay concept history">
      <button type="button" className="concept-atlas-replay-mobile-back" aria-label="Exit playback" onClick={onClose}>
        <ChevronLeft aria-hidden="true" />
      </button>
      <div className="concept-atlas-replay-lead">
        <button
          type="button"
          className={`concept-atlas-replay-play ${playing ? "is-playing" : ""}`.trim()}
          aria-label={playing ? "Pause concept history replay" : "Play concept history replay"}
          onClick={onPlayToggle}
          disabled={preparing}
        >
          {playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        </button>
      </div>

      <div className="concept-atlas-replay-scrubber">
        <div
          className="concept-atlas-replay-scrubber-window"
          onPointerDown={handleScrubberPointerDown}
          onPointerMove={handleScrubberPointerMove}
          onPointerUp={handleScrubberPointerEnd}
          onPointerCancel={handleScrubberPointerEnd}
        >
          <ConceptAtlasReplayProgress series={series} cursorPercent={cursorPercent} />
          <input
            type="range"
            min="0"
            max={maxIndex}
            step="any"
            value={safeIndex}
            disabled={preparing}
            aria-label="Scrub concept history"
            onChange={(event) => onScrub(event.target.value)}
          />
        </div>
      </div>

      <div className="concept-atlas-replay-trailing">
        {preparing ? (
          <div className="concept-atlas-replay-preparing" role={preparationError ? "alert" : "status"}>
            <span>{preparationError || preparationMessage}</span>
            {preparationAction ? <button type="button" onClick={onRetry}>{preparationAction}</button> : null}
          </div>
        ) : null}

        {narrationStatus ? <span className="concept-atlas-replay-audio-status" role="status">{narrationStatus}</span> : null}

      <div
        className="concept-atlas-replay-settings-controls"
        ref={settingsRef}
        onKeyDown={handleSettingsKeyDown}
        onBlur={(event) => {
          if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpenSetting(null)
        }}
      >
        <div className="concept-atlas-replay-settings concept-atlas-replay-mobile-settings">
          <button
            ref={mobileSettingsTriggerRef}
            type="button"
            className={`concept-atlas-replay-settings-trigger concept-atlas-replay-mobile-settings-trigger ${openSetting === "mobile" ? "is-open" : ""}`.trim()}
            aria-label="Playback settings"
            aria-expanded={openSetting === "mobile"}
            aria-haspopup="dialog"
            aria-controls={openSetting === "mobile" ? `${settingsId}-mobile` : undefined}
            onClick={() => setOpenSetting((current) => current === "mobile" ? null : "mobile")}
          >
            <MoreVertical aria-hidden="true" />
          </button>

          {openSetting === "mobile" ? (
            <div className="concept-atlas-replay-menu concept-atlas-replay-mobile-menu" id={`${settingsId}-mobile`} role="dialog" aria-label="Playback settings">
              <div className="concept-atlas-replay-mobile-menu-handle" aria-hidden="true" />
              <section className="concept-atlas-replay-mobile-menu-section" aria-labelledby={`${settingsId}-mobile-speed`}>
                <span className="concept-atlas-replay-menu-title" id={`${settingsId}-mobile-speed`}>Playback speed</span>
                <div className="concept-atlas-replay-mobile-options">
                  {CONCEPT_REPLAY_SPEED_OPTIONS.map((option) => (
                    <button
                      type="button"
                      className={`concept-atlas-replay-mobile-option ${option.value === playbackSpeed ? "is-selected" : ""}`.trim()}
                      key={option.value}
                      aria-pressed={option.value === playbackSpeed}
                      onClick={() => onSpeedChange?.(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>
              {versions.length > 0 ? (
                <section className="concept-atlas-replay-mobile-menu-section" aria-labelledby={`${settingsId}-mobile-detail`}>
                  <span className="concept-atlas-replay-menu-title" id={`${settingsId}-mobile-detail`}>Story detail</span>
                  <p className="concept-atlas-replay-mobile-detail-hint">{detailHint}</p>
                  <div className="concept-atlas-replay-mobile-options">
                    {versions.map((version) => (
                      <button
                        type="button"
                        key={version.value}
                        className={`concept-atlas-replay-mobile-option ${version.value === playbackDetail ? "is-selected" : ""}`.trim()}
                        aria-pressed={version.value === playbackDetail}
                        disabled={!version.available}
                        onClick={() => chooseDetail(version)}
                      >
                        <span>{version.label}</span>
                        <small>{conceptReplayDurationLabel(version.durationMs, playbackSpeed)}</small>
                      </button>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          ) : null}
        </div>

        {versions.length > 0 ? (
          <div className="concept-atlas-replay-settings concept-atlas-replay-desktop-setting">
            <button
              ref={detailTriggerRef}
              type="button"
              className={`concept-atlas-replay-settings-trigger concept-atlas-replay-detail-trigger ${openSetting === "detail" ? "is-open" : ""}`.trim()}
              aria-label={`Story detail, ${selectedVersion?.label || "Balanced"}${durationLabel ? `, estimated duration ${durationLabel}` : ""}`}
              aria-expanded={openSetting === "detail"}
              aria-haspopup="dialog"
              aria-controls={openSetting === "detail" ? `${settingsId}-detail` : undefined}
              onClick={() => setOpenSetting((current) => current === "detail" ? null : "detail")}
            >
              <DetailIcon aria-hidden="true" viewBox={detailIconViewBox} />
            </button>

            {openSetting === "detail" ? (
              <div className="concept-atlas-replay-menu concept-atlas-replay-detail-menu" id={`${settingsId}-detail`} role="dialog" aria-label="Story detail">
                <p className="concept-atlas-replay-detail-heading" id={`${settingsId}-detail-hint`}>{detailHint}</p>
                <div className="concept-atlas-replay-detail-slider" style={{ "--detail-progress": `${selectedVersionIndex / Math.max(1, versions.length - 1) * 100}%` }}>
                  <div className="concept-atlas-replay-detail-ticks" aria-hidden="true">
                    {versions.map((version) => <span key={version.value} className={version.available ? "" : "is-unavailable"} />)}
                  </div>
                  <input
                    type="range"
                    min="0"
                    max={versions.length - 1}
                    step="1"
                    value={selectedVersionIndex}
                    disabled={allVersionsEqual}
                    aria-label="Story detail"
                    aria-valuetext={`${selectedVersion?.label || "Balanced"}${durationLabel ? `, approximately ${durationLabel}` : ""}`}
                    aria-describedby={`${settingsId}-detail-hint`}
                    autoFocus
                    onChange={(event) => chooseDetail(versions[Number(event.target.value)])}
                  />
                </div>
                <div className="concept-atlas-replay-detail-options">
                  {versions.map((version) => (
                    <button
                      type="button"
                      key={version.value}
                      className={`concept-atlas-replay-detail-option ${version.value === playbackDetail ? "is-selected" : ""}`.trim()}
                      aria-pressed={version.value === playbackDetail}
                      disabled={!version.available}
                      title={!version.available ? "The available story fits in Balanced." : undefined}
                      onClick={() => chooseDetail(version)}
                    >
                      <span>{version.label}</span>
                      <span className="concept-atlas-replay-duration">{conceptReplayDurationLabel(version.durationMs, playbackSpeed)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="concept-atlas-replay-settings concept-atlas-replay-desktop-setting" onKeyDown={handleSpeedKeyDown}>
          <button
            ref={speedTriggerRef}
            type="button"
            className={`concept-atlas-replay-settings-trigger concept-atlas-replay-speed-trigger ${openSetting === "speed" ? "is-open" : ""}`.trim()}
            aria-label={`Playback speed, ${playbackSpeedLabel}`}
            aria-expanded={openSetting === "speed"}
            aria-haspopup="menu"
            aria-controls={openSetting === "speed" ? `${settingsId}-speed` : undefined}
            onClick={() => setOpenSetting((current) => current === "speed" ? null : "speed")}
          >
            <span>{playbackSpeedLabel}</span>
          </button>

          {openSetting === "speed" ? (
            <div className="concept-atlas-replay-menu concept-atlas-replay-speed-menu" id={`${settingsId}-speed`} role="menu" aria-label="Playback speed">
              <div className="concept-atlas-replay-menu-section">
                <span className="concept-atlas-replay-menu-title">Playback speed</span>
                <div className="concept-atlas-replay-speed-list">
                  {CONCEPT_REPLAY_SPEED_OPTIONS.map((option) => {
                    const selected = option.value === playbackSpeed

                    return (
                      <button
                        type="button"
                        className={`concept-atlas-replay-speed-option ${selected ? "is-selected" : ""}`.trim()}
                        key={option.value}
                        role="menuitemradio"
                        aria-checked={selected}
                        onClick={() => chooseSpeed(option.value)}
                      >
                        <Check aria-hidden="true" />
                        <span>{option.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      </div>
    </div>
  )
}

function ConceptAtlasReplayProgress({ series, cursorPercent = 100 }) {
  const safeCursorPercent = Math.max(0, Math.min(100, Number(cursorPercent) || 0))
  const monthTicks = replayTimelineMonthTicks(series)
  const thumbClassName = [
    "concept-atlas-replay-progress-thumb",
    safeCursorPercent <= 0 ? "is-start" : "",
    safeCursorPercent >= 100 ? "is-end" : ""
  ].filter(Boolean).join(" ")

  return (
    <div className="concept-atlas-replay-progress" style={{ "--replay-progress": `${safeCursorPercent}%` }}>
      <span className="concept-atlas-replay-progress-track" aria-hidden="true">
        <span className="concept-atlas-replay-progress-fill"></span>
        <span className={thumbClassName} style={{ left: `${safeCursorPercent}%` }}></span>
      </span>
      {monthTicks.length > 0 ? (
        <span className="concept-atlas-replay-months" aria-hidden="true">
          {monthTicks.map((tick) => (
            <span
              className={`concept-atlas-replay-month ${tick.edge ? `is-${tick.edge}` : ""}`.trim()}
              key={tick.key}
              style={{ left: `${tick.position}%` }}
            >
              {tick.label}
            </span>
          ))}
        </span>
      ) : null}
    </div>
  )
}

function conceptReplayFrameWithActivation(frame, activationFrame = null) {
  if (!frame) return frame

  return {
    ...frame,
    activeConceptKeys: activationFrame?.activeConceptKeys || [],
    activeEdgeKeys: activationFrame?.activeEdgeKeys || [],
    activePersonaKeys: activationFrame?.activePersonaKeys || [],
    activeActionKeys: activationFrame?.activeActionKeys || []
  }
}

// During replay only a lit constellation — spoken or in the beat — carries a
// label; resting constellations keep their stars and lose their names.
export function conceptAtlasRootLabelVisible({ hovered = false, idle = false, replayLabelsActive = false, replayActive = false } = {}) {
  return Boolean(hovered || idle || (replayLabelsActive && replayActive))
}

function conceptAtlasReplayActive({
  item,
  nodeType,
  replayActiveConceptKeys,
  replayActivePersonaKeys,
  replayActiveActionKeys
}) {
  if (nodeType === "persona") return replayActivePersonaKeys.has(item.contextKey || item.key)
  if (nodeType === "action") return replayActiveActionKeys.has(item.contextKey || item.key)

  return replayActiveConceptKeys.has(item.key)
}

function conceptAtlasReplayVisible({
  item,
  nodeType,
  replayVisibleConceptKeys,
  replayVisiblePersonaKeys,
  replayVisibleActionKeys
}) {
  if (nodeType === "persona") return replayVisiblePersonaKeys.has(item.contextKey || item.key)
  if (nodeType === "action") return replayVisibleActionKeys.has(item.contextKey || item.key)

  return replayVisibleConceptKeys.has(item.key)
}

export function replayFrameConceptCommitCount(frame, conceptKey) {
  const activeDeltas = frame?.activeEvent?.conceptCommitDeltas || {}
  const activeCount = Number(activeDeltas[conceptKey])
  if (activeCount > 0) return activeCount

  const count = Number(frame?.conceptCommitCounts?.[conceptKey]) || 0
  if (count > 0 && count < 1) return 1

  return Math.round(count)
}

function contextTimelineReplaySignature(contextTimeline = null) {
  if (!contextTimeline) return ""

  return JSON.stringify({
    status: contextTimeline.status || null,
    personas: (contextTimeline.personas || []).map((persona) => [
      persona.key,
      persona.name,
      (persona.milestones || []).map((milestone) => [
        milestone.date,
        milestone.caption,
        milestone.storyRole,
        milestone.actionKey,
        milestone.evidenceCommitShas
      ])
    ]),
    actions: (contextTimeline.actions || []).map((action) => [
      action.key,
      action.label,
      action.personaKeys,
      (action.milestones || []).map((milestone) => [
        milestone.date,
        milestone.caption,
        milestone.storyRole,
        milestone.personaKey,
        milestone.evidenceCommitShas
      ])
    ])
  })
}

function replayTranscriptReplaySignature(replayTranscript = null) {
  if (!replayTranscript) return ""

  return JSON.stringify({
    status: replayTranscript.status || null,
    narrated: Boolean(replayTranscript.narration),
    source: replayTranscript.source || null,
    entries: (replayTranscript.entries || []).map((entry) => [
      entry.sequence,
      entry.date,
      entry.caption,
      entry.storyRole,
      entry.conceptKeys,
      entry.personaKeys,
      entry.actionKeys,
      entry.targetKeys,
      entry.evidenceCommitShas
    ])
  })
}

function contextConstellationsSignature(contextConstellations = null) {
  if (!contextConstellations) return ""

  const signatureFor = (records = []) => records.map((record) => [
    record?.subjectType,
    record?.subjectKey || record?.key,
    record?.label,
    record?.constellation?.status,
    record?.constellation?.generatedAt,
    (record?.constellation?.facets || []).map((facet) => [
      facet.key,
      facet.label,
      facet.parentKey,
      facet.kind,
      facet.commitShas
    ]),
    (record?.nextMoves || []).map((move) => [
      move.key,
      move.title,
      move.facetKey,
      move.personaKey,
      move.actionKey,
      move.conceptKey,
      move.recommendationScore
    ]),
    (record?.replayNarrative?.milestones || []).map((milestone) => [
      milestone.date,
      milestone.caption,
      milestone.personaKey,
      milestone.actionKey,
      milestone.conceptKey,
      milestone.evidenceCommitShas
    ])
  ])

  return JSON.stringify({
    personas: signatureFor(contextConstellations.personas || []),
    actions: signatureFor(contextConstellations.actions || [])
  })
}

function conceptReplaySpeedLabel(speed) {
  return CONCEPT_REPLAY_SPEED_OPTIONS.find((option) => option.value === speed)?.label || "1x"
}

function replayProgressPercentForIndex(index, length) {
  if (length <= 1) return 50

  return (index / (length - 1)) * 100
}

function conceptsFromPayload(payload = {}) {
  if (payload?.concepts?.items) return payload.concepts
  if (payload?.concept?.concepts?.items) return payload.concept.concepts

  return null
}

export function conceptAtlasItemsFor(concepts = [], domain = null, focusContext = {}, contextOverview = null, contextConstellations = null, contextRoutes = {}) {
  const contextIndex = conceptAtlasContextIndex(contextOverview, contextConstellations, contextRoutes)
  const conceptItems = concepts.map((concept, index) => {
    const key = conceptItemKey(concept) || `concept-${index}`
    const commits = Array.isArray(concept?.commits) ? concept.commits : []
    const facets = Array.isArray(concept?.constellation?.facets) ? concept.constellation.facets : []
    const replayNarrative = compactReplayNarrative(concept)
    const relationships = [
      ...conceptAtlasRelationshipsFor(concept),
      ...conceptAtlasContextRelationshipsFor(concept, replayNarrative, contextIndex)
    ]
    const starColor = starColorFor("concept", key)
    const atlasCommits = compactAtlasCommits(concept)
    const atlasContributionDays = compactAtlasContributionDays(concept)

    return {
      key,
      atlasType: "concept",
      title: conceptDisplayTitle(concept),
      summary: concept?.summary || "",
      commitCount: Number(concept?.commitCount || commits.length || 0),
      rootCommit: compactAtlasRootCommit(concept),
      detailPath: concept?.detailPath || null,
      color: starColor,
      glowColor: starGlowColorFor(starColor),
      luminance: conceptAtlasLuminanceFor({
        commits: atlasCommits,
        contributionDays: atlasContributionDays,
        generatedAt: concept?.constellation?.generatedAt
      }, domain),
      graphNode: concept?.constellation?.graphNode || null,
      generatedAt: concept?.constellation?.generatedAt || null,
      replayNarrative,
      commits: atlasCommits,
      contributionDays: atlasContributionDays,
      relationships: relationships.map((relationship) => ({
        targetKey: relationship.targetKey,
        kind: relationship.kind || "other",
        direction: relationship.direction || "outbound",
        strength: Number(relationship.strength || 1),
        label: relationship.label || "",
        rationale: relationship.rationale || "",
        commitShas: Array.isArray(relationship.commitShas) ? relationship.commitShas : [],
        evidenceFallback: Boolean(relationship.evidenceFallback)
      })).filter((relationship) => relationship.targetKey),
      facets: facets.map((facet, facetIndex) => ({
        key: facet.key || `${key}-facet-${facetIndex}`,
        label: facet.label || `Facet ${facetIndex + 1}`,
        parentKey: facet.parentKey || null,
        kind: facet.kind || null,
        firstSeenDate: facet.firstSeenDate || null,
        commitShas: Array.isArray(facet.commitShas) ? facet.commitShas : [],
        color: starColor
      }))
    }
  })

  return [
    ...conceptItems,
    ...contextIndex.personas.map((persona) => contextAtlasPersonaItem(persona, domain)),
    ...contextIndex.actions.map((action) => contextAtlasActionItem(action, contextIndex, domain))
  ]
}

// Maps atlas node keys to their inbox-issue attention state. A node with any
// linked open issue pulses ("attention"); a linked open Critical issue
// escalates it to a corona ("critical"). Nodes without open issues are absent.
export function conceptAtlasAttentionByKey(concepts = [], issues = [], atlasItems = []) {
  const issueList = Array.isArray(issues) ? issues : []
  const openIssues = issueList.filter(isOpenInboxIssue)
  const openIssueByKey = new Map(openIssues.map((issue) => [String(issue?.key || ""), issue]))
  const knownIssueKeys = new Set(issueList.map((issue) => String(issue?.key || "")).filter(Boolean))
  const atlasKeys = new Set((Array.isArray(atlasItems) ? atlasItems : []).map((item) => item?.key).filter(Boolean))
  const attention = {}

  function mark(nodeKey, issue) {
    if (!nodeKey || !atlasKeys.has(nodeKey)) return

    const level = String(issue?.severity || "").toLowerCase() === "critical" ? "critical" : "attention"
    if (attention[nodeKey] !== "critical") attention[nodeKey] = level
  }

  openIssues.forEach((issue) => {
    conceptAtlasList(issue?.personaKeys || issue?.persona_keys).forEach((reference) => {
      const slug = conceptAtlasStableKey(reference)
      if (slug) mark(conceptAtlasTypedKey("persona", slug), issue)
    })
    conceptAtlasList(issue?.actionKeys || issue?.action_keys).forEach((reference) => {
      const slug = conceptAtlasStableKey(reference)
      if (slug) mark(conceptAtlasTypedKey("action", slug), issue)
    })
  })

  ;(Array.isArray(concepts) ? concepts : []).forEach((concept) => {
    const nodeKey = conceptItemKey(concept)
    if (!nodeKey) return

    conceptAtlasList(concept?.nextMoves).forEach((move) => {
      const issueKey = String(move?.inboxIssue?.key || move?.inboxIssueKey || move?.inbox_issue_key || "")
      if (!issueKey) return

      const liveIssue = openIssueByKey.get(issueKey)
      if (liveIssue) {
        mark(nodeKey, liveIssue)
        return
      }
      // The live issue list is the source of truth: a key it knows but did
      // not surface as open is closed. The embedded copy only bridges
      // payloads where the live list is missing the issue entirely.
      if (!knownIssueKeys.has(issueKey) && isOpenInboxIssue(move?.inboxIssue)) mark(nodeKey, move.inboxIssue)
    })
  })

  return attention
}

// Collects the atlas node keys related to a single inbox issue — its typed
// persona/action satellites plus every concept whose next moves reference the
// issue. Returns null (no highlight) rather than an empty set so the scene's
// "undefined leaves state untouched / null clears" convention stays legible.
// Empty-sky taps unwind whatever is open, innermost first: a zoomed concept
// returns to the player behind it, then an issue sheet, and a tap in the
// player itself either toggles mobile playback or returns desktop to the
// resting sky.
export function atlasBackgroundTapTarget({ focusedKey = null, issueDetailOpen = false, replaySessionActive = false } = {}) {
  if (focusedKey) return "concept"
  if (issueDetailOpen) return "issue"
  if (!replaySessionActive) return null
  return "replay"
}

export function conceptAtlasIssueHighlightKeys(concepts = [], issue = null, atlasItems = []) {
  if (!issue) return null

  const issueKey = String(issue?.key || "")
  const atlasKeys = new Set((Array.isArray(atlasItems) ? atlasItems : []).map((item) => item?.key).filter(Boolean))
  const highlightKeys = new Set()

  function mark(nodeKey) {
    if (nodeKey && atlasKeys.has(nodeKey)) highlightKeys.add(nodeKey)
  }

  conceptAtlasList(issue?.personaKeys || issue?.persona_keys).forEach((reference) => {
    const slug = conceptAtlasStableKey(reference)
    if (slug) mark(conceptAtlasTypedKey("persona", slug))
  })
  conceptAtlasList(issue?.actionKeys || issue?.action_keys).forEach((reference) => {
    const slug = conceptAtlasStableKey(reference)
    if (slug) mark(conceptAtlasTypedKey("action", slug))
  })

  if (issueKey) {
    ;(Array.isArray(concepts) ? concepts : []).forEach((concept) => {
      const nodeKey = conceptItemKey(concept)
      if (!nodeKey) return

      const related = conceptAtlasList(concept?.nextMoves).some((move) =>
        String(move?.inboxIssue?.key || move?.inboxIssueKey || move?.inbox_issue_key || "") === issueKey)
      if (related) mark(nodeKey)
    })
  }

  return highlightKeys.size > 0 ? highlightKeys : null
}

// Collects the atlas node keys the Context frame's selection should light up:
// the selected persona/action star plus its one-hop relationship neighbors.
// Concept→persona/action edges live only on the concept item, so neighbors
// must be gathered from both edge directions. An unmatched target returns
// null (no emphasis) — the sky must never dim wholesale because a key failed
// to resolve.
export function conceptAtlasEmphasisKeysFor(atlasItems = [], target = null) {
  const type = target?.type
  const targetKey = conceptAtlasStableKey(target?.key)
  if (!type || !targetKey) return null

  const items = Array.isArray(atlasItems) ? atlasItems : []
  const item = items.find((candidate) => (
    candidate?.atlasType === type &&
    (candidate?.contextKey === targetKey || conceptAtlasStableKey(candidate?.contextRouteKey) === targetKey)
  ))
  if (!item?.key) return null

  const itemKeys = new Set(items.map((candidate) => candidate?.key).filter(Boolean))
  const emphasisKeys = new Set([item.key])
  items.forEach((candidate) => {
    if (!candidate?.key) return

    const relationships = Array.isArray(candidate.relationships) ? candidate.relationships : []
    relationships.forEach((relationship) => {
      const relatedKey = relationship?.targetKey
      if (!relatedKey) return
      if (candidate.key === item.key && itemKeys.has(relatedKey)) emphasisKeys.add(relatedKey)
      if (relatedKey === item.key) emphasisKeys.add(candidate.key)
    })
  })

  return emphasisKeys
}

export function conceptReplayFocusToneColors(concepts = [], replayDate, { domain = null, frame = null } = {}) {
  const replayDateKey = normalizeDateKey(replayDate)
  if (!replayDateKey) return {}
  const replayDomain = conceptReplayDomainAt(concepts, replayDateKey, domain)
  const coolingWindowDays = conceptReplayCoolingWindowDays(replayDomain)
  const activeConceptKeys = new Set(Array.isArray(frame?.activeConceptKeys) ? frame.activeConceptKeys : [])

  return Object.fromEntries((Array.isArray(concepts) ? concepts : []).map((concept, index) => {
    const key = conceptItemKey(concept) || concept?.key || `concept-${index}`
    const tone = activeConceptKeys.has(key)
      ? conceptReplayFreshActivityTone(replayDateKey, { coolingWindowDays })
      : conceptReplayActivityTone(concept, replayDateKey, { coolingWindowDays })

    return [key, {
      color: tone.lineColor,
      glowColor: tone.glowColor,
      lineColor: tone.lineColor,
      areaStartColor: tone.areaStartColor,
      areaEndColor: tone.areaEndColor,
      luminance: tone.luminance,
      tone: tone.key,
      score: tone.score,
      signal: tone.signal
    }]
  }))
}

function conceptReplayFreshActivityTone(replayDateKey, { coolingWindowDays = CONCEPT_REPLAY_COOLING_DAYS } = {}) {
  return conceptReplayToneForScore(1, "focused", {
    totalActivity: 1,
    daysSinceLastActivity: 0,
    lastActivityDate: replayDateKey,
    lastActivity: 1,
    coolingWindowDays,
    activityRecencyScore: 1,
    focusScore: 1,
    replayFrameActive: true
  })
}

function conceptReplayDomainAt(concepts = [], replayDateKey, domain = null) {
  const start = normalizeDateKey(domain?.start) || conceptEarliestActivityKey(concepts) || replayDateKey
  const domainEnd = normalizeDateKey(domain?.end)
  const end = domainEnd && domainEnd < replayDateKey ? domainEnd : replayDateKey

  return { start, end }
}

function conceptReplayCoolingWindowDays(domain = null) {
  const start = parseDateOnly(domain?.start)
  const end = parseDateOnly(domain?.end)
  if (!start || !end) return CONCEPT_REPLAY_COOLING_DAYS

  const historyDays = Math.max(1, daysBetween(start, end) + 1)

  return clampNumber(
    Math.round(historyDays * 0.28),
    CONCEPT_REPLAY_COOLING_DAYS,
    CONCEPT_REPLAY_COOLING_DAYS * 3
  )
}

function conceptReplayActivityTone(concept, replayDateKey, { coolingWindowDays = CONCEPT_REPLAY_COOLING_DAYS } = {}) {
  const replayDate = parseDateOnly(replayDateKey)
  const activeDays = buildContributionSeries(concept, concept?.commits || [])
    .filter((day) => day.date && day.date <= replayDateKey)
    .map((day) => ({
      ...day,
      activity: conceptDayActivity(day),
      parsedDate: parseDateOnly(day.date)
    }))
    .filter((day) => day.parsedDate && (day.activity > 0 || Number(day.count) > 0))
  const totalActivity = activeDays.reduce((sum, day) => sum + Math.max(day.activity, Number(day.count) || 0), 0)

  if (!replayDate || activeDays.length === 0 || totalActivity <= 0) {
    const birthKey = conceptReplayBirthKey(concept)
    const birthDate = parseDateOnly(birthKey)
    if (birthDate && birthKey <= replayDateKey) {
      const daysSinceBirth = Math.max(0, daysBetween(birthDate, replayDate))
      const score = clamp01(1 - (daysSinceBirth / Math.max(CONCEPT_REPLAY_FRESH_DAYS, coolingWindowDays)))

      return conceptReplayToneForScore(score, conceptReplayToneKey(score), {
        totalActivity: 1,
        daysSinceLastActivity: daysSinceBirth,
        lastActivityDate: birthKey,
        lastActivity: 1,
        coolingWindowDays,
        activityRecencyScore: score,
        focusScore: score,
        birthFallback: true
      })
    }

    return conceptReplayToneForScore(0, "abandoned", {
      totalActivity: 0,
      daysSinceLastActivity: null,
      lastActivityDate: null,
      coolingWindowDays
    })
  }

  const lastActivity = activeDays[activeDays.length - 1]
  const daysSinceLastActivity = Math.max(0, daysBetween(lastActivity.parsedDate, replayDate))
  const score = clamp01(1 - (daysSinceLastActivity / Math.max(CONCEPT_REPLAY_FRESH_DAYS, coolingWindowDays)))

  return conceptReplayToneForScore(score, conceptReplayToneKey(score), {
    totalActivity,
    daysSinceLastActivity,
    lastActivityDate: lastActivity.date,
    lastActivity: lastActivity.activity,
    coolingWindowDays,
    activityRecencyScore: score,
    focusScore: score
  })
}

function conceptReplayBirthKey(concept) {
  return normalizeDateKey(concept?.rootCommit?.committedAt)
    || normalizeDateKey(concept?.rootCommit?.date)
    || normalizeDateKey(concept?.root_commit?.committed_at)
    || normalizeDateKey(concept?.root_commit?.date)
    || normalizeDateKey(concept?.generatedAt)
    || normalizeDateKey(concept?.generated_at)
    || normalizeDateKey(concept?.constellation?.generatedAt)
    || normalizeDateKey(concept?.constellation?.generated_at)
}

function conceptReplayToneKey(score) {
  if (score >= 0.66) return "focused"
  if (score >= 0.42) return "active"
  if (score >= 0.18) return "cooling"

  return "abandoned"
}

function conceptReplayToneForScore(score, key, signal = {}) {
  const hue = conceptReplayHue(score)
  const lightness = Math.round(56 + clamp01(score) * 8)
  const lineColor = `hsl(${hue}, 82%, ${lightness}%)`

  return {
    key,
    label: key,
    score,
    signal,
    // The atlas modulates star brightness (never hue) during replay; the hsl
    // colors below stay for the non-atlas detail surfaces.
    luminance: signal?.daysSinceLastActivity == null
      ? STAR_LUMINANCE_FLOOR
      : starLuminanceForDaysSince(signal.daysSinceLastActivity),
    lineColor,
    areaStartColor: `hsla(${hue}, 82%, ${lightness}%, ${0.18 + clamp01(score) * 0.1})`,
    areaEndColor: `hsla(${hue}, 82%, ${lightness}%, 0)`,
    glowColor: `hsla(${hue}, 82%, ${lightness}%, ${0.34 + clamp01(score) * 0.18})`
  }
}

function conceptReplayHue(score) {
  const safeScore = clamp01(score)
  if (safeScore >= 0.66) return Math.round(lerp(96, 139, (safeScore - 0.66) / 0.34))
  if (safeScore >= 0.3) return Math.round(lerp(34, 96, (safeScore - 0.3) / 0.36))

  return Math.round(lerp(2, 34, safeScore / 0.3))
}

// Resting star brightness from recency: days since the node's last activity
// (contribution day or commit), measured against the contribution-domain end
// so it stays deterministic for a given payload rather than wall-clock time.
function conceptAtlasLuminanceFor({ commits = [], contributionDays = [], generatedAt = null } = {}, domain = null) {
  const activityKeys = [
    ...(Array.isArray(contributionDays) ? contributionDays : [])
      .filter((day) => (Number(day?.codeVolume) || 0) > 0 || (Number(day?.count) || 0) > 0)
      .map((day) => normalizeDateKey(day?.date)),
    ...(Array.isArray(commits) ? commits : []).map((commit) => normalizeDateKey(commit?.committedAt || commit?.date))
  ].filter(Boolean).sort()
  const lastKey = activityKeys[activityKeys.length - 1] || normalizeDateKey(generatedAt)
  if (!lastKey) return STAR_LUMINANCE_UNKNOWN

  const domainEndKey = normalizeDateKey(domain?.end)
  const endKey = domainEndKey && domainEndKey > lastKey ? domainEndKey : lastKey
  const lastDate = parseDateOnly(lastKey)
  const endDate = parseDateOnly(endKey)
  if (!lastDate || !endDate) return STAR_LUMINANCE_UNKNOWN

  return starLuminanceForDaysSince(Math.max(0, daysBetween(lastDate, endDate)))
}

function conceptEarliestActivityKey(concepts = []) {
  return (Array.isArray(concepts) ? concepts : [])
    .flatMap((concept) => [
      ...(Array.isArray(concept?.contributionDays) ? concept.contributionDays.map((day) => normalizeDateKey(day?.date)) : []),
      ...(Array.isArray(concept?.commits) ? concept.commits.map((commit) => normalizeDateKey(commit?.committedAt || commit?.date)) : [])
    ])
    .filter(Boolean)
    .sort()[0] || null
}

function compactAtlasCommits(concept) {
  return fullConceptCommits(concept).map((commit) => ({
    id: commit.id || null,
    sha: commit.sha || null,
    shortSha: commit.shortSha || null,
    status: commit.status || null,
    title: commit.title || commit.commitSubject || null,
    summary: commit.summary || null,
    committedAt: commit.committedAt || null,
    date: normalizeDateKey(commit.committedAt),
    additions: Number(commit.additions) || 0,
    deletions: Number(commit.deletions) || 0
  }))
}

function compactAtlasRootCommit(concept) {
  const rootCommit = conceptRootCommit(concept)
  if (!rootCommit) return null

  return compactAtlasCommit(rootCommit)
}

function compactAtlasCommit(commit) {
  if (!commit) return null

  const committedAt = commit.committedAt || commit.date || null

  return {
    id: commit.id || null,
    sha: commit.sha || null,
    shortSha: commit.shortSha || commit.short_sha || null,
    status: commit.status || null,
    title: commit.title || commit.commitSubject || null,
    summary: commit.summary || null,
    committedAt,
    date: normalizeDateKey(committedAt),
    additions: Number(commit.additions) || 0,
    deletions: Number(commit.deletions) || 0
  }
}

function conceptRootCommitSignature(commit) {
  if (!commit) return null

  return [
    commit.id,
    commit.sha,
    commit.shortSha || commit.short_sha,
    commit.committedAt || commit.date,
    commit.title,
    commit.summary
  ]
}

function compactAtlasContributionDays(concept) {
  return (Array.isArray(concept?.contributionDays) ? concept.contributionDays : []).map((day) => ({
    date: normalizeDateKey(day.date),
    count: Number(day.count) || day.commits?.length || 0,
    codeVolume: Number(day.codeVolume) || commitVolume(day.commits),
    remainingCount: Number(day.remainingCount) || 0,
    commits: compactAtlasDayCommits(day.commits)
  })).filter((day) => day.date)
}

function compactAtlasDayCommits(commits = []) {
  return uniqueCommits(Array.isArray(commits) ? commits : []).map((commit) => ({
    id: commit.id || null,
    sha: commit.sha || null,
    shortSha: commit.shortSha || null,
    committedAt: commit.committedAt || commit.date || null,
    additions: Number(commit.additions) || 0,
    deletions: Number(commit.deletions) || 0
  }))
}

function compactReplayNarrative(concept) {
  const narrative = concept?.replayNarrative || {}
  const milestones = Array.isArray(narrative?.milestones) ? narrative.milestones : []
  const supplementalMilestones = Array.isArray(narrative?.supplementalMilestones) ? narrative.supplementalMilestones : []
  const transcript = narrative?.transcript || null
  const transcriptEntries = Array.isArray(transcript?.entries) ? transcript.entries : []
  const compactMilestone = (milestone) => ({
    conceptKey: milestone.conceptKey || conceptItemKey(concept),
    source: milestone.source || null,
    sequence: Number(milestone.sequence) || null,
    date: milestone.date || null,
    caption: milestone.caption || "",
    storyRole: milestone.storyRole || null,
    personaKey: milestone.personaKey || null,
    workflowKey: milestone.workflowKey || null,
    targetKeys: Array.isArray(milestone.targetKeys) ? milestone.targetKeys : [],
    evidenceCommitShas: Array.isArray(milestone.evidenceCommitShas) ? milestone.evidenceCommitShas : [],
    facetKeys: Array.isArray(milestone.facetKeys) ? milestone.facetKeys : []
  })
  const compactTranscriptEntries = transcriptEntries.map((entry, index) => ({
    source: entry.source || transcript?.source || null,
    sequence: Number(entry.sequence || index + 1),
    date: entry.date || null,
    caption: entry.caption || "",
    storyRole: entry.storyRole || null,
    conceptKeys: Array.isArray(entry.conceptKeys) ? entry.conceptKeys : [entry.conceptKey || conceptItemKey(concept)].filter(Boolean),
    personaKeys: Array.isArray(entry.personaKeys) ? entry.personaKeys : [entry.personaKey].filter(Boolean),
    actionKeys: Array.isArray(entry.actionKeys) ? entry.actionKeys : [entry.actionKey || entry.workflowKey].filter(Boolean),
    targetKeys: Array.isArray(entry.targetKeys) ? entry.targetKeys : [],
    evidenceCommitShas: Array.isArray(entry.evidenceCommitShas) ? entry.evidenceCommitShas : [],
    facetKeys: Array.isArray(entry.facetKeys) ? entry.facetKeys : []
  })).filter((entry) => entry.caption)

  const compact = {
    milestones: milestones.map(compactMilestone).filter((milestone) => milestone.caption)
  }
  const compactSupplementalMilestones = supplementalMilestones.map(compactMilestone).filter((milestone) => milestone.caption)
  if (compactSupplementalMilestones.length > 0) compact.supplementalMilestones = compactSupplementalMilestones
  if (compactTranscriptEntries.length > 0) {
    compact.transcript = {
      status: transcript?.status || "ready",
      source: transcript?.source || compactTranscriptEntries[0]?.source || null,
      entries: compactTranscriptEntries
    }
  }

  return compact
}

function conceptAtlasRelationshipsFor(concept) {
  const explicit = Array.isArray(concept?.constellation?.conceptEdges) ? concept.constellation.conceptEdges : []
  const related = Array.isArray(concept?.constellation?.relatedConcepts) ? concept.constellation.relatedConcepts : []
  const explicitTargets = new Set(explicit.map((relationship) => relationship?.targetKey).filter(Boolean))
  const relationships = explicit.map((relationship) => ({
    targetKey: relationship.targetKey,
    kind: relationship.kind || "other",
    direction: relationship.direction || "outbound",
    strength: Number(relationship.strength || 1),
    label: relationship.label || "",
    rationale: relationship.rationale || "",
    commitShas: Array.isArray(relationship.commitShas) ? relationship.commitShas : [],
    evidenceFallback: false
  })).filter((relationship) => relationship.targetKey)

  related.forEach((relationship) => {
    const targetKey = relationship?.targetKey
    if (!targetKey || explicitTargets.has(targetKey)) return

    const sharedCommits = Array.isArray(relationship.sharedCommitShas) ? relationship.sharedCommitShas : []
    const sharedTerms = Array.isArray(relationship.sharedTerms) ? relationship.sharedTerms : []
    const strength = sharedCommits.length >= 2 ? 2 : sharedCommits.length > 0 || sharedTerms.length >= 2 ? 1.35 : 1

    relationships.push({
      targetKey,
      kind: "overlaps",
      direction: "bidirectional",
      strength,
      label: relationship.relationSignal || "Related concept",
      rationale: "",
      commitShas: sharedCommits,
      evidenceFallback: true
    })
  })

  return relationships
}

function conceptAtlasItemType(item = {}) {
  return item?.atlasType || "concept"
}

function conceptAtlasItemIsConcept(item = {}) {
  return conceptAtlasItemType(item) === "concept"
}

function conceptAtlasNodeTypeVisible(visibility = DEFAULT_CONCEPT_ATLAS_NODE_TYPE_VISIBILITY, nodeType = "concept") {
  const type = nodeType || "concept"
  if (!Object.hasOwn(DEFAULT_CONCEPT_ATLAS_NODE_TYPE_VISIBILITY, type)) return true

  return visibility?.[type] !== false
}

function conceptAtlasNodeTypeLabel(type = "concept") {
  if (type === "persona") return "Persona"
  if (type === "action") return "User action"

  return "Concept"
}

function conceptAtlasDetailAriaLabel(item = {}, nodeType = "concept", commitCount = 0) {
  if (nodeType === "concept") return `Open ${item.title} concept details, ${commitCount} ${pluralize("commit", commitCount)}`
  if (nodeType === "persona") return `Open ${item.title} persona details`
  if (nodeType === "action") return `Open ${item.title} user action details`

  return `Open ${item.title} details`
}

function conceptAtlasContextIndex(contextOverview = null, contextConstellations = null, contextRoutes = {}) {
  const contextConstellationIndex = conceptAtlasContextConstellationIndex(contextConstellations)
  const actionStoryboards = conceptAtlasActionStoryboards(contextOverview, contextRoutes)
  const storyboardsByActionKey = conceptAtlasActionStoryboardIndex(actionStoryboards)
  const personas = conceptAtlasList(contextOverview?.users).map((persona, index) => {
    const routeKey = conceptAtlasPersonaRouteKey(persona, index)
    const key = conceptAtlasStableKey(routeKey)

    return {
      ...persona,
      atlasKey: conceptAtlasTypedKey("persona", key),
      contextKey: key,
      contextRouteKey: routeKey,
      detailPath: conceptAtlasContextDetailPath(contextRoutes?.conceptDetailBasePath || contextRoutes?.conceptListPath, "personas", routeKey),
      contextConstellation: contextConstellationIndex.get(conceptAtlasTypedKey("persona", key)) || null
    }
  }).filter((persona) => persona.contextKey)
  const actions = conceptAtlasList(contextOverview?.actions).map((action, index) => {
    const routeKey = conceptAtlasActionRouteKey(action, index)
    const key = conceptAtlasStableKey(routeKey)

    return {
      ...action,
      atlasKey: conceptAtlasTypedKey("action", key),
      contextKey: key,
      contextRouteKey: routeKey,
      detailPath: conceptAtlasContextDetailPath(contextRoutes?.conceptDetailBasePath || contextRoutes?.conceptListPath, "actions", routeKey),
      contextConstellation: contextConstellationIndex.get(conceptAtlasTypedKey("action", key)) || null
    }
  }).filter((action) => action.contextKey)
  const personasByKey = new Map(personas.map((persona) => [persona.contextKey, persona]))
  const actionsByKey = new Map(actions.map((action) => [action.contextKey, action]))
  const personasByReference = conceptAtlasReferenceIndex(personas, [ "contextKey", "key", "name", "label", "archetype" ])
  const actionsByReference = conceptAtlasReferenceIndex(actions, [ "contextKey", "key", "label", "name" ])

  return {
    actions,
    actionsByKey,
    actionsByReference,
    personas,
    personasByKey,
    personasByReference,
    storyboardsByActionKey
  }
}

function conceptAtlasActionStoryboards(contextOverview = null, contextRoutes = {}) {
  const routeStoryboards = contextRoutes?.actionStoryboards || contextRoutes?.storyboards
  if (Array.isArray(routeStoryboards)) return routeStoryboards.filter(Boolean)

  return conceptAtlasList(
    contextOverview?.storyboards
      || contextOverview?.actionStoryboards
      || contextOverview?.contextActionStoryboards
  )
}

function conceptAtlasActionStoryboardIndex(storyboards = []) {
  const index = new Map()

  conceptAtlasList(storyboards).forEach((storyboard) => {
    [
      storyboard?.actionKey,
      storyboard?.action_key,
      storyboard?.key,
      storyboard?.actionLabel,
      storyboard?.action_label
    ].map(conceptAtlasStableKey).filter(Boolean).forEach((key) => {
      if (!index.has(key)) index.set(key, storyboard)
    })
  })

  return index
}

function conceptAtlasContextConstellationIndex(contextConstellations = null) {
  const records = [
    ...conceptAtlasList(contextConstellations?.personas),
    ...conceptAtlasList(contextConstellations?.actions)
  ]

  return new Map(records.flatMap((record) => {
    const type = record?.subjectType || record?.subject_type
    const key = conceptAtlasStableKey(record?.subjectKey || record?.subject_key || record?.key)
    if (!type || !key) return []

    return [[conceptAtlasTypedKey(type, key), record]]
  }))
}

function contextAtlasPersonaItem(persona, domain = null) {
  const contextConstellation = contextAtlasConstellationPayload(persona)
  const starColor = starColorFor("persona", persona.atlasKey)

  return {
    key: persona.atlasKey,
    contextKey: persona.contextKey,
    contextRouteKey: persona.contextRouteKey,
    atlasType: "persona",
    title: persona.name || persona.label || "Persona",
    summary: conceptAtlasPersonaSummary(persona),
    commitCount: contextConstellation.commitCount,
    detailPath: persona.detailPath || null,
    visual: contextAtlasPersonaVisual(persona),
    color: starColor,
    glowColor: starGlowColorFor(starColor),
    luminance: conceptAtlasLuminanceFor(contextConstellation, domain),
    graphNode: contextConstellation.graphNode || {
      role: "persona",
      clusterKey: "personas",
      clusterLabel: "Personas",
      summary: persona.role || persona.description || null
    },
    generatedAt: contextConstellation.generatedAt,
    constellation: contextConstellation.constellation,
    nextMoves: contextConstellation.nextMoves,
    replayNarrative: contextConstellation.replayNarrative,
    metaLabel: persona.archetype || "Persona",
    commits: contextConstellation.commits,
    contributionDays: contextConstellation.contributionDays,
    relationships: conceptAtlasPersonaRelationships(persona),
    facets: contextConstellation.facets
  }
}

function contextAtlasActionItem(action, contextIndex, domain = null) {
  const contextConstellation = contextAtlasConstellationPayload(action)
  const starColor = starColorFor("action", action.atlasKey)

  return {
    key: action.atlasKey,
    contextKey: action.contextKey,
    contextRouteKey: action.contextRouteKey,
    atlasType: "action",
    title: action.label || action.name || "User action",
    summary: action.description || "",
    commitCount: contextConstellation.commitCount,
    detailPath: action.detailPath || null,
    visual: contextAtlasActionVisual(action, contextIndex),
    color: starColor,
    glowColor: starGlowColorFor(starColor),
    luminance: conceptAtlasLuminanceFor(contextConstellation, domain),
    graphNode: contextConstellation.graphNode || {
      role: "user_action",
      clusterKey: "user-actions",
      clusterLabel: "User actions",
      summary: action.description || null
    },
    generatedAt: contextConstellation.generatedAt,
    constellation: contextConstellation.constellation,
    nextMoves: contextConstellation.nextMoves,
    replayNarrative: contextConstellation.replayNarrative,
    metaLabel: "User action",
    commits: contextConstellation.commits,
    contributionDays: contextConstellation.contributionDays,
    relationships: conceptAtlasActionPersonaRelationships(action, contextIndex),
    facets: contextConstellation.facets
  }
}

function contextAtlasPersonaVisual(persona = {}) {
  const avatar = persona.avatar || {}
  if (!avatar.url) return null

  const title = persona.name || persona.label || "Persona"

  return {
    kind: "persona-avatar",
    status: avatar.status || "ready",
    preserveAlt: true,
    image: {
      url: avatar.url,
      alt: avatar.alt || `${title} persona avatar`
    }
  }
}

function contextAtlasActionVisual(action = {}, contextIndex = {}) {
  const storyboard = contextAtlasActionStoryboard(action, contextIndex.storyboardsByActionKey)
  const firstFrame = storyboard?.frames?.[0]
  const frameUrl = firstFrame?.thumbnailUrl || firstFrame?.url
  if (!frameUrl) return null

  const title = action.label || action.name || storyboard?.actionLabel || "User action"

  return {
    kind: "action-storyboard",
    status: storyboard?.status || "ready",
    preserveAlt: true,
    frames: [{
      url: frameUrl,
      alt: firstFrame.alt || `${title} storyboard frame 1`
    }]
  }
}

function contextAtlasActionStoryboard(action = {}, storyboardsByActionKey = new Map()) {
  return storyboardsByActionKey.get(action.contextKey)
    || storyboardsByActionKey.get(conceptAtlasStableKey(action.contextRouteKey))
    || storyboardsByActionKey.get(conceptAtlasStableKey(action.key))
    || storyboardsByActionKey.get(conceptAtlasStableKey(action.label || action.name))
    || null
}

function contextAtlasConstellationPayload(subject = {}) {
  const record = subject.contextConstellation || {}
  const constellation = record.constellation || null
  const facets = Array.isArray(constellation?.facets) ? constellation.facets : []
  const contributionDays = compactAtlasContributionDays(record)
  const commits = uniqueCommits(contributionDays.flatMap((day) => day.commits))

  return {
    constellation,
    commitCount: commits.length,
    commits,
    contributionDays,
    generatedAt: constellation?.generatedAt || null,
    graphNode: constellation?.graphNode || null,
    nextMoves: Array.isArray(record.nextMoves) ? record.nextMoves : [],
    replayNarrative: record.replayNarrative || null,
    facets: facets.map((facet, facetIndex) => ({
      key: facet.key || `${subject.atlasKey}-facet-${facetIndex}`,
      label: facet.label || `Facet ${facetIndex + 1}`,
      parentKey: facet.parentKey || null,
      kind: facet.kind || null,
      commitShas: Array.isArray(facet.commitShas) ? facet.commitShas : [],
      color: starColorFor(subject.atlasKey?.startsWith("persona:") ? "persona" : "action", subject.atlasKey)
    }))
  }
}

function conceptAtlasPersonaRelationships(persona = {}) {
  const relationships = Array.isArray(persona.relationships || persona.personaRelationships || persona.persona_relationships)
    ? persona.relationships || persona.personaRelationships || persona.persona_relationships
    : []

  return relationships.filter(Boolean).flatMap((relationship, index) => {
    if (!relationship || typeof relationship !== "object") return []

    const targetKey = conceptAtlasStableKey(
      relationship.personaKey || relationship.persona_key || relationship.targetKey || relationship.target_key || relationship.key || relationship.label || relationship.name
    )
    if (!targetKey || targetKey === persona.contextKey) return []

    return [ {
      targetKey: conceptAtlasTypedKey("persona", targetKey),
      kind: "informs",
      direction: "bidirectional",
      strength: 2,
      label: conceptAtlasRelationshipLabel(relationship) || relationship.label || relationship.name || "Related persona",
      rationale: relationship.sharedGoal || relationship.shared_goal || relationship.tension || "",
      commitShas: [],
      evidenceFallback: false,
      sourceIndex: index
    } ]
  })
}

function conceptAtlasActionPersonaRelationships(action = {}, contextIndex) {
  return conceptAtlasActionPersonas(action, contextIndex).map((persona) => ({
    targetKey: persona.atlasKey,
    kind: "uses",
    direction: "bidirectional",
    strength: 2.15,
    label: action.label || action.name || "User action",
    rationale: action.description || "",
    commitShas: [],
    evidenceFallback: false
  }))
}

function conceptAtlasContextRelationshipsFor(concept = {}, replayNarrative = {}, contextIndex) {
  return [
    ...conceptAtlasPersonaContextRelationshipsFor(concept, replayNarrative, contextIndex),
    ...conceptAtlasActionContextRelationshipsFor(replayNarrative, contextIndex)
  ]
}

function conceptAtlasPersonaContextRelationshipsFor(concept = {}, replayNarrative = {}, contextIndex) {
  const personaEvidence = new Map()

  conceptAtlasList(concept?.nextMoves).forEach((move) => {
    const personaKey = conceptAtlasStableKey(move?.personaKey || move?.persona_key)
    if (!personaKey || !contextIndex.personasByKey.has(personaKey)) return

    const current = personaEvidence.get(personaKey) || { labels: [], strength: 1.6 }
    if (move.title) current.labels.push(move.title)
    current.strength = Math.max(current.strength, 2.2)
    personaEvidence.set(personaKey, current)
  })

  conceptAtlasList(replayNarrative?.milestones).forEach((milestone) => {
    const personaKey = conceptAtlasStableKey(milestone?.personaKey || milestone?.persona_key)
    if (!personaKey || !contextIndex.personasByKey.has(personaKey)) return

    const current = personaEvidence.get(personaKey) || { labels: [], strength: 1.6 }
    if (milestone.caption) current.labels.push(milestone.caption)
    current.strength = Math.max(current.strength, 1.75)
    personaEvidence.set(personaKey, current)
  })

  return Array.from(personaEvidence.entries()).map(([personaKey, evidence]) => ({
    targetKey: contextIndex.personasByKey.get(personaKey).atlasKey,
    kind: "informs",
    direction: "bidirectional",
    strength: evidence.strength,
    label: evidence.labels[0] || "Persona fit",
    rationale: evidence.labels.slice(0, 2).join(" "),
    commitShas: [],
    evidenceFallback: false
  }))
}

function conceptAtlasActionContextRelationshipsFor(replayNarrative = {}, contextIndex) {
  const actionEvidence = new Map()

  conceptAtlasList(replayNarrative?.milestones).forEach((milestone) => {
    const workflowKey = conceptAtlasStableKey(milestone?.workflowKey || milestone?.workflow_key)
    const action = workflowKey && (contextIndex.actionsByKey.get(workflowKey) || contextIndex.actionsByReference.get(workflowKey))
    if (!action) return

    const current = actionEvidence.get(action.contextKey) || { action, labels: [], strength: 1.45 }
    if (milestone.caption) current.labels.push(milestone.caption)
    current.strength = Math.max(current.strength, 1.75)
    actionEvidence.set(action.contextKey, current)
  })

  return Array.from(actionEvidence.values()).map((evidence) => ({
    targetKey: evidence.action.atlasKey,
    kind: "informs",
    direction: "bidirectional",
    strength: evidence.strength,
    label: evidence.action.label || evidence.action.name || "User action",
    rationale: evidence.labels.slice(0, 2).join(" "),
    commitShas: [],
    evidenceFallback: false
  }))
}

function conceptAtlasActionPersonas(action = {}, contextIndex) {
  const seen = new Set()

  return [
    action.userKeys,
    action.user_keys,
    action.personaKeys,
    action.persona_keys,
    action.primaryUserKeys,
    action.primary_user_keys,
    action.users,
    action.primaryUsers,
    action.primary_users,
    action.personas
  ].flatMap(conceptAtlasReferenceValues).flatMap(conceptAtlasReferenceVariants).flatMap((reference) => {
    const persona = contextIndex.personasByReference.get(reference)
    if (!persona || seen.has(persona.contextKey)) return []

    seen.add(persona.contextKey)
    return [ persona ]
  })
}

function conceptAtlasReferenceIndex(items = [], keys = []) {
  return items.reduce((index, item) => {
    keys.forEach((key) => {
      conceptAtlasReferenceVariants(item?.[key]).forEach((reference) => {
        if (!index.has(reference)) index.set(reference, item)
      })
    })

    return index
  }, new Map())
}

function conceptAtlasRelationshipLabel(relationship = {}) {
  return relationship.relationship || relationship.role || relationship.label || relationship.name || ""
}

function conceptAtlasPersonaSummary(persona = {}) {
  return persona.description || persona.role || persona.headlineQuote || persona.headline_quote || persona.primaryGoal || persona.primary_goal || ""
}

function conceptAtlasPersonaRouteKey(persona = {}, index = 0) {
  return persona?.key || conceptAtlasStableKey(persona?.name || persona?.label || `persona-${index}`)
}

function conceptAtlasActionRouteKey(action = {}, index = 0) {
  return action?.key || conceptAtlasStableKey(action?.label || action?.name || `action-${index}`)
}

function conceptAtlasSelectionFor(selection = null) {
  const type = selection?.type === "persona" || selection?.type === "action" ? selection.type : null
  const key = typeof selection?.key === "string" && selection.key.trim() ? selection.key.trim() : null
  if (!type || !key) return null

  return { type, key }
}

function conceptAtlasContextDetailPath(listPath, resource, key) {
  if (!listPath || !resource || !key) return null

  return `${listPath.replace(/\/$/, "")}/${resource}/${encodeURIComponent(key)}`
}

function conceptAtlasReferenceValues(value) {
  if (Array.isArray(value)) return value.flatMap(conceptAtlasReferenceValues)
  if (value && typeof value === "object") return [ value.key, value.id, value.name, value.label ].filter(Boolean)

  return String(value ?? "").split(/[\n,]/).map((item) => item.trim()).filter(Boolean)
}

function conceptAtlasReferenceVariants(value) {
  const key = conceptAtlasStableKey(value)
  if (!key) return []

  const variants = new Set([ key ])
  key.split("-").forEach((part) => variants.add(part))
  if (key.endsWith("ies") && key.length > 4) variants.add(`${key.slice(0, -3)}y`)
  if (key.endsWith("es") && key.length > 3) variants.add(key.slice(0, -2))
  if (key.endsWith("s") && key.length > 3) variants.add(key.slice(0, -1))

  return Array.from(variants).filter((variant) => variant.length > 2)
}

function conceptAtlasStableKey(value) {
  return String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
}

function conceptAtlasTypedKey(type, key) {
  return `${type}:${key}`
}

function conceptAtlasList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : []
}

export function ConceptCard({ className = "", concept, domain, focusContext, onNavigate }) {
  const commits = concept.commits || []
  const readyCommits = commits.filter((commit) => commit.status === "ready")
  const visual = conceptVisualFor(concept)
  const displayTitle = conceptDisplayTitle(concept)
  const detailPath = concept.detailPath
  const visualAvailable = conceptVisualAvailable(visual)
  const [expandedVisual, setExpandedVisual] = useState(false)
  const body = (
    <div className="concept-card-body">
      <div className="concept-card-heading">
        <h2>{displayTitle}</h2>
        <ConceptEvolution concept={concept} commits={commits} readyCommits={readyCommits} domain={domain} focusContext={focusContext} compact />
      </div>
      <ConceptFormerTitles concept={concept} />
      {concept.summary ? <p className="concept-summary">{concept.summary}</p> : null}
    </div>
  )

  function openExpandedVisual(event) {
    event.preventDefault()
    event.stopPropagation()
    setExpandedVisual(true)
  }

  function closeExpandedVisual() {
    setExpandedVisual(false)
  }

  return (
    <article className={[ "concept-card", className, detailPath ? "is-clickable" : null ].filter(Boolean).join(" ")}>
      {visualAvailable ? (
        <div className="concept-card-assets">
          <ConceptCardVisual
            concept={concept}
            detailPath={detailPath}
            onExpand={openExpandedVisual}
            onNavigate={onNavigate}
            title={displayTitle}
            visual={visual}
          />
        </div>
      ) : null}

      {detailPath ? (
        <a
          className="concept-card-link concept-card-body-link"
          href={detailPath}
          aria-label={`Open ${displayTitle} concept details`}
          onClick={(event) => onNavigate?.(concept, event)}
        >
          {body}
        </a>
      ) : body}

      <ConceptVisualModal
        concept={concept}
        domain={domain}
        focusContext={focusContext}
        onClose={closeExpandedVisual}
        open={expandedVisual}
        title={displayTitle}
        visual={visual}
      />
    </article>
  )
}

function ConceptCardVisual({ concept, detailPath, onExpand, onNavigate, title, visual }) {
  const content = <ConceptVisual visual={visual} title={title} />

  return (
    <div className="concept-card-media concept-card-media-expandable">
      {detailPath ? (
        <a
          className="concept-card-media-link"
          href={detailPath}
          aria-label={`Open ${title} concept details`}
          onClick={(event) => onNavigate?.(concept, event)}
        >
          {content}
        </a>
      ) : content}
      <button
        type="button"
        className="concept-card-visual-expand"
        aria-label={`Expand ${title} concept visual`}
        title="Expand visual"
        onClick={onExpand}
      >
        <Maximize2 aria-hidden="true" />
      </button>
    </div>
  )
}

function ConceptVisualModal({ concept, domain, focusContext, onClose, open, title, visual }) {
  const titleId = useId()
  const descriptionId = useId()
  const commits = concept?.commits || []
  const readyCommits = commits.filter((commit) => commit.status === "ready")
  const summary = concept?.summary || null

  useEffect(() => {
    if (!open) return undefined

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose?.()
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  function stopModalPropagation(event) {
    event.stopPropagation()
  }

  function handleBackdropClick(event) {
    event.stopPropagation()
    if (event.target === event.currentTarget) onClose?.()
  }

  const modal = (
    <div
      className="stack-modal-backdrop concept-visual-modal-backdrop"
      onClick={handleBackdropClick}
      onPointerDown={stopModalPropagation}
      onWheel={stopModalPropagation}
      role="presentation"
    >
      <div
        className="focus-card stack-selection-modal concept-visual-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={summary ? descriptionId : undefined}
      >
        <button type="button" className="concept-visual-modal-close" aria-label="Close concept visual preview" autoFocus onClick={onClose}>
          <X aria-hidden="true" />
        </button>
        <div className="concept-visual-modal-image">
          <ConceptVisual visual={visual} title={title} preferThumbnail={false} />
        </div>
        <div className="concept-visual-modal-details">
          <div className="concept-visual-modal-heading">
            <h2 id={titleId}>{title}</h2>
            <ConceptEvolution concept={concept} commits={commits} readyCommits={readyCommits} domain={domain} focusContext={focusContext} compact />
          </div>
          <ConceptFormerTitles concept={concept} />
          {summary ? <p id={descriptionId} className="concept-summary">{summary}</p> : null}
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

function ConceptDetail({
  className = "",
  concept,
  concepts = [],
  domain,
  focusContext,
  fullViewHref = null,
  fullViewLabel = null,
  onConceptNavigate = null,
  onOpenFullView = null,
  openIssues = []
}) {
  const commits = fullConceptCommits(concept)
  const readyCommits = concept?.atlasType === "persona"
    ? commits
    : commits.filter((commit) => commit.status === "ready")
  const visual = conceptVisualFor(concept)
  const displayTitle = conceptDisplayTitle(concept)
  const constellationStatusVisible = conceptConstellationStatusVisible(concept)
  const visualVisible = conceptVisualVisible(visual)
  const commitsHref = conceptCommitHistoryPath(concept)

  return (
    <article className={[ "concept-detail", className ].filter(Boolean).join(" ")}>
      <header className="concept-detail-hero is-stacked">
        <div className="concept-detail-hero-media">
          {visualVisible || constellationStatusVisible ? (
            <div className="concept-detail-media">
              {constellationStatusVisible ? (
                <div className={`concept-detail-assets ${visualVisible ? "has-visual" : ""}`.trim()}>
                  {visualVisible ? <ConceptVisual visual={visual} title={displayTitle} preferThumbnail={false} /> : null}
                  <ConceptGraphStatus concept={concept} />
                </div>
              ) : (
                <ConceptVisual visual={visual} title={displayTitle} preferThumbnail={false} />
              )}
            </div>
          ) : (
            <div className="detail-hero-placeholder" aria-hidden="true"><Image /></div>
          )}
          <div className="concept-detail-share-overlay">
            {fullViewHref ? (
              <a
                className="detail-share-button is-icon-only persona-full-view-link"
                href={fullViewHref}
                aria-label={fullViewLabel || `Open full ${displayTitle} page`}
                title={fullViewLabel || `Open full ${displayTitle} page`}
                onClick={onOpenFullView}
              >
                <SquareArrowOutUpRight aria-hidden="true" strokeWidth={1.8} />
              </a>
            ) : (
              <ConceptTitleMenu concept={concept} iconOnly />
            )}
          </div>
        </div>
        <div className="concept-detail-hero-copy">
          <ConceptDetailTitle title={displayTitle} summary={concept.summary} />
          <ConceptFormerTitles concept={concept} />
        </div>
      </header>

      <ConceptEvolution
        concept={concept}
        commits={commits}
        commitsHref={commitsHref}
        domain={domain}
        focusContext={focusContext}
        onConceptNavigate={onConceptNavigate}
        readyCommits={readyCommits}
        showNextHorizon
      />
      <ConceptIssues issues={openIssues} />
      <ConceptNextMoves concept={concept} />
    </article>
  )
}

function ConceptDetailTitle({ title, summary = null, action = null }) {
  return (
    <div className={`concept-detail-title ${summary ? "has-summary" : ""}`.trim()}>
      <div className="concept-detail-title-copy">
        <h2>{title}</h2>
        {summary ? <p className="concept-detail-title-summary">{summary}</p> : null}
      </div>
      {action ? <div className="concept-detail-title-action">{action}</div> : null}
    </div>
  )
}

export function conceptShareUrl(concept, { view = null } = {}) {
  const detailPath = concept?.detailPath
  if (!detailPath) return null
  const sharePath = view === "commits" ? conceptCommitHistoryPath(concept) : detailPath
  if (!sharePath) return null
  if (typeof window === "undefined") return sharePath

  try {
    return new URL(sharePath, window.location.origin).toString()
  } catch {
    return sharePath
  }
}

export function conceptCommitHistoryPath(concept) {
  const detailPath = concept?.detailPath
  if (!detailPath) return null

  const normalizedPath = String(detailPath).replace(/\/$/, "")
  const match = normalizedPath.match(/^(.*\/concept)\/([^/]+)$/)
  if (!match) return null

  return `${match[1]}/commits/${match[2]}`
}

function ConceptTitleMenu({ concept, iconOnly = false, view = null }) {
  const iconOnlyLabel = view === "commits" ? "Share commit history" : "Share concept"

  return (
    <DetailShareButton
      iconOnly={iconOnly}
      label={iconOnly ? iconOnlyLabel : "Share"}
      shareUrl={conceptShareUrl(concept, { view })}
    />
  )
}

export function conceptOpenIssues(concept, issues = []) {
  const issueList = Array.isArray(issues) ? issues : []
  const openIssueByKey = new Map(issueList.filter(isOpenInboxIssue).map((issue) => [String(issue?.key || ""), issue]))
  const knownIssueKeys = new Set(issueList.map((issue) => String(issue?.key || "")).filter(Boolean))
  const collected = new Map()

  conceptAtlasList(concept?.nextMoves).forEach((move) => {
    const issueKey = String(move?.inboxIssue?.key || move?.inboxIssueKey || move?.inbox_issue_key || "")
    if (!issueKey || collected.has(issueKey)) return

    const liveIssue = openIssueByKey.get(issueKey)
    if (liveIssue) {
      collected.set(issueKey, liveIssue)
      return
    }
    // The live issue list is the source of truth: a key it knows but did
    // not surface as open is closed. The embedded copy only bridges
    // payloads where the live list is missing the issue entirely.
    if (!knownIssueKeys.has(issueKey) && isOpenInboxIssue(move?.inboxIssue)) collected.set(issueKey, move.inboxIssue)
  })

  return orderedInboxIssues([...collected.values()])
}

function ConceptIssues({ issues = [] }) {
  return (
    <section className="concept-issues" aria-label="Open issues">
      <div className="concept-detail-section-heading">
        <h3>Issues</h3>
      </div>
      {issues.length === 0 ? (
        <p className="concept-issues-empty">No open issues touch this concept.</p>
      ) : (
        <div className="concept-issue-list" role="list">
          {issues.map((issue) => (
            <a className="detail-list-row concept-issue-item" href={issue.detailPath} key={issue.key} role="listitem">
              <span className="detail-list-thumb" aria-hidden="true">
                {issue.visual?.url ? <img src={issue.visual.url} alt="" loading="lazy" /> : <ShieldAlert strokeWidth={1.6} />}
              </span>
              <span className="detail-list-copy">
                <span className="detail-list-title">{issue.title}</span>
                <span className="detail-list-meta">
                  <span className={inboxIssueSeverityClassName(issue)} aria-hidden="true" />
                  {issue.severity ? `${issue.severity} · Issue` : "Issue"}
                </span>
              </span>
              <ChevronRight className="detail-list-chevron" aria-hidden="true" strokeWidth={1.8} />
            </a>
          ))}
        </div>
      )}
    </section>
  )
}

function conceptCardFocusTone(concept, commits, domain, focusContext) {
  return conceptFocusTone(fillQuietDays(buildContributionSeries(concept, commits), domain), focusContext)
}

function conceptDetailToneStyleForDate(concept, concepts = [], domain = null, fallbackTone) {
  const conceptKey = conceptItemKey(concept) || concept?.key
  const replayConcepts = Array.isArray(concepts) && concepts.length > 0 ? concepts : [concept]
  const fallbackStyle = conceptFocusStyle(fallbackTone)

  return (date) => {
    const replayTone = conceptReplayFocusToneColors(replayConcepts, date, { domain })[conceptKey]
    if (!replayTone) return fallbackStyle

    return {
      "--concept-ticker-line": replayTone.color || fallbackTone.lineColor,
      "--concept-ticker-area-start": replayTone.areaStartColor || fallbackTone.areaStartColor,
      "--concept-ticker-area-end": replayTone.areaEndColor || fallbackTone.areaEndColor,
      "--concept-ticker-glow": replayTone.glowColor || fallbackTone.glowColor
    }
  }
}

function conceptConstellationVisible(concept) {
  return Array.isArray(concept?.constellation?.facets) && concept.constellation.facets.length > 0
}

function conceptConstellationStatusVisible(concept) {
  if (conceptConstellationVisible(concept)) return false

  return ["pending", "generating", "failed"].includes(concept?.constellation?.status)
}

function ConceptGraphStatus({ concept, compact = false }) {
  const status = concept?.constellation?.status || "pending"
  const failed = status === "failed"
  const generating = status === "generating"
  const label = failed ? "Graph failed" : generating ? "Graph generating" : "Graph pending"
  const detail = failed
    ? "Retrying with fresh evidence"
    : generating
      ? "Mapping facets"
      : "Queued for facets"

  return (
    <div className={`concept-graph-status is-${status} ${compact ? "is-compact" : ""}`.trim()} role="status" aria-label={`${label}: ${detail}`}>
      {failed ? <AlertCircle aria-hidden="true" /> : generating ? <LoadingOrb state="shaping" /> : <Network aria-hidden="true" />}
      <span>{label}</span>
      <small>{detail}</small>
    </div>
  )
}

const NEXT_MOVE_KIND_LABELS = {
  fix: "Fix",
  improve: "Improve",
  expand: "Expand",
  harden: "Harden",
  other: "Open path"
}

function ConceptNextMoves({ concept }) {
  const moves = conceptNextMovesForDisplay(concept?.nextMoves, { limit: 5 })

  if (moves.length === 0) return null

  return (
    <section className="concept-next-moves" aria-label="Roadmap">
      <div className="concept-detail-section-heading">
        <h3>Roadmap</h3>
      </div>

      <div className="concept-next-move-grid">
        {moves.map((move, index) => (
          <ConceptNextMoveCard index={index} key={move.key || index} move={move} />
        ))}
      </div>
    </section>
  )
}

function conceptNextMovesForDisplay(moves = [], { limit = 5, recommendedOnly = false } = {}) {
  const items = Array.isArray(moves) ? moves.filter(roadmapMoveReady) : []
  const recommended = items.filter((move) => move?.recommended)
  const primary = recommended.length > 0 ? recommended : items.slice(0, 1)
  if (recommendedOnly) return primary.slice(0, 1)

  const primaryKeys = new Set(primary.map((move) => move?.key || move?.title).filter(Boolean))
  const alternatives = items.filter((move) => {
    const key = move?.key || move?.title
    return key ? !primaryKeys.has(key) : !primary.includes(move)
  })

  return [...primary, ...alternatives].slice(0, Math.max(1, Number(limit) || 1))
}

const ROADMAP_TONES = ["green", "blue", "violet"]
const ROADMAP_CHANGE_SIZES = ["S", "M", "L", "XL"]
const ROADMAP_ICONS = { fix: Wrench, improve: Sparkles, expand: GitBranch, harden: ShieldCheck, other: Waypoints }

function roadmapMoveReady(move) {
  const description = move?.rationale || move?.recommendationReason
  return Boolean(description && ROADMAP_CHANGE_SIZES.includes(move?.anticipatedSize))
}

function ConceptNextMoveCard({ index = 0, move }) {
  const issue = move.inboxIssue
  const kind = NEXT_MOVE_KIND_LABELS[move.kind] ? move.kind : "other"
  const tone = ROADMAP_TONES[index % ROADMAP_TONES.length]
  const className = `concept-next-move is-${tone}`
  const description = move.rationale || move.recommendationReason
  const anticipatedSize = move.anticipatedSize
  const Icon = ROADMAP_ICONS[kind]

  return (
    <details className={className}>
      <summary className="detail-list-row concept-next-move-summary">
        <span className="detail-list-thumb" aria-hidden="true"><Icon strokeWidth={1.6} /></span>
        <span className="detail-list-copy">
          <strong className="detail-list-title">{move.title}</strong>
          <span className="detail-list-meta">{NEXT_MOVE_KIND_LABELS[kind]}</span>
        </span>
        <ChevronRight className="detail-list-chevron concept-next-move-chevron" aria-hidden="true" strokeWidth={1.8} />
      </summary>
      <div className="concept-next-move-details">
        {description ? <p>{description}</p> : null}
        <div className="concept-next-move-size">
          <span>Anticipated change</span>
          <strong>{anticipatedSize}</strong>
        </div>
        {issue?.detailPath ? (
          <a className="concept-next-move-issue-link" href={issue.detailPath}>
            <span>View related issue</span>
            <ExternalLink aria-hidden="true" strokeWidth={1.8} />
          </a>
        ) : null}
      </div>
    </details>
  )
}

function ConceptFormerTitles({ concept }) {
  const formerTitles = conceptFormerTitles(concept)

  if (formerTitles.length === 0) return null

  return (
    <div className="concept-formerly">
      <span>Formerly</span>
      {" "}
      <strong>{formerTitles.join(", ")}</strong>
    </div>
  )
}

export function conceptFormerTitles(concept) {
  const candidates = Array.isArray(concept?.formerTitles) ? concept.formerTitles : []
  const seenTitles = [ conceptDisplayTitle(concept), concept?.title ].filter(Boolean)
  const formerTitles = []

  candidates.forEach((candidate) => {
    const title = candidate?.toString().trim()
    if (!title || seenTitles.some((seenTitle) => conceptTitlesEquivalent(seenTitle, title))) return

    seenTitles.push(title)
    if (formerTitles.length < 3) formerTitles.push(title)
  })

  return formerTitles
}

function conceptTitlesEquivalent(firstTitle, secondTitle) {
  const first = normalizedConceptTitle(firstTitle)
  const second = normalizedConceptTitle(secondTitle)
  if (!first || !second) return false

  return first === second
    || pluralizedConceptTitle(first) === second
    || pluralizedConceptTitle(second) === first
}

function normalizedConceptTitle(title) {
  return title?.toString().trim().replace(/\s+/g, " ").toLocaleLowerCase("en") || ""
}

function pluralizedConceptTitle(title) {
  return title.replace(/[a-z]+$/u, (word) => {
    if (/[^aeiou]y$/u.test(word)) return `${word.slice(0, -1)}ies`
    if (/(?:s|x|z|ch|sh)$/u.test(word)) return `${word}es`

    return `${word}s`
  })
}

function ConceptVisual({ visual, title, preferThumbnail = true }) {
  const firstFrame = visual?.image || visual?.frames?.[0]
  const imageUrl = conceptVisualImageUrl(firstFrame, { preferThumbnail })
  const status = visual?.status || "idle"
  const kindClass = visual?.kind ? `is-${conceptAtlasStableKey(visual.kind)}` : null
  const imageAlt = visual?.preserveAlt
    ? firstFrame?.alt || `${title} visual`
    : conceptVisualAlt(firstFrame?.alt, title)

  return (
    <div className={[ "concept-storyboard", `is-${status}`, kindClass ].filter(Boolean).join(" ")}>
      {imageUrl ? (
        <img src={imageUrl} alt={imageAlt} loading="lazy" />
      ) : (
        <div className={`concept-storyboard-placeholder is-${status}`}>
          {status === "generating" ? (
            <LoadingOrb size={64} state="shaping" />
          ) : status === "failed" ? (
            <AlertCircle aria-hidden="true" />
          ) : (
            <Image aria-hidden="true" />
          )}
          <span>{visualStatusLabel(status)}</span>
        </div>
      )}
    </div>
  )
}

function conceptVisualImageUrl(frame, { preferThumbnail = true } = {}) {
  if (!frame) return null

  return preferThumbnail
    ? frame.thumbnailUrl || frame.url || null
    : frame.url || frame.thumbnailUrl || null
}

function ConceptEvolution({ concept, commits, commitsHref = null, readyCommits, domain, focusContext, onConceptNavigate = null, showNextHorizon = false, compact = false }) {
  if (commits.length === 0) {
    if (compact) return null

    return (
      <div className="concept-evolution is-empty">
        <p>No matching commits yet.</p>
      </div>
    )
  }

  return (
    <div className={`concept-evolution ${compact ? "is-compact" : ""}`.trim()}>
      <ConceptTicker
        compact={compact}
        concept={concept}
        commits={commits}
        commitsHref={commitsHref}
        domain={domain}
        focusContext={focusContext}
        onConceptNavigate={onConceptNavigate}
        readyCommits={readyCommits}
        showNextHorizon={showNextHorizon}
      />
    </div>
  )
}

function ConceptTicker({ concept, commits, commitsHref = null, readyCommits, domain, focusContext, onConceptNavigate = null, showNextHorizon = false, compact = false }) {
  const series = buildContributionSeries(concept, commits)
  const sparkSeries = fillQuietDays(series, domain)
  const totalCount = concept?.commitCount || commits.length
  const trend = volumeTrend(series)
  const displayTitle = conceptDisplayTitle(concept)
  const focusTone = conceptFocusTone(sparkSeries, focusContext)
  const historyLinkLabel = commitsHref
    ? `View full commit history for ${displayTitle}: ${totalCount} ${pluralize("commit", totalCount)} across ${series.length} ${pluralize("day", series.length)}`
    : null

  return (
    <div
      className={`concept-contribution-wrap concept-ticker is-focus-${focusTone.key} ${commitsHref ? "has-history-link" : ""} ${compact ? "is-compact" : ""}`.trim()}
      data-focus-tone={focusTone.key}
      data-focus-label={focusTone.label}
      style={conceptFocusStyle(focusTone)}
      aria-label={commitsHref ? undefined : `${displayTitle} related commits by day`}
    >
      {commitsHref ? (
        <a
          className="concept-ticker-history-link"
          href={commitsHref}
          aria-label={historyLinkLabel}
          title="View full commit history"
          onClick={(event) => onConceptNavigate?.(concept, event, { view: "commits" })}
        />
      ) : null}
      {compact ? (
        <span className="sr-only">
          {totalCount} {pluralize("commit", totalCount)} across {series.length} {pluralize("day", series.length)}
        </span>
      ) : (
        <div className="concept-ticker-stats">
          <span className="sr-only">Concept focus: {focusTone.label}</span>
          <span className="concept-ticker-count">{totalCount} {pluralize("Commit", totalCount)}</span>
          {series.length > 0 ? (
            <span className="concept-ticker-meta">
              <span>{series.length} {pluralize("day", series.length)}</span>
              {trend ? <span className={`concept-ticker-delta is-${trend.direction}`}>{trend.label}</span> : null}
            </span>
          ) : null}
          {readyCommits.length !== commits.length ? (
            <span className="concept-ticker-progress">{readyCommits.length}/{commits.length} translated</span>
          ) : null}
        </div>
      )}

      {series.length > 0 ? (
        <ConceptSparkline
          series={sparkSeries}
          conceptKey={conceptItemKey(concept)}
          ghostMoves={showNextHorizon ? conceptNextMovesForDisplay(concept?.nextMoves, { limit: SPARK_MAX_GHOSTS, recommendedOnly: true }) : []}
          journeyWaypoints={showNextHorizon ? conceptJourneyWaypoints(concept) : []}
          showPoints={!compact}
        />
      ) : null}
    </div>
  )
}

function conceptJourneyWaypoints(concept) {
  return Array.isArray(concept?.journey?.waypoints) ? concept.journey.waypoints : []
}

function conceptDisplayTitle(concept) {
  return concept?.displayTitle || concept?.title || "Concept"
}

function ConceptSparkline({ series, conceptKey, ghostMoves = [], journeyWaypoints = [], showPoints = true }) {
  const hasGhosts = ghostMoves.length > 0
  const xScale = hasGhosts ? 1 - SPARK_FUTURE_FRACTION : 1
  const basePoints = sparklinePoints(series)
  const points = hasGhosts
    ? basePoints.map((point) => ({ ...point, x: point.x * xScale }))
    : basePoints
  const linePath = sparklinePath(points)
  const conceptSlug = sparklineSlug(conceptKey)
  const gradientId = `concept-spark-${conceptSlug}`
  const nowX = SPARK_W * xScale
  const ghostPoints = hasGhosts ? sparklineGhostPoints(ghostMoves, points, nowX) : []
  const waypointByDate = new Map(journeyWaypoints.map((waypoint) => [waypoint.date, waypoint]))

  return (
    <div className="concept-ticker-chart">
      <svg className="concept-ticker-svg" viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} preserveAspectRatio="none" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--concept-ticker-area-start)" />
            <stop offset="100%" stopColor="var(--concept-ticker-area-end)" />
          </linearGradient>
        </defs>
        <path className="concept-ticker-area" d={sparklineAreaPath(points, linePath)} fill={`url(#${gradientId})`} />
        <path className="concept-ticker-line" d={linePath} />
      </svg>

      {hasGhosts ? (
        <span className="concept-ticker-now-divider" style={{ left: `${nowX}%` }} aria-hidden="true"></span>
      ) : null}

      {showPoints ? series.map((day, index) => day.count > 0 ? (
        <ConceptTickerPoint
          day={day}
          key={day.date}
          point={points[index]}
          conceptSlug={conceptSlug}
          waypoint={waypointByDate.get(day.date)}
        />
      ) : null) : null}

      {ghostPoints.map((ghost) => (
        <span
          className={`concept-ticker-ghost-dot ${ghost.recommended ? "is-recommended" : ""}`.trim()}
          key={ghost.key}
          style={{ left: `${ghost.x}%`, top: `${(ghost.y / SPARK_H) * 100}%` }}
        >
          <span className="sr-only">Possible next: {ghost.title}</span>
        </span>
      ))}
    </div>
  )
}

function sparklineGhostPoints(ghostMoves, points, nowX) {
  const lastPoint = points[points.length - 1]
  const lastY = lastPoint ? lastPoint.y : SPARK_H / 2
  const span = SPARK_W - SPARK_PAD_X - nowX

  return ghostMoves.map((move, index) => {
    const fanDirection = index % 2 === 0 ? -1 : 1
    const fanStep = 3 + Math.floor(index / 2) * 4.5
    const y = Math.min(SPARK_H - SPARK_PAD_BOTTOM, Math.max(SPARK_PAD_TOP, lastY + fanDirection * fanStep))

    return {
      key: move.key || `ghost-${index}`,
      title: move.title || "Possible next move",
      recommended: Boolean(move.recommended),
      x: nowX + ((index + 1) / (ghostMoves.length + 1)) * span,
      y
    }
  })
}

function ConceptTickerPoint({ day, point, conceptSlug, waypoint = null }) {
  const [waypointOpen, setWaypointOpen] = useState(false)
  const label = `${day.count} ${day.count === 1 ? "commit" : "commits"} on ${day.date}`

  if (!waypoint) {
    return (
      <span
        className="concept-ticker-point"
        data-concept-sparkline-point={conceptSlug}
        style={{ left: `${point.x}%`, top: `${(point.y / SPARK_H) * 100}%` }}
      >
        <span className="concept-ticker-dot" aria-hidden="true"></span>
        <span className="sr-only">{label}</span>
      </span>
    )
  }

  return (
    <span
      className="concept-ticker-point has-waypoint"
      data-concept-sparkline-point={conceptSlug}
      style={{ left: `${point.x}%`, top: `${(point.y / SPARK_H) * 100}%` }}
      onMouseEnter={() => setWaypointOpen(true)}
      onMouseLeave={() => setWaypointOpen(false)}
    >
      <button
        type="button"
        className="concept-ticker-dot is-waypoint"
        aria-label={`${label}. See what happened next.`}
        onFocus={() => setWaypointOpen(true)}
        onBlur={() => setWaypointOpen(false)}
      ></button>
      {waypointOpen ? <ConceptWaypointPopover waypoint={waypoint} /> : null}
    </span>
  )
}

export function ConceptWaypointPopover({ waypoint }) {
  const options = Array.isArray(waypoint.options) ? waypoint.options : []
  const chosenOption = options.find((option) => option.key === waypoint.chosenKey)

  return (
    <span className="concept-ticker-waypoint-pop" role="status">
      <strong>What happened next</strong>
      <span className="concept-ticker-waypoint-options">
        {chosenOption ? (
          <span className="concept-ticker-waypoint-option is-chosen">
            <span className="concept-ticker-waypoint-title">{chosenOption.title}</span>
            {chosenOption.rationale ? <span className="concept-ticker-waypoint-why">{chosenOption.rationale}</span> : null}
          </span>
        ) : (
          <span className="concept-ticker-waypoint-option is-chosen">
            <span className="concept-ticker-waypoint-title">Somewhere new</span>
          </span>
        )}
      </span>
      {waypoint.actualSummary ? (
        <span className="concept-ticker-waypoint-shipped">What shipped: {waypoint.actualSummary}</span>
      ) : null}
    </span>
  )
}

function ConceptCommitPage({ concept, onClose = null, onNavigate = null, playControl = null }) {
  const displayTitle = conceptDisplayTitle(concept)

  return (
    <article className="concept-detail concept-commit-page">
      <div className="concept-commit-page-toolbar">
        <a
          className="concept-commit-page-back"
          href={concept.detailPath}
          onClick={(event) => onNavigate?.(concept, event)}
        >
          <ArrowLeft aria-hidden="true" strokeWidth={1.8} />
          <span>Back to concept</span>
        </a>

        <div className="concept-commit-page-actions" aria-label="Commit history controls">
          <ConceptTitleMenu concept={concept} iconOnly view="commits" />
          {onClose ? (
            <button
              type="button"
              className="concept-commit-page-close"
              aria-label="Close concept history"
              title="Close"
              onClick={onClose}
            >
              <X aria-hidden="true" strokeWidth={1.8} />
            </button>
          ) : null}
        </div>
      </div>

      <header className="concept-commit-page-header">
        <div>
          <p className="section-label">Commit history</p>
          <h2>{displayTitle}</h2>
        </div>
      </header>
      <ConceptCommitHistory concept={concept} playControl={playControl} />
    </article>
  )
}

function ConceptCommitHistory({ concept, playControl = null }) {
  const days = conceptHistoryDays(concept)
  const commitCount = concept?.commitCount || days.reduce((count, day) => count + day.commits.length, 0)

  if (days.length === 0) {
    return (
      <section className="concept-detail-history">
        <div className="concept-detail-section-heading">
          <h3>Full commit history</h3>
        </div>
        <div className="concept-evolution is-empty">
          <p>No matching commits yet.</p>
        </div>
      </section>
    )
  }

  return (
    <section className="concept-detail-history" aria-label="Full commit history">
      <div className="concept-detail-section-heading">
        <h3>Full commit history</h3>
        <span>{commitCount} {pluralize("commit", commitCount)}</span>
        {playControl ? (
          <button
            type="button"
            className={`concept-history-play ${playControl.playing ? "is-playing" : ""}`.trim()}
            aria-label={playControl.playing ? "Pause concept history playback" : "Play concept history"}
            aria-pressed={playControl.playing}
            disabled={!playControl.available}
            title={playControl.playing ? "Pause playback" : "Play this concept's history"}
            onClick={() => playControl.onToggle?.()}
          >
            {playControl.playing ? <Pause aria-hidden="true" strokeWidth={1.9} /> : <Play aria-hidden="true" strokeWidth={1.9} />}
          </button>
        ) : null}
      </div>

      <div className="concept-history-days">
        {days.map((day) => (
          <section className="concept-history-day" key={day.date}>
            <header className="concept-history-day-header">
              <h4>{formatContributionDate(day.date)}</h4>
              <span>{day.count || day.commits.length} {pluralize("commit", day.count || day.commits.length)}</span>
            </header>

            <div className="concept-history-commits" role="list">
              {day.commits.map((commit) => (
                <ConceptHistoryCommit commit={commit} key={commit.id || commit.sha || commit.shortSha} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  )
}

function ConceptHistoryCommit({ commit }) {
  const title = contributionCommitTitle(commit)
  const summary = contributionCommitSummary(commit)
  const meta = [
    commit.shortSha,
    contributionAuthorLabel(commit),
    commit.committedAtInWords,
    contributionStatusLabel(commit)
  ].filter(Boolean)
  const diffLabel = conceptCommitDiffLabel(commit)
  const content = (
    <>
      <span className="concept-history-commit-main">
        <strong>{title}</strong>
        {summary ? <span>{summary}</span> : null}
      </span>
      <span className="concept-history-commit-meta">
        {meta.length > 0 ? <small>{meta.join(" · ")}</small> : null}
        {diffLabel ? <small>{diffLabel}</small> : null}
      </span>
    </>
  )

  if (!commit.commitUrl) {
    return (
      <div className="concept-history-commit" role="listitem">
        {content}
      </div>
    )
  }

  return (
    <a
      className="concept-history-commit"
      href={commit.commitUrl}
      target="_blank"
      rel="noopener noreferrer"
      role="listitem"
      aria-label={`Open ${title} on GitHub`}
    >
      <span className="concept-history-commit-main">
        <strong>{title}</strong>
        {summary ? <span>{summary}</span> : null}
      </span>
      <span className="concept-history-commit-meta">
        {meta.length > 0 ? <small>{meta.join(" · ")}</small> : null}
        {diffLabel ? <small>{diffLabel}</small> : null}
      </span>
    </a>
  )
}

export function ConceptCommitDrawer({ commit, onClose }) {
  useEffect(() => {
    if (!commit) return undefined

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose?.()
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [commit, onClose])

  if (!commit) return null

  const title = contributionCommitTitle(commit)
  const summary = contributionCommitSummary(commit)
  const author = contributionAuthorLabel(commit)
  const statusLabel = contributionStatusLabel(commit)
  const committedDate = formatContributionDate(normalizeDateKey(commit.committedAt))
  const byline = [author, committedDate, commit.committedAtInWords].filter(Boolean)
  const fileCount = Number(commit.changedFileCount)
  const additions = Number(commit.additions)
  const deletions = Number(commit.deletions)
  const hasStats = fileCount > 0 || additions > 0 || deletions > 0
  const commitSubject = commit.commitSubject && commit.commitSubject !== title ? commit.commitSubject : null

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) onClose?.()
  }

  const drawer = (
    <div className="concept-commit-drawer-backdrop" onClick={handleBackdropClick} role="presentation">
      <aside className="concept-commit-drawer" role="dialog" aria-modal="true" aria-label={`Commit details: ${title}`}>
        <header className="concept-commit-drawer-top">
          {commit.shortSha ? <code>{commit.shortSha}</code> : null}
          {statusLabel ? <span className={`concept-commit-drawer-status is-${commit.status}`}>{statusLabel}</span> : null}
          <button type="button" className="concept-commit-drawer-close" aria-label="Close commit details" autoFocus onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>

        <div className="concept-commit-drawer-body">
          <h3>{title}</h3>
          {byline.length > 0 ? <p className="concept-commit-drawer-byline">{byline.join(" · ")}</p> : null}
          {summary ? <p className="concept-commit-drawer-summary">{summary}</p> : null}

          {hasStats ? (
            <dl className="concept-commit-drawer-stats">
              <div>
                <dt>Files</dt>
                <dd>{fileCount > 0 ? fileCount : 0}</dd>
              </div>
              <div>
                <dt>Added</dt>
                <dd className="is-added">+{additions > 0 ? additions : 0}</dd>
              </div>
              <div>
                <dt>Removed</dt>
                <dd className="is-removed">-{deletions > 0 ? deletions : 0}</dd>
              </div>
            </dl>
          ) : null}

          {commitSubject ? (
            <div className="concept-commit-drawer-section">
              <h4>Commit message</h4>
              <p className="concept-commit-drawer-subject">{commitSubject}</p>
            </div>
          ) : null}
        </div>

        {commit.commitUrl ? (
          <a className="concept-commit-drawer-link" href={commit.commitUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink aria-hidden="true" />
            <span>View on GitHub</span>
          </a>
        ) : null}
      </aside>
    </div>
  )

  if (typeof document === "undefined") return drawer

  return createPortal(drawer, document.body)
}

function ConceptSkeletonList() {
  return (
    <div className="concept-grid concept-skeleton-grid" aria-label="Loading repository concepts" aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((index) => <ConceptSkeletonCard key={index} index={index} />)}
    </div>
  )
}

function ConceptSkeletonCard({ index }) {
  return (
    <article className="concept-card is-skeleton" aria-hidden="true">
      <div className="concept-skeleton-media"></div>
      <div className="concept-card-body">
        <span className={`concept-skeleton-line concept-skeleton-title is-${index % 3}`}></span>
        <span className="concept-skeleton-line concept-skeleton-summary"></span>
        <span className="concept-skeleton-line concept-skeleton-summary is-short"></span>
        <div className="concept-skeleton-rail">
          {[0, 1, 2, 3].map((dot) => <span className="concept-skeleton-dot" key={dot}></span>)}
        </div>
      </div>
    </article>
  )
}

export function buildContributionSeries(concept, commits) {
  return Array.from(contributionBuckets(concept, commits).values())
    .sort((a, b) => a.date.localeCompare(b.date))
}

export function fillQuietDays(series, domain = null) {
  if (series.length === 0) return series

  let start = parseDateOnly(series[0].date)
  let end = parseDateOnly(series[series.length - 1].date)
  if (!start || !end) return series

  const domainStart = parseDateOnly(domain?.start)
  const domainEnd = parseDateOnly(domain?.end)
  if (domainStart && domainStart < start) start = domainStart
  if (domainEnd && domainEnd > end) end = domainEnd

  const byDate = new Map(series.map((day) => [ day.date, day ]))
  const filled = []

  for (let cursor = start; cursor <= end; cursor = addDays(cursor, 1)) {
    const date = dateToKey(cursor)
    filled.push(byDate.get(date) || { date, count: 0, codeVolume: 0, commits: [], remainingCount: 0 })
  }

  return filled
}

function contributionDomain(summary) {
  const start = summary?.firstCommittedAt || null
  const end = summary?.latestCommittedAt || null

  return start || end ? { start, end } : null
}

export function volumeTrend(series) {
  if (series.length === 0) return null

  const latestKey = series[series.length - 1].date
  const latest = parseDateOnly(latestKey)
  if (!latest) return null

  const currentStart = dateToKey(addDays(latest, -6))
  const priorStart = dateToKey(addDays(latest, -13))
  let currentVolume = 0
  let priorVolume = 0

  series.forEach((day) => {
    const volume = Number(day.codeVolume) || 0
    if (day.date >= currentStart && day.date <= latestKey) currentVolume += volume
    else if (day.date >= priorStart && day.date < currentStart) priorVolume += volume
  })

  if (priorVolume <= 0) return null

  const percent = ((currentVolume - priorVolume) / priorVolume) * 100
  const roundedPercent = Math.round(percent)
  const direction = roundedPercent > 0 ? "up" : roundedPercent < 0 ? "down" : "flat"
  const prefix = roundedPercent > 0 ? "+" : ""

  return {
    percent,
    direction,
    label: `${prefix}${roundedPercent}%`
  }
}

export function conceptFocusContext(concepts = [], domain = null) {
  const activityValues = concepts.flatMap((concept) => {
    const series = buildContributionSeries(concept, concept?.commits || [])

    return fillQuietDays(series, domain)
      .map((day) => conceptDayActivity(day))
      .filter((activity) => activity > 0)
  })

  return {
    significantActivityThreshold: percentile(activityValues, 0.2)
  }
}

export function conceptFocusTone(series, context = {}) {
  const signal = conceptFocusSignals(series, context)

  if (signal.totalActivity <= 0) {
    return conceptFocusToneForScore(0.16, "quiet", "quiet", signal)
  }

  const score = signal.focusScore

  if (score >= 0.68) return conceptFocusToneForScore(score, "focused", "focused", signal)
  if (score >= 0.46) return conceptFocusToneForScore(score, "active", "active", signal)
  if (score >= 0.22) return conceptFocusToneForScore(score, "cooling", "cooling", signal)

  return conceptFocusToneForScore(score, "abandoned", "abandoned", signal)
}

function conceptFocusSignals(series = [], context = {}) {
  const days = Array.isArray(series) ? series : []
  const measuredDays = days.map((day) => ({
    date: parseDateOnly(day?.date),
    activity: conceptDayActivity(day)
  }))
  const activeDays = measuredDays.filter(({ date, activity }) => date && activity > 0)
  const totalActivity = measuredDays.reduce((sum, day) => sum + day.activity, 0)

  if (days.length === 0 || activeDays.length === 0 || totalActivity <= 0) {
    return {
      totalActivity: 0,
      significantActivity: 0,
      significantActivityThreshold: 0,
      recencyScore: 0,
      activityRecencyScore: 0,
      focusScore: 0,
      daysSinceLastActivity: null,
      daysSinceLastSignificantActivity: null,
      projectHistoryDays: 0
    }
  }

  const firstDate = parseDateOnly(days[0]?.date) || activeDays[0].date
  const endDate = parseDateOnly(days[days.length - 1]?.date) || activeDays[activeDays.length - 1].date
  const projectHistoryDays = Math.max(1, daysBetween(firstDate, endDate) + 1)
  const activityValues = activeDays.map(({ activity }) => activity)
  const significantActivityThreshold = Number.isFinite(Number(context?.significantActivityThreshold))
    ? Number(context.significantActivityThreshold)
    : percentile(activityValues, 0.2)
  const significantDays = activeDays.filter(({ activity }) => significantActivity(activity, significantActivityThreshold))
  const lastActivityDate = activeDays[activeDays.length - 1].date
  const daysSinceLastActivity = Math.max(0, daysBetween(lastActivityDate, endDate))
  const activityRecencyScore = clamp01(1 - (daysSinceLastActivity / projectHistoryDays))

  if (significantDays.length === 0) {
    return {
      totalActivity,
      significantActivity: 0,
      significantActivityThreshold,
      recencyScore: 0,
      activityRecencyScore,
      focusScore: activityRecencyScore,
      daysSinceLastActivity,
      daysSinceLastSignificantActivity: null,
      projectHistoryDays
    }
  }

  const lastSignificantDate = significantDays[significantDays.length - 1].date
  const daysSinceLastSignificantActivity = Math.max(0, daysBetween(lastSignificantDate, endDate))
  const recencyScore = clamp01(1 - (daysSinceLastSignificantActivity / projectHistoryDays))

  return {
    totalActivity,
    significantActivity: significantDays.reduce((sum, { activity }) => sum + activity, 0),
    significantActivityThreshold,
    recencyScore,
    activityRecencyScore,
    focusScore: Math.max(recencyScore, activityRecencyScore),
    daysSinceLastActivity,
    daysSinceLastSignificantActivity,
    projectHistoryDays
  }
}

function conceptFocusToneForScore(score, key, label, signal) {
  const hue = key === "abandoned" ? 2 : conceptFocusHue(signal)
  const lineColor = `hsl(${hue}, 78%, 62%)`

  return {
    key,
    label,
    score,
    signal,
    lineColor,
    areaStartColor: `hsla(${hue}, 78%, 62%, 0.24)`,
    areaEndColor: `hsla(${hue}, 78%, 62%, 0)`,
    glowColor: `hsla(${hue}, 78%, 62%, 0.42)`
  }
}

function conceptFocusStyle(tone) {
  const lineColor = tone.lineColor || tone.color || "#70e58f"
  const glowColor = tone.glowColor || `color-mix(in oklab, ${lineColor} 42%, transparent)`

  return {
    "--concept-ticker-line": lineColor,
    "--concept-ticker-area-start": tone.areaStartColor || `color-mix(in oklab, ${lineColor} 24%, transparent)`,
    "--concept-ticker-area-end": tone.areaEndColor || "transparent",
    "--concept-ticker-glow": glowColor
  }
}

function conceptFocusHue(signal = {}) {
  const score = clamp01(signal?.focusScore)

  if (signal?.totalActivity <= 0) return 28
  if (score < 0.68) return Math.round(lerp(2, 34, score / 0.68))

  return Math.round(lerp(96, 139, (score - 0.68) / 0.32))
}

function conceptDayActivity(day) {
  const codeVolume = Number(day?.codeVolume) || 0
  if (codeVolume > 0) return codeVolume

  return Number(day?.count) || 0
}

function daysBetween(start, end) {
  const dayMs = 24 * 60 * 60 * 1000
  return Math.round((end.getTime() - start.getTime()) / dayMs)
}

function clamp01(value) {
  return Math.max(0, Math.min(1, Number(value) || 0))
}

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, Number(value) || 0))
}

function lerp(start, end, amount) {
  return start + (end - start) * amount
}

function percentile(values, rank) {
  const sorted = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b)

  if (sorted.length === 0) return 0
  if (sorted.length === 1) return sorted[0]

  const index = (sorted.length - 1) * clamp01(rank)
  const lower = Math.floor(index)
  const upper = Math.ceil(index)

  if (lower === upper) return sorted[lower]

  return lerp(sorted[lower], sorted[upper], index - lower)
}

function significantActivity(activity, threshold) {
  return activity > 0 && activity + 1e-9 >= threshold
}

const SPARK_W = 100
const SPARK_H = 40
const SPARK_PAD_X = 3
const SPARK_PAD_TOP = 4
const SPARK_PAD_BOTTOM = 4
const SPARK_FUTURE_FRACTION = 0.16
const SPARK_MAX_GHOSTS = 5
const SPARK_MIN_AMPLITUDE = 0.04

function sparklinePoints(series) {
  const scaled = series.map((day) => Math.sqrt(Math.max(0, Number(day.codeVolume) || 0)))
  const maxScaled = Math.max(...scaled)
  const innerW = SPARK_W - SPARK_PAD_X * 2
  const innerH = SPARK_H - SPARK_PAD_TOP - SPARK_PAD_BOTTOM

  return series.map((day, index) => {
    const active = Number(day.count) > 0
    const norm = maxScaled === 0 ? 0.5 : active ? Math.max(scaled[index] / maxScaled, SPARK_MIN_AMPLITUDE) : 0

    return {
      x: series.length === 1 ? SPARK_W / 2 : SPARK_PAD_X + (index / (series.length - 1)) * innerW,
      y: series.length === 1 ? SPARK_H / 2 : SPARK_PAD_TOP + (1 - norm) * innerH
    }
  })
}

function sparklinePath(points) {
  if (points.length === 0) return ""
  if (points.length === 1) {
    return `M ${spark(SPARK_PAD_X)} ${spark(points[0].y)} L ${spark(SPARK_W - SPARK_PAD_X)} ${spark(points[0].y)}`
  }

  const segments = [`M ${spark(points[0].x)} ${spark(points[0].y)}`]

  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[index - 1] || points[index]
    const p1 = points[index]
    const p2 = points[index + 1]
    const p3 = points[index + 2] || p2

    segments.push(`C ${spark(p1.x + (p2.x - p0.x) / 6)} ${spark(clampSparkY(p1.y + (p2.y - p0.y) / 6))} ${spark(p2.x - (p3.x - p1.x) / 6)} ${spark(clampSparkY(p2.y - (p3.y - p1.y) / 6))} ${spark(p2.x)} ${spark(p2.y)}`)
  }

  return segments.join(" ")
}

function sparklineAreaPath(points, linePath) {
  if (!linePath) return ""

  const startX = points.length === 1 ? SPARK_PAD_X : points[0].x
  const endX = points.length === 1 ? SPARK_W - SPARK_PAD_X : points[points.length - 1].x

  return `${linePath} L ${spark(endX)} ${SPARK_H} L ${spark(startX)} ${SPARK_H} Z`
}

function clampSparkY(value) {
  return Math.min(SPARK_H - 0.5, Math.max(0.5, value))
}

function spark(value) {
  return Math.round(value * 100) / 100
}

function sparklineSlug(value) {
  return String(value || "concept").toLowerCase().replace(/[^a-z0-9_-]+/g, "-")
}

function contributionBuckets(concept, commits) {
  const buckets = new Map()
  const contributionDays = Array.isArray(concept?.contributionDays) ? concept.contributionDays : []

  if (contributionDays.length > 0) {
    contributionDays.forEach((day) => {
      const date = normalizeDateKey(day.date)
      if (!date) return

      buckets.set(date, {
        date,
        count: day.count || day.commits?.length || 0,
        codeVolume: Number(day.codeVolume) || commitVolume(day.commits),
        commits: day.commits || [],
        remainingCount: day.remainingCount || 0
      })
    })
    return buckets
  }

  commits.forEach((commit) => {
    const date = normalizeDateKey(commit.committedAt)
    if (!date) return

    const bucket = buckets.get(date) || { date, count: 0, codeVolume: 0, commits: [], remainingCount: 0 }
    bucket.count += 1
    bucket.codeVolume += commitVolume([commit])
    bucket.commits.push(commit)
    buckets.set(date, bucket)
  })

  return buckets
}

function conceptHistoryDays(concept) {
  const contributionDays = Array.isArray(concept?.contributionDays) ? concept.contributionDays : []
  const days = contributionDays.length > 0
    ? contributionDays.map((day) => ({
      date: normalizeDateKey(day.date),
      count: Number(day.count) || day.commits?.length || 0,
      commits: uniqueCommits(day.commits || [])
        .sort((a, b) => commitTime(b) - commitTime(a))
    })).filter((day) => day.date && day.commits.length > 0)
    : buildContributionSeries(concept, concept?.commits || []).map((day) => ({
      date: day.date,
      count: Number(day.count) || day.commits.length,
      commits: uniqueCommits(day.commits || [])
        .sort((a, b) => commitTime(b) - commitTime(a))
    })).filter((day) => day.date && day.commits.length > 0)

  return days.sort((a, b) => b.date.localeCompare(a.date))
}

function fullConceptCommits(concept) {
  return conceptHistoryDays(concept).flatMap((day) => day.commits)
}

function conceptRootCommit(concept, commits = null) {
  const explicit = normalizeRootCommit(concept?.rootCommit || concept?.root_commit)
  if (explicit) return explicit

  const candidates = Array.isArray(commits) ? commits : fullConceptCommits(concept)
  return uniqueCommits(candidates)
    .map(normalizeRootCommit)
    .filter(Boolean)
    .sort((left, right) => commitTime(left) - commitTime(right))[0] || null
}

function normalizeRootCommit(commit) {
  if (!commit) return null

  const committedAt = commit.committedAt || commit.committed_at || commit.date || null
  if (!committedAt) return null

  return {
    ...commit,
    committedAt,
    shortSha: commit.shortSha || commit.short_sha || null
  }
}

function uniqueCommits(commits = []) {
  const seen = new Set()

  return commits.filter((commit) => {
    const key = commitItemKey(commit)
    if (!key) return true
    if (seen.has(key)) return false

    seen.add(key)
    return true
  })
}

function commitTime(commit) {
  if (!commit?.committedAt) return 0
  const time = new Date(commit.committedAt).getTime()

  return Number.isNaN(time) ? 0 : time
}

function commitVolume(commits) {
  if (!Array.isArray(commits)) return 0

  return commits.reduce((sum, commit) => sum + (Number(commit.additions) || 0) + (Number(commit.deletions) || 0), 0)
}

function contributionStatusLabel(commit) {
  if (commit.status === "failed") return "failed"
  if (commit.status && commit.status !== "ready") return "reading"

  return null
}

function contributionCommitTitle(commit) {
  if (commit.status === "failed") return "Could not translate this change"
  if (commit.status && commit.status !== "ready") return "Reading this change"

  return commit.evolutionTitle || commit.title || commit.commitSubject || "Commit"
}

function contributionCommitSummary(commit) {
  if (commit.status === "failed") return CONCEPT_ITEM_ERROR_COPY
  if (commit.status && commit.status !== "ready") return "Codex is translating this commit into product language."

  return commit.summary
}

function contributionAuthorLabel(commit) {
  const authorNames = Array.isArray(commit.authorNames) ? commit.authorNames.filter(Boolean) : []
  if (authorNames.length > 0) return authorNames.join(", ")

  const coAuthorNames = Array.isArray(commit.coAuthorNames) ? commit.coAuthorNames.filter(Boolean) : []
  return [commit.authorName, ...coAuthorNames].filter(Boolean).join(", ")
}

function conceptCommitDiffLabel(commit) {
  const parts = []
  if (Number(commit?.changedFileCount) > 0) parts.push(`${commit.changedFileCount} ${pluralize("file", Number(commit.changedFileCount))}`)
  if (Number(commit?.additions) > 0) parts.push(`+${commit.additions}`)
  if (Number(commit?.deletions) > 0) parts.push(`-${commit.deletions}`)

  return parts.join(" ")
}

function formatContributionDate(date) {
  const parsed = parseDateOnly(date)
  if (!parsed) return date

  return parsed.toLocaleDateString("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  })
}

function normalizeDateKey(value) {
  if (!value) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.toString())) return value.toString()

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null

  return dateToKey(parsed)
}

function parseDateOnly(value) {
  if (!value) return null
  const match = value.toString().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
}

function dateToKey(date) {
  return date.toISOString().slice(0, 10)
}

function addDays(date, amount) {
  const next = new Date(date.getTime())
  next.setUTCDate(next.getUTCDate() + amount)
  return next
}

function visualStatusLabel(status) {
  if (status === "ready") return "Concept visual ready"
  if (status === "generating") return "Generating concept visual"
  if (status === "failed") return "Concept visual failed"

  return "Concept visual pending"
}

function conceptVisualFor(concept) {
  return concept?.visual || null
}

function conceptVisualVisible(visual) {
  if (visual?.image || visual?.frames?.[0]) return true

  return activeVisualStatuses.has(visual?.status) || visual?.status === "failed"
}

function conceptVisualAvailable(visual) {
  return Boolean(visual?.image || visual?.frames?.[0])
}

function conceptVisualAlt(alt, title) {
  const fallback = `${title} concept visual`
  if (!alt) return fallback

  return alt.toString()
    .replace(/\bstoryboard frame\s*\d*\b/gi, "concept visual")
    .replace(/\bstoryboard\b/gi, "concept visual")
}

export function conceptIconsFor(concepts = []) {
  const usedIcons = new Set()

  return new Map(concepts.map((concept, index) => {
    const Icon = conceptIconFor(concept, usedIcons, index)
    usedIcons.add(Icon)

    return [conceptIconAssignmentKey(concept, index), Icon]
  }))
}

function conceptIconAssignmentKey(concept, index) {
  return `${conceptItemKey(concept) || "concept"}:${index}`
}

function conceptIconFor(concept, usedIcons = null, fallbackOffset = 0) {
  const candidates = conceptIconCandidatesFor(concept)
  const unmatchedIcon = candidates.find((Icon) => !usedIcons?.has(Icon))

  if (unmatchedIcon) return unmatchedIcon
  if (candidates[0] && !usedIcons) return candidates[0]

  return conceptIconFallback(usedIcons, fallbackOffset)
}

function conceptIconCandidatesFor(concept) {
  const titleText = conceptDisplayTitle(concept).toLowerCase()
  const searchText = conceptIconSearchText(concept)

  return CONCEPT_ICON_RULES.map((rule, index) => {
    const matches = rule.terms.filter((term) => conceptIconTermMatches(searchText, term))
    if (matches.length === 0) return null

    const bestTerm = matches.sort((first, second) => second.length - first.length)[0]
    const titleMatch = conceptIconTermMatches(titleText, bestTerm)
    const score = (titleMatch ? 1000 : 0) + (bestTerm.length * 10) - index - (rule.generic ? 100 : 0)

    return { Icon: rule.Icon, score }
  })
    .filter(Boolean)
    .sort((first, second) => second.score - first.score)
    .reduce((icons, match) => {
      if (!icons.includes(match.Icon)) icons.push(match.Icon)
      return icons
    }, [])
}

function conceptIconSearchText(concept) {
  return [
    concept?.title,
    concept?.displayTitle,
    concept?.summary,
    concept?.key,
    concept?.terms,
    concept?.aliases,
    concept?.formerTitles
  ].flat().filter(Boolean).join(" ").toLowerCase()
}

function conceptIconTermMatches(searchText, term) {
  return searchText.includes(term)
}

function conceptIconFallback(usedIcons, fallbackOffset) {
  if (usedIcons) {
    const unusedFallback = CONCEPT_ICON_FALLBACKS.find((Icon) => !usedIcons.has(Icon))
    if (unusedFallback) return unusedFallback
  }

  return CONCEPT_ICON_FALLBACKS[fallbackOffset % CONCEPT_ICON_FALLBACKS.length] || Lightbulb
}
